// Externalized from SharedScripts.razor (TKR-10273) so this ~60 KB interop bundle is browser-cached
// instead of inlined into every page's HTML and re-compressed on every request.

   // IN-APP HISTORY DEPTH — what the SocialFi topbar's back arrow is allowed to appear for.
   //
   // history.length is not the question. It counts every entry in the tab, including the ones that belong to
   // whoever sent the visitor here, so a shared /post/{id} link opened straight from a search result looked
   // like a page with somewhere to go back to and the arrow walked the visitor off the site.
   //
   // So each history entry carries its own depth, stamped into history.state: 0 for the entry the visitor
   // arrived on, one more for each page they then opened from inside the app. Depth > 0 is the answer to "is
   // the entry behind this one one of ours". Stamping the ENTRY rather than counting navigations is what makes
   // back and forward read correctly with no extra bookkeeping - the browser hands the value back with the
   // entry - and history.state survives a reload and a bfcache restore.
   //
   // Blazor keeps its own keys in that same state object (userState / _index in blazor.web.js, where _index is
   // how it replays a navigation past a NavigationLock), so the stamp is added to a COPY of whatever is there
   // and never replaces it.
   const tokeroHistoryDepthStateKey = 'tkHistoryDepth';
   const tokeroHistoryEntrySessionKey = 'tokero.history.entry';

   // All three per-document, so all three are null exactly once per real page load. That is the signal that
   // separates a same-document Blazor navigation - enhanced nav or NavigateTo, no page load, and the visitor
   // was already inside the app - from arriving at a document from somewhere else, where the referrer decides.
   let tokeroLastEntryDepth = null;
   let tokeroLastEntryUrl = null;
   let tokeroLastEntryHistoryLength = null;

   const tokeroReadStampedHistoryDepth = () => {
     const historyState = window.history.state;
     if (!historyState || typeof historyState !== 'object') {
       return null;
     }
     const stampedDepth = historyState[tokeroHistoryDepthStateKey];
     if (typeof stampedDepth !== 'number') {
       return null;
     }
     return stampedDepth;
   };

   const tokeroStampHistoryDepth = (depth) => {
     try {
       const existingState = window.history.state;
       let stampedState = {};
       if (existingState && typeof existingState === 'object') {
         stampedState = Object.assign({}, existingState);
       }
       stampedState[tokeroHistoryDepthStateKey] = depth;
       window.history.replaceState(stampedState, '');
     } catch (err) {
       // Safari rate-limits replaceState (100 per 30s). The in-memory depth below still carries the answer for
       // the rest of this document; only a later back/forward onto this entry loses it.
       console.error('tokeroStampHistoryDepth failed', err);
     }
   };

   // sessionStorage is per-tab and survives full page loads, which is how the entry the visitor came FROM is
   // still known after a navigation that replaced the document. history.length rides along with the depth
   // because it is what tells a push apart from a replace across that boundary.
   const tokeroReadSessionHistoryEntry = () => {
     try {
       const storedEntry = window.sessionStorage.getItem(tokeroHistoryEntrySessionKey);
       if (!storedEntry) {
         return null;
       }
       const parsedEntry = JSON.parse(storedEntry);
       if (typeof parsedEntry?.depth !== 'number' || typeof parsedEntry?.length !== 'number') {
         return null;
       }
       return parsedEntry;
     } catch (err) {
       // Storage blocked, or a value written by an older build. Treated as unknown, which lands the visitor on
       // the referrer branch below.
       return null;
     }
   };

   const tokeroWriteSessionHistoryEntry = (depth, historyLength) => {
     try {
       const entryToStore = { depth: depth, length: historyLength };
       window.sessionStorage.setItem(tokeroHistoryEntrySessionKey, JSON.stringify(entryToStore));
     } catch (err) {
       // Storage blocked (private mode, third-party context). The referrer branch below still resolves the
       // entry the visitor arrived on, which is the case that matters.
     }
   };

   // Deliberately same-ORIGIN, not "some Tokero host": community apps run on their own subdomain and on
   // customer custom domains, and the browser cannot be asked which of those are ours. A walk from the main
   // site into a community app therefore starts that app at depth 0 - the app has its own navigation, and an
   // arrow that jumps the visitor to a different host is the behaviour being removed here, not kept.
   const tokeroHasSameOriginReferrer = () => {
     const referrer = document.referrer;
     if (!referrer) {
       return false;
     }
     try {
       const referrerOrigin = new URL(referrer).origin;
       return referrerOrigin === window.location.origin;
     } catch (err) {
       return false;
     }
   };

   // A REPLACED entry is the trap this has to handle: NavigateTo(replace: true) and location.replace() swap the
   // current entry instead of adding one - the Marketplace category filters and the CmsPage canonical rewrite
   // both do it - so the visitor is one URL further along with nothing new behind them. history.length is what
   // separates the two: a push moves it, a replace does not.
   //
   // The one case it reads wrong is a push that lands on top of forward entries it truncates, where the length
   // can come out unchanged and the depth stops one short. That direction is the safe one: it can only hide an
   // arrow that would have worked, never offer one that walks off the site.
   const tokeroResolveCurrentHistoryDepth = () => {
     const currentUrl = window.location.href;
     const currentHistoryLength = window.history.length;

     const stampedDepth = tokeroReadStampedHistoryDepth();
     if (stampedDepth !== null) {
       tokeroLastEntryDepth = stampedDepth;
       tokeroLastEntryUrl = currentUrl;
       tokeroLastEntryHistoryLength = currentHistoryLength;
       tokeroWriteSessionHistoryEntry(stampedDepth, currentHistoryLength);
       return stampedDepth;
     }

     // Unstamped, but the same URL this document last stamped: the stamp was dropped by a framework
     // replaceState (Blazor writes state: null on an enhanced navigation), so this is the same entry rather
     // than a new one. Put the depth it already had back instead of counting the page a second time.
     if (tokeroLastEntryDepth !== null && tokeroLastEntryUrl === currentUrl) {
       tokeroStampHistoryDepth(tokeroLastEntryDepth);
       return tokeroLastEntryDepth;
     }

     let resolvedDepth = 0;
     if (tokeroLastEntryDepth !== null) {
       // Same document, different URL: a navigation the visitor made from inside the app.
       resolvedDepth = tokeroLastEntryDepth;
       const pushedRatherThanReplaced = currentHistoryLength !== tokeroLastEntryHistoryLength;
       if (pushedRatherThanReplaced) {
         resolvedDepth = tokeroLastEntryDepth + 1;
       }
     } else {
       // First resolve in this document, so the visitor got here by a real page navigation. Only a same-origin
       // referrer proves the entry behind them is one of ours - a search result, an ad, a shared link and a
       // typed URL all leave them with nothing of ours to go back to.
       const arrivedFromOurSite = tokeroHasSameOriginReferrer();
       if (arrivedFromOurSite) {
         // No stored entry (first page of the tab after a cache flush, or storage blocked) still means one of
         // our pages is behind this one, since the referrer says so.
         resolvedDepth = 1;
         const previousEntry = tokeroReadSessionHistoryEntry();
         if (previousEntry !== null) {
           resolvedDepth = previousEntry.depth;
           const pushedRatherThanReplaced = currentHistoryLength !== previousEntry.length;
           if (pushedRatherThanReplaced) {
             resolvedDepth = previousEntry.depth + 1;
           }
         }
       }
     }

     tokeroStampHistoryDepth(resolvedDepth);
     tokeroLastEntryDepth = resolvedDepth;
     tokeroLastEntryUrl = currentUrl;
     tokeroLastEntryHistoryLength = currentHistoryLength;
     tokeroWriteSessionHistoryEntry(resolvedDepth, currentHistoryLength);
     return resolvedDepth;
   };

   // Resolves the current entry as a side effect, so C# calling it once per navigation is also what keeps the
   // stamps up to date.
   window.tokeroHasInAppHistory = function () {
     try {
       const currentDepth = tokeroResolveCurrentHistoryDepth();
       if (currentDepth < 1) {
         return false;
       }
       // A tab with a single entry has nothing behind it whatever the depth says. Chrome copies sessionStorage
       // into a tab opened with target="_blank", so an in-app link opened in a new tab arrives with a
       // same-origin referrer AND a stored depth, and this is what stops it claiming a back entry it lacks.
       return window.history.length > 1;
     } catch (err) {
       console.error('tokeroHasInAppHistory failed', err);
       // The coarse version of the same question, so a failure degrades instead of hiding a working arrow.
       return tokeroHasSameOriginReferrer();
     }
   };

   window.tokeroGoBack = function (fallbackUrl) {
     // Re-asked at click time rather than trusted from render time: the control may have been painted before
     // the visitor navigated, and this is the guard that keeps a stale arrow from leaving the site at all.
     const hasInAppHistory = window.tokeroHasInAppHistory();
     if (hasInAppHistory) {
       window.history.back();
       return;
     }
     if (fallbackUrl) {
       window.location.assign(fallbackUrl);
     }
   };

   window.getUtcOffset = () => {
     try{
       const offsetInMinutes = new Date().getTimezoneOffset();
       return -offsetInMinutes;
     } catch (err) {
       console.error("getUtcOffset failed", err);
       return 0;
     }
   };

   window.getUserTimeZone = function () {
     return Intl.DateTimeFormat().resolvedOptions().timeZone;
   };

   // The three browser facts the layout refines its header-derived values with on every circuit's first render, in
   // ONE interop call. Each used to be its own round trip, and with Azure SignalR a round trip is a few hundred
   // milliseconds, paid in series before the layout could re-render its children with the refined zone. The three
   // individual functions stay for their other callers.
   window.getBrowserEnvironment = function () {
     return {
       utcOffset: window.getUtcOffset(),
       timeZone: window.getUserTimeZone(),
       isMobileApp: window.isMobileApp()
     };
   };

   window.getUserTime = function () {
     return new Date().toLocaleString();
   };

   const waitForVideoEvent = (element, eventName, timeoutMs) => {
     return new Promise((resolve) => {
       let settled = false;
       const finish = (didFire) => {
         if (settled) {
           return;
         }
         settled = true;
         element.removeEventListener(eventName, onEvent);
         element.removeEventListener('error', onError);
         clearTimeout(timer);
         resolve(didFire);
       };
       const onEvent = () => finish(true);
       const onError = () => finish(false);
       element.addEventListener(eventName, onEvent);
       element.addEventListener('error', onError);
       const timer = setTimeout(() => finish(false), timeoutMs);
     });
   };

   // Sized and opaque on purpose. WebKit treats a 1px, opacity:0 video as not worth decoding, so it would load
   // metadata and never produce a frame — drawImage then painted nothing and the filmstrip came out white. It is
   // kept off-viewport instead of hidden, and asks for real media data rather than just metadata.
   const createOffscreenVideo = (objectUrl) => {
     const video = document.createElement('video');
     video.preload = 'auto';
     video.muted = true;
     video.defaultMuted = true;
     video.playsInline = true;
     video.setAttribute('playsinline', '');
     video.setAttribute('webkit-playsinline', '');
     video.setAttribute('muted', '');
     video.style.position = 'fixed';
     video.style.left = '-10000px';
     video.style.top = '0';
     video.style.width = '160px';
     video.style.height = '90px';
     video.style.pointerEvents = 'none';
     document.body.appendChild(video);
     video.src = objectUrl;
     try { video.load(); } catch (e) { }
     const dispose = () => {
       try { video.removeAttribute('src'); video.load(); } catch (e) { }
       try { document.body.removeChild(video); } catch (e) { }
     };
     return { video: video, dispose: dispose };
   };

   const VIDEO_FRAME_MAX_EDGE_PIXELS = 720;
   const VIDEO_FRAME_JPEG_QUALITY = 0.75;

   const encodeVideoFrameToJpeg = (video) => {
     if (!video || !video.videoWidth || !video.videoHeight) {
       return '';
     }

     let scale = 1;
     if (video.videoWidth >= video.videoHeight && video.videoWidth > VIDEO_FRAME_MAX_EDGE_PIXELS) {
       scale = VIDEO_FRAME_MAX_EDGE_PIXELS / video.videoWidth;
     }
     if (video.videoHeight > video.videoWidth && video.videoHeight > VIDEO_FRAME_MAX_EDGE_PIXELS) {
       scale = VIDEO_FRAME_MAX_EDGE_PIXELS / video.videoHeight;
     }

     const width = Math.max(1, Math.round(video.videoWidth * scale));
     const height = Math.max(1, Math.round(video.videoHeight * scale));
     const canvas = document.createElement('canvas');
     canvas.width = width;
     canvas.height = height;
     const ctx = canvas.getContext('2d');
     ctx.drawImage(video, 0, 0, width, height);
     return canvas.toDataURL('image/jpeg', VIDEO_FRAME_JPEG_QUALITY);
   };

   const readVideoDurationSeconds = async (video) => {
     if (isFinite(video.duration) && video.duration > 0) {
       return video.duration;
     }

     try {
       video.currentTime = 1e7;
       await waitForVideoEvent(video, 'durationchange', 3000);
       video.currentTime = 0;
     } catch (e) {
       return 0;
     }

     if (isFinite(video.duration) && video.duration > 0) {
       return video.duration;
     }
     return 0;
   };

   // Reads the selected video file straight from a DOM <input type=file> (Blazor InputFile renders one)
   window.getVideoMetaAndPoster = async (inputId, previewVideoElementId) => {
     const input = document.getElementById(inputId);
     if (!input || !input.files || input.files.length === 0) {
       return null;
     }

     const file = input.files[0];
     let video = null;
     let disposeVideo = null;

     const preview = document.getElementById(previewVideoElementId);
     if (preview && preview.src) {
       video = preview;
     }
     if (video === null) {
       const objectUrl = URL.createObjectURL(file);
       const offscreen = createOffscreenVideo(objectUrl);
       video = offscreen.video;
       disposeVideo = () => { offscreen.dispose(); URL.revokeObjectURL(objectUrl); };
     }

     try {
       if (video.readyState < 1) {
         await waitForVideoEvent(video, 'loadedmetadata', 15000);
       }

       const durationSeconds = await readVideoDurationSeconds(video);
       if (durationSeconds <= 0) {
         console.error('getVideoMetaAndPoster: duration unavailable');
         return null;
       }

       let posterDataUrl = '';
       try {
         if (video.readyState < 2) {
           await waitForVideoEvent(video, 'loadeddata', 8000);
         }

         let seekTarget = 3;
         if (durationSeconds < 3) {
           seekTarget = 0.1;
           if (durationSeconds < 0.1) {
             seekTarget = 0;
           }
         }
         video.currentTime = seekTarget;
         await waitForVideoEvent(video, 'seeked', 3000);

         posterDataUrl = encodeVideoFrameToJpeg(video);
       } catch (posterErr) {
         console.error('poster capture failed', posterErr);
         posterDataUrl = '';
       }

       // Dimensions come off the element once metadata loads; the per-platform blockers check the clip against them.
       return {
         durationSeconds: durationSeconds,
         sizeBytes: file.size,
         posterDataUrl: posterDataUrl,
         widthPixels: video.videoWidth,
         heightPixels: video.videoHeight
       };
     } catch (err) {
       console.error('getVideoMetaAndPoster failed', err);
       return null;
     } finally {
       if (disposeVideo !== null) {
         disposeVideo();
       }
     }
   };

   // Reads the frame size and a cover shot straight off a STASHED clip. getVideoMetaAndPoster above does the same for
   // a file still sitting in an <input>; a clip the browser produced itself - a refit - never passes through one, so
   // there is no input left to read it from. Duration is read too, but only to place the cover seek, as that one does.
   window.getStashedVideoMeta = async (token) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return null;
     }

     const objectUrl = URL.createObjectURL(file);
     const offscreen = createOffscreenVideo(objectUrl);
     const video = offscreen.video;

     try {
       if (video.readyState < 1) {
         await waitForVideoEvent(video, 'loadedmetadata', 15000);
       }

       const durationSeconds = await readVideoDurationSeconds(video);

       let posterDataUrl = '';
       try {
         if (video.readyState < 2) {
           await waitForVideoEvent(video, 'loadeddata', 8000);
         }

         let seekTarget = 3;
         if (durationSeconds < 3) {
           seekTarget = 0.1;
           if (durationSeconds < 0.1) {
             seekTarget = 0;
           }
         }
         video.currentTime = seekTarget;
         await waitForVideoEvent(video, 'seeked', 3000);

         posterDataUrl = encodeVideoFrameToJpeg(video);
       } catch (posterErr) {
         console.error('getStashedVideoMeta: poster capture failed', posterErr);
         posterDataUrl = '';
       }

       return {
         posterDataUrl: posterDataUrl,
         widthPixels: video.videoWidth,
         heightPixels: video.videoHeight,
         durationSeconds: durationSeconds
       };
     } catch (err) {
       console.error('getStashedVideoMeta failed', err);
       return null;
     } finally {
       offscreen.dispose();
       URL.revokeObjectURL(objectUrl);
     }
   };

   // Points a <video> element at an object URL of the file chosen in the given InputFile so the user can
   // play back exactly what they picked. The object URL is stashed on the element dataset so
   // revokeVideoPreview can release it later and we don't leak a blob per re-pick.
   window.setVideoPreviewFromInput = (inputId, videoElementId) => {
     const input = document.getElementById(inputId);
     const video = document.getElementById(videoElementId);
     if (!input || !input.files || input.files.length === 0 || !video) {
       return;
     }

     if (video.dataset.objectUrl) {
       URL.revokeObjectURL(video.dataset.objectUrl);
     }

     const objectUrl = URL.createObjectURL(input.files[0]);
     video.dataset.objectUrl = objectUrl;
     video.src = objectUrl;
     video.load();
   };

   // Snapshots whatever frame the preview <video> is currently showing into a JPEG data URL, so the user
   // can scrub the native controls to the frame they want and set it as the post's cover (poster). The
   // preview is fed from a same-origin object URL of the picked file, so the canvas is not tainted and
   // toDataURL succeeds. Returns '' when the element is missing or the frame can't be captured.
   window.captureVideoFrame = (videoElementId) => {
     const video = document.getElementById(videoElementId);
     if (!video || !video.videoWidth || !video.videoHeight) {
       return '';
     }

     try {
       return encodeVideoFrameToJpeg(video);
     } catch (err) {
       console.error('captureVideoFrame failed', err);
       return '';
     }
   };

   // Builds an Instagram-style trim + cover scrubber under the video. The strip is filled with a row of
   // evenly-spaced thumbnails (decoded offscreen from the picked file so the visible preview is not disturbed)
   // and three draggable handles: a left and right TRIM handle that mark the kept [start, end] window (the
   // regions outside it are dimmed), and a COVER marker constrained to sit inside that window. Pointer drag
   // (mouse or touch) grabs the nearest handle, maps x to a timestamp, and seeks the visible preview so the
   // frame shows over the video. On release: dragging a trim edge reports the new range to .NET
   // (OnTrimRangeChanged) so it recomputes the effective duration/size and the over-limit gate; dragging the
   // cover captures that frame and hands it back (OnCoverFrameSelected). Idempotent: re-calling rebuilds.
   window.initCoverScrubber = async (videoElementId, stripElementId, inputId, dotNetRef) => {
     const strip = document.getElementById(stripElementId);
     const video = document.getElementById(videoElementId);
     const input = document.getElementById(inputId);
     if (!strip || !video || !input || !input.files || input.files.length === 0) {
       return;
     }

     // Tear down any scrubber previously wired on this element before rebuilding for the new clip.
     if (strip._coverCleanup) {
       try { strip._coverCleanup(); } catch (e) { }
     }
     strip.innerHTML = '';

     const track = document.createElement('div');
     track.className = 'post-media-filmstrip-track';
     // Dim overlays paint the trimmed-out regions (before start / after end).
     const dimLeft = document.createElement('div');
     dimLeft.className = 'post-media-filmstrip-dim';
     const dimRight = document.createElement('div');
     dimRight.className = 'post-media-filmstrip-dim';
     // Trim edges + cover marker. Cover keeps the original handle class so its styling is unchanged.
     const trimLeft = document.createElement('div');
     trimLeft.className = 'post-media-filmstrip-trim post-media-filmstrip-trim-left';
     const trimRight = document.createElement('div');
     trimRight.className = 'post-media-filmstrip-trim post-media-filmstrip-trim-right';
     const handle = document.createElement('div');
     handle.className = 'post-media-filmstrip-handle';
     strip.appendChild(track);
     strip.appendChild(dimLeft);
     strip.appendChild(dimRight);
     strip.appendChild(trimLeft);
     strip.appendChild(trimRight);
     strip.appendChild(handle);

     // Thumbnails: decode the picked file in an offscreen video and snapshot a handful of evenly-spaced
     // frames. A per-seek timeout keeps a stubborn browser from hanging the build; on failure the bar still
     // works as a plain scrub track (just without the filmstrip preview).
     const objectUrl = URL.createObjectURL(input.files[0]);
     const offscreen = createOffscreenVideo(objectUrl);
     const off = offscreen.video;
     const frameCount = 8;
     let frameSource = off;
     let isUsingPreview = false;
     let previewResumeTime = 0;
     try {
       await waitForVideoEvent(off, 'loadedmetadata', 15000);
       if (off.readyState < 2) {
         await waitForVideoEvent(off, 'loadeddata', 8000);
       }

       // readyState < 2 means no frame has been decoded, so drawImage would paint nothing — the white strip.
       // Mobile WebKit does this routinely for a video it considers off-screen. The on-screen preview is the one
       // element the browser is definitely decoding, so borrow it and put its playhead back afterwards.
       if (off.readyState < 2 || off.videoWidth === 0) {
         frameSource = video;
         isUsingPreview = true;
         if (video.readyState < 1) {
           await waitForVideoEvent(video, 'loadedmetadata', 15000);
         }
         if (video.readyState < 2) {
           await waitForVideoEvent(video, 'loadeddata', 10000);
         }
         previewResumeTime = video.currentTime;
       }

       if (frameSource.readyState < 2 || frameSource.videoWidth === 0) {
         throw new Error('filmstrip: no decodable frame source');
       }

       let stripDuration = 0;
       if (isFinite(frameSource.duration) && frameSource.duration > 0) {
         stripDuration = frameSource.duration;
       }

       const cellCanvas = document.createElement('canvas');
       const cellWidth = 80;
       let aspect = 0.5625;
       if (frameSource.videoWidth && frameSource.videoHeight) {
         aspect = frameSource.videoHeight / frameSource.videoWidth;
       }
       cellCanvas.width = cellWidth;
       cellCanvas.height = Math.round(cellWidth * aspect);
       const cellCtx = cellCanvas.getContext('2d');

       for (let i = 0; i < frameCount; i++) {
         let seekTime = 0;
         if (stripDuration > 0) {
           seekTime = stripDuration * (i + 0.5) / frameCount;
         }
         frameSource.currentTime = seekTime;
         // A seek that merely runs slow on a busy phone must not cost the whole strip, so a miss skips its own
         // cell and the build carries on.
         await waitForVideoEvent(frameSource, 'seeked', 6000);
         if (frameSource.readyState < 2 || frameSource.videoWidth === 0) {
           continue;
         }
         cellCtx.drawImage(frameSource, 0, 0, cellCanvas.width, cellCanvas.height);
         const cell = document.createElement('img');
         cell.src = cellCanvas.toDataURL('image/jpeg', 0.6);
         cell.className = 'post-media-filmstrip-cell';
         cell.draggable = false;
         track.appendChild(cell);
       }
     } catch (err) {
       console.error('filmstrip build failed', err);
     } finally {
       if (isUsingPreview) {
         try { video.currentTime = previewResumeTime; } catch (e) { }
       }
       offscreen.dispose();
       URL.revokeObjectURL(objectUrl);
     }

     // Total clip duration drives all ratio<->seconds maths for both the trim range and the cover frame.
     if (video.readyState < 1) {
       await waitForVideoEvent(video, 'loadedmetadata', 15000);
     }
     const clipDuration = await readVideoDurationSeconds(video);

     // Trim range + cover position, all kept as 0..1 ratios along the strip. Defaults: the full clip is
     // selected, cover parked at the representative 3s frame (clamped into the clip for very short videos).
     let startRatio = 0;
     let endRatio = 1;
     let coverRatio = 0;
     if (clipDuration > 0) {
       let coverSeconds = 3;
       if (coverSeconds > clipDuration) {
         coverSeconds = clipDuration;
       }
       coverRatio = coverSeconds / clipDuration;
     }

     // Smallest selectable window so the two trim handles can never cross or produce a zero-length clip: one
     // second where the duration is known, otherwise a small fixed fraction of the bar.
     let minGapRatio = 0.02;
     if (clipDuration > 0) {
       minGapRatio = 1 / clipDuration;
       if (minGapRatio > 0.5) {
         minGapRatio = 0.5;
       }
     }

     // Map a pointer x to a clamped 0..1 ratio along the strip.
     const ratioFromClientX = (clientX) => {
       const rect = strip.getBoundingClientRect();
       let ratio = 0;
       if (rect.width > 0) {
         ratio = (clientX - rect.left) / rect.width;
       }
       if (ratio < 0) { ratio = 0; }
       if (ratio > 1) { ratio = 1; }
       return ratio;
     };

     // Push the current ratios onto the DOM: handle/marker positions and the dimmed out-of-range regions.
     const placeHandle = (element, ratio) => {
       const stripWidth = strip.clientWidth;
       if (stripWidth <= 0) {
         element.style.left = (ratio * 100) + '%';
         return;
       }

       const halfWidth = (element.offsetWidth / 2) + 2;
       let x = ratio * stripWidth;
       if (x < halfWidth) {
         x = halfWidth;
       }
       if (x > stripWidth - halfWidth) {
         x = stripWidth - halfWidth;
       }
       element.style.left = x + 'px';
     };

     const renderPositions = () => {
       placeHandle(trimLeft, startRatio);
       placeHandle(trimRight, endRatio);
       placeHandle(handle, coverRatio);
       dimLeft.style.left = '0%';
       dimLeft.style.width = (startRatio * 100) + '%';
       dimRight.style.left = (endRatio * 100) + '%';
       dimRight.style.width = ((1 - endRatio) * 100) + '%';
     };

     // Seek the visible preview to a ratio so the frame at that position shows over the video.
     const seekToRatio = (ratio) => {
       if (clipDuration > 0) {
         video.currentTime = ratio * clipDuration;
       }
     };

     // Snapshot the frame now showing in the preview and hand it to .NET as the chosen cover, together with its
     // position on the original timeline (seconds). .NET keeps the frame as the thumbnail and the timestamp so a
     // platform that sets its cover by time (e.g. TikTok's video_cover_timestamp_ms) can match the picked frame.
     const captureAndCommitCover = () => {
       const dataUrl = window.captureVideoFrame(videoElementId);
       if (dataUrl && dotNetRef) {
         dotNetRef.invokeMethodAsync('OnCoverFrameSelected', dataUrl, coverRatio * clipDuration);
       }
     };

     // Report the current trim range (in seconds) to .NET so it recomputes the effective duration / size and
     // the over-limit save gate. Called on release of either trim handle.
     const commitTrimRange = () => {
       if (dotNetRef) {
         dotNetRef.invokeMethodAsync('OnTrimRangeChanged', startRatio * clipDuration, endRatio * clipDuration);
       }
     };

     // Decide which of the three handles a pointer press grabs: the nearest of left-trim, right-trim, or the
     // cover marker. Trim edges win ties so the range stays adjustable even when the cover sits on an edge.
     const pickHandle = (ratio) => {
       const distanceToLeft = Math.abs(ratio - startRatio);
       const distanceToRight = Math.abs(ratio - endRatio);
       const distanceToCover = Math.abs(ratio - coverRatio);
       let target = 'cover';
       let best = distanceToCover;
       if (distanceToLeft <= best) {
         target = 'left';
         best = distanceToLeft;
       }
       if (distanceToRight <= best) {
         target = 'right';
         best = distanceToRight;
       }
       return target;
     };

     let dragging = false;
     let dragTarget = 'cover';

     // Apply a drag to whichever handle was grabbed, keeping the invariants (cover stays inside [start, end],
     // the two trim edges keep at least minGapRatio between them).
     const applyDrag = (ratio) => {
       if (dragTarget === 'left') {
         let maxStart = endRatio - minGapRatio;
         if (ratio > maxStart) { ratio = maxStart; }
         if (ratio < 0) { ratio = 0; }
         startRatio = ratio;
         if (coverRatio < startRatio) {
           coverRatio = startRatio;
         }
         renderPositions();
         seekToRatio(startRatio);
         return;
       }
       if (dragTarget === 'right') {
         let minEnd = startRatio + minGapRatio;
         if (ratio < minEnd) { ratio = minEnd; }
         if (ratio > 1) { ratio = 1; }
         endRatio = ratio;
         if (coverRatio > endRatio) {
           coverRatio = endRatio;
         }
         renderPositions();
         seekToRatio(endRatio);
         return;
       }
       // Cover marker: constrained to stay inside the selected window.
       if (ratio < startRatio) { ratio = startRatio; }
       if (ratio > endRatio) { ratio = endRatio; }
       coverRatio = ratio;
       renderPositions();
       seekToRatio(coverRatio);
     };

     const onDown = (e) => {
       dragging = true;
       const ratio = ratioFromClientX(e.clientX);
       dragTarget = pickHandle(ratio);
       if (e.pointerId != null && strip.setPointerCapture) {
         try { strip.setPointerCapture(e.pointerId); } catch (err) { }
       }
       applyDrag(ratio);
       e.preventDefault();
     };
     const onMove = (e) => {
       if (!dragging) {
         return;
       }
       applyDrag(ratioFromClientX(e.clientX));
     };
     const onUp = (e) => {
       if (!dragging) {
         return;
       }
       dragging = false;
       applyDrag(ratioFromClientX(e.clientX));
       // Trimming an edge re-reports the range (and may have shoved the cover, so re-capture too); scrubbing
       // the cover re-captures once the final seek settles. The timeout is the fallback for browsers that skip
       let coverFallbackTimer = null;
       let isCoverCaptured = false;
       const captureCoverOnce = () => {
         if (isCoverCaptured) {
           return;
         }
         isCoverCaptured = true;
         video.onseeked = null;
         clearTimeout(coverFallbackTimer);
         captureAndCommitCover();
       };
       video.onseeked = captureCoverOnce;
       coverFallbackTimer = setTimeout(captureCoverOnce, 300);
       if (dragTarget === 'left' || dragTarget === 'right') {
         commitTrimRange();
       }
     };

     strip.addEventListener('pointerdown', onDown);
     strip.addEventListener('pointermove', onMove);
     strip.addEventListener('pointerup', onUp);
     strip.addEventListener('pointercancel', onUp);

     // On mobile a drag on the LEFT trim handle starts at ratio 0 — i.e. in the screen's edge zone — where the
     // OS claims it as the interactive swipe-back gesture and navigates away mid-cut. touch-action:none on the
     // strip stops scrolling but not that system gesture, so also consume the touch sequence directly: a
     // non-passive touchmove that preventDefaults keeps the slide on the strip (left/right along the filmstrip)
     // instead of the browser back-navigation. Same pattern the photo cropper uses; we preventDefault only on
     // touchmove (never touchstart — that cancels the pointer stream on iOS and would kill the drag).
     const swallowTouchMove = (e) => { e.preventDefault(); };
     strip.addEventListener('touchmove', swallowTouchMove, { passive: false });

     const onStripResize = () => {
       renderPositions();
     };
     window.addEventListener('resize', onStripResize);
     window.addEventListener('orientationchange', onStripResize);

     strip._coverCleanup = () => {
       strip.removeEventListener('pointerdown', onDown);
       strip.removeEventListener('pointermove', onMove);
       strip.removeEventListener('pointerup', onUp);
       strip.removeEventListener('pointercancel', onUp);
       strip.removeEventListener('touchmove', swallowTouchMove);
       window.removeEventListener('resize', onStripResize);
       window.removeEventListener('orientationchange', onStripResize);
       strip._coverCleanup = null;
     };

     // Paint the initial full-range selection with the cover parked at its default 3s frame.
     renderPositions();
   };

   // Removes the scrubber's pointer listeners and clears the strip element (mode switch / re-pick / dispose).
   window.disposeCoverScrubber = (stripElementId) => {
     const strip = document.getElementById(stripElementId);
     if (!strip) {
       return;
     }
     if (strip._coverCleanup) {
       try { strip._coverCleanup(); } catch (e) { }
     }
     strip.innerHTML = '';
   };

   // Releases the preview object URL and (when an inputId is given) clears the file input so the same
   // file can be re-picked and a mode switch leaves no stale selection behind.
   window.revokeVideoPreview = (videoElementId, inputId) => {
     const video = document.getElementById(videoElementId);
     if (video && video.dataset.objectUrl) {
       URL.revokeObjectURL(video.dataset.objectUrl);
       video.dataset.objectUrl = '';
       video.removeAttribute('src');
       video.load();
     }
     if (inputId) {
       const input = document.getElementById(inputId);
       try { if (input) { input.value = ''; } } catch (e) { }
     }
   };

   // SocialFi video upload goes browser -> HTTP endpoint -> blob storage, NOT over the SignalR circuit. The
   // picked File is stashed here when the attach dialog commits (so the reference survives the dialog closing,
   // which removes its <input>), then POSTed directly to the upload endpoint when the composer creates the
   // post. Keyed by an opaque token handed back to .NET; the File object is a lightweight handle, not a copy.
   window.__pendingVideoUploads = window.__pendingVideoUploads || {};
   let __pendingVideoSeq = 0;

   window.stashSelectedVideo = (inputId) => {
     const input = document.getElementById(inputId);
     if (!input || !input.files || input.files.length === 0) {
       return '';
     }
     __pendingVideoSeq = __pendingVideoSeq + 1;
     const token = 'vid_' + __pendingVideoSeq;
     window.__pendingVideoUploads[token] = input.files[0];
     return token;
   };

   window.discardStashedVideo = (token) => {
     if (token && window.__pendingVideoUploads[token]) {
       delete window.__pendingVideoUploads[token];
     }
   };

   // Copies a stash entry under a NEW token, so an edit made for one destination (a refit into the frame that one
   // platform demands) cannot reach the clip the other destinations are still pointing at. The bytes are not copied:
   // both tokens reference the same Blob until one of them is replaced.
   window.cloneStashedVideo = (token) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return '';
     }
     __pendingVideoSeq = __pendingVideoSeq + 1;
     const clonedToken = 'vid_' + __pendingVideoSeq;
     window.__pendingVideoUploads[clonedToken] = file;
     return clonedToken;
   };

   // Initializes TikTok preview video controls: progress tracking, click-to-seek, drag-to-seek, and pause/play state.
   // The progress dot only appears when the video is paused or being dragged. Called once per <video> element, so a
   // second call reaches the same progress bar with a new clip.
   window.tkpInitVideoControls = (videoEl, progressFillEl, progressBarEl, muted) => {
     if (!videoEl || !progressFillEl || !progressBarEl) return;

     // Blazor sets the muted/autoplay attributes after the element is created, so the live muted property is still
     // false and browsers block (or voice) the unmuted autoplay. Set the property for real - to the preview's own
     // mute state, which its volume button toggles - then start playback.
     videoEl.muted = muted;
     videoEl.play().catch(() => {});

     let seeking = false;

     const updateFill = () => {
       if (!document.body.contains(videoEl)) return;
       if (!seeking && videoEl.duration > 0) {
         progressFillEl.style.width = (videoEl.currentTime / videoEl.duration * 100) + '%';
       }
       requestAnimationFrame(updateFill);
     };
     requestAnimationFrame(updateFill);

     const seekToX = (clientX) => {
       const rect = progressBarEl.getBoundingClientRect();
       let ratio = (clientX - rect.left) / rect.width;
       if (ratio < 0) ratio = 0;
       if (ratio > 1) ratio = 1;
       videoEl.currentTime = ratio * videoEl.duration;
       progressFillEl.style.width = (ratio * 100) + '%';
     };

     // The bar outlives the clip, so the previous clip's handler is dropped first; left attached, it would keep
     // seeking the detached element it was wired to.
     if (progressBarEl._tkpSeekHandler) {
       progressBarEl.removeEventListener('mousedown', progressBarEl._tkpSeekHandler);
     }
     progressBarEl._tkpSeekHandler = (e) => {
       if (!videoEl.paused) {
         videoEl.pause();
         progressBarEl.classList.add('tkp-paused');
       }
       seeking = true;
       progressBarEl.classList.add('tkp-seeking');
       seekToX(e.clientX);

       const onMove = (ev) => seekToX(ev.clientX);
       const onUp = () => {
         seeking = false;
         progressBarEl.classList.remove('tkp-seeking');
         document.removeEventListener('mousemove', onMove);
         document.removeEventListener('mouseup', onUp);
       };
       document.addEventListener('mousemove', onMove);
       document.addEventListener('mouseup', onUp);
     };
     progressBarEl.addEventListener('mousedown', progressBarEl._tkpSeekHandler);
   };

   // Toggles play/pause on the TikTok preview video and syncs the progress bar paused state.
   window.tkpTogglePlayPause = (videoEl, progressBarEl) => {
     if (!videoEl || !progressBarEl) return;
     if (videoEl.paused) {
       videoEl.play();
       progressBarEl.classList.remove('tkp-paused');
     } else {
       videoEl.pause();
       progressBarEl.classList.add('tkp-paused');
     }
   };

   const VIDEO_UPLOAD_PROBE_ANCHOR_MS = 2000;
   const VIDEO_UPLOAD_SPEED_PROBE_MS = 5000;
   const VIDEO_UPLOAD_SLOW_BYTES_PER_SECOND = 128 * 1024;
   const VIDEO_UPLOAD_SLOW_PROJECTED_SECONDS = 300;
   const VIDEO_UPLOAD_STALL_MS = 20000;
   const VIDEO_UPLOAD_PROJECTION_MARGIN_MS = 30000;
   const VIDEO_UPLOAD_RESPONSE_WAIT_MS = 120000;
   const VIDEO_UPLOAD_FLOOR_BYTES_PER_SECOND = 2 * 1024;
   const VIDEO_UPLOAD_MAX_DRAIN_MS = 600000;

   const VIDEO_UPLOAD_SINGLE_SHOT_MAX_BYTES = 25 * 1024 * 1024;

   const VIDEO_UPLOAD_TARGET_SLICE_SECONDS = 25;
   const VIDEO_UPLOAD_MIN_SLICE_BYTES = 256 * 1024;
   const VIDEO_UPLOAD_MAX_SLICE_BYTES = 1 * 1024 * 1024;
   const VIDEO_UPLOAD_DEFAULT_SLICE_BYTES = 1 * 1024 * 1024;

   const videoUploadSlicedMediaIds = new Set();

   let isVideoJobCancelled = false;

   const uploadWholeFile = (file, baseUrl, mediaId, reportProgress, deadlineAt) => {
     return new Promise((resolve) => {
       const xhr = new XMLHttpRequest();
       xhr.open('POST', baseUrl, true);
       xhr.setRequestHeader('X-Media-Id', mediaId);
       xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

       const startedAt = performance.now();
       let lastProgressAt = startedAt;
       let loadedBytes = 0;
       let settled = false;

       let anchorAt = startedAt;
       let anchorBytes = 0;
       let isAnchorSet = false;
       const measureBytesPerSecond = () => {
         const elapsedMs = performance.now() - anchorAt;
         if (elapsedMs <= 0) {
           return 0;
         }
         const bytesPerSecond = ((loadedBytes - anchorBytes) * 1000) / elapsedMs;
         return bytesPerSecond;
       };
       const settle = (verdict) => {
         if (settled) {
           return;
         }
         settled = true;
         clearInterval(watchdogTimer);
         resolve({ verdict: verdict, bytesPerSecond: measureBytesPerSecond() });
       };

       const watchdogTimer = setInterval(() => {
         const now = performance.now();
         if (isVideoJobCancelled) {
           settle('cancelled');
           xhr.abort();
           return;
         }
         if (deadlineAt > 0 && now > deadlineAt) {
           settle('too-slow');
           xhr.abort();
           return;
         }
         if (now - lastProgressAt > VIDEO_UPLOAD_STALL_MS) {
           settle('slow');
           xhr.abort();
           return;
         }

         const elapsedSinceStartMs = now - startedAt;
         if (elapsedSinceStartMs < VIDEO_UPLOAD_PROBE_ANCHOR_MS) {
           return;
         }
         if (!isAnchorSet) {
           isAnchorSet = true;
           anchorAt = now;
           anchorBytes = loadedBytes;
           return;
         }
         if (now - anchorAt < VIDEO_UPLOAD_SPEED_PROBE_MS) {
           return;
         }

         const bytesPerSecond = measureBytesPerSecond();

         // Projected finish vs the optimise cut-off: bail to the transcode now instead of burning the full window.
         const remainingBytes = file.size - loadedBytes;
         const projectedRemainingMs = (remainingBytes / Math.max(1, bytesPerSecond)) * 1000;
         const projectedFinishAt = now + projectedRemainingMs;
         const willClearlyMissDeadline = deadlineAt > 0
           && projectedFinishAt > deadlineAt + VIDEO_UPLOAD_PROJECTION_MARGIN_MS;
         if (willClearlyMissDeadline) {
           settle('too-slow');
           xhr.abort();
           return;
         }

         const projectedSeconds = file.size / Math.max(1, bytesPerSecond);
         const isSlowLink = bytesPerSecond < VIDEO_UPLOAD_SLOW_BYTES_PER_SECOND
           || projectedSeconds > VIDEO_UPLOAD_SLOW_PROJECTED_SECONDS;
         if (isSlowLink) {
           settle('slow');
           xhr.abort();
         }
       }, 1000);

       xhr.upload.onprogress = (e) => {
         if (!e.lengthComputable) {
           return;
         }
         loadedBytes = e.loaded;
         lastProgressAt = performance.now();
         reportProgress(e.loaded);
       };
       xhr.onload = () => {
         if (xhr.status >= 200 && xhr.status < 300) {
           settle('ok');
           return;
         }
         if (xhr.status === 401 || xhr.status === 403) {
           settle('rejected');
           return;
         }
         settle('slow');
       };
       xhr.onerror = () => settle('slow');
       xhr.ontimeout = () => settle('slow');
       xhr.onabort = () => settle('slow');
       xhr.send(file);
     });
   };

   const chooseSliceBytes = (bytesPerSecond) => {
     if (!(bytesPerSecond > 0)) {
       return VIDEO_UPLOAD_DEFAULT_SLICE_BYTES;
     }

     let sliceBytes = Math.floor(bytesPerSecond * VIDEO_UPLOAD_TARGET_SLICE_SECONDS);
     if (sliceBytes < VIDEO_UPLOAD_MIN_SLICE_BYTES) {
       sliceBytes = VIDEO_UPLOAD_MIN_SLICE_BYTES;
     }
     if (sliceBytes > VIDEO_UPLOAD_MAX_SLICE_BYTES) {
       sliceBytes = VIDEO_UPLOAD_MAX_SLICE_BYTES;
     }
     return sliceBytes;
   };

   const uploadInSlices = async (file, baseUrl, mediaId, reportProgress, measuredBytesPerSecond, deadlineAt, skipResume) => {
     const totalSize = file.size;
     const result = { committed: false, tooSlow: false, cancelled: false };

     const retryDelays = [0, 3000, 5000, 10000, 20000];
     const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

     let chunkSize = chooseSliceBytes(measuredBytesPerSecond);

     let staged = [];
     let isAlreadyCommitted = false;
     for (let attempt = 0; attempt < retryDelays.length && !skipResume; attempt++) {
       if (attempt > 0) {
         await sleep(retryDelays[attempt]);
       }
       try {
         const statusResp = await fetch(baseUrl + 'status/', {
           method: 'GET',
           headers: { 'X-Media-Id': mediaId, 'X-Total-Size': String(totalSize) }
         });
         if (statusResp.ok) {
           const data = await statusResp.json();
           staged = (data && data.staged) || [];
           isAlreadyCommitted = !!(data && data.committed);
           break;
         }
         if (statusResp.status === 401 || statusResp.status === 403) {
           return result;
         }
       } catch (e) {
         staged = [];
       }
     }

     if (isAlreadyCommitted) {
       reportProgress(totalSize);
       result.committed = true;
       return result;
     }

     if (staged.length > 0) {
       let maxStaged = 0;
       for (const s of staged) {
         if (s.size > maxStaged) {
           maxStaged = s.size;
         }
       }

       if (maxStaged > VIDEO_UPLOAD_MAX_SLICE_BYTES) {
         staged = [];
       }
       if (maxStaged > 0 && maxStaged <= VIDEO_UPLOAD_MAX_SLICE_BYTES) {
         chunkSize = maxStaged;
       }
     }

     const chunkTotal = Math.max(1, Math.ceil(totalSize / chunkSize));
     const stagedIndices = new Set(staged.map((s) => s.index));
     const contentType = file.type || 'video/mp4';

     let confirmedBytes = 0;
     for (const s of staged) {
       confirmedBytes += s.size;
     }
     let maxReportedBytes = 0;
     const reportSliceProgress = (extraLoaded) => {
       const uploadedBytes = confirmedBytes + extraLoaded;
       if (uploadedBytes > maxReportedBytes) {
         maxReportedBytes = uploadedBytes;
       }
       reportProgress(maxReportedBytes);
     };
     reportSliceProgress(0);

     const postOneChunk = (index, blob) => {
       return new Promise((resolve) => {
         const xhr = new XMLHttpRequest();
         xhr.open('POST', baseUrl + 'chunk/', true);
         xhr.setRequestHeader('X-Media-Id', mediaId);
         xhr.setRequestHeader('X-Chunk-Index', String(index));
         xhr.setRequestHeader('X-Chunk-Total', String(chunkTotal));
         xhr.setRequestHeader('X-Total-Size', String(totalSize));
         xhr.setRequestHeader('Content-Type', contentType);

         let lastProgressAt = performance.now();
         let silenceAllowanceMs = Math.max(
           VIDEO_UPLOAD_RESPONSE_WAIT_MS,
           Math.ceil((blob.size / VIDEO_UPLOAD_FLOOR_BYTES_PER_SECOND) * 1000));
         if (silenceAllowanceMs > VIDEO_UPLOAD_MAX_DRAIN_MS) {
           silenceAllowanceMs = VIDEO_UPLOAD_MAX_DRAIN_MS;
         }
         let settled = false;
         const settle = (isUploaded) => {
           if (settled) {
             return;
           }
           settled = true;
           clearInterval(stallTimer);
           resolve(isUploaded);
         };
         const stallTimer = setInterval(() => {
           if (isVideoJobCancelled) {
             settle(false);
             xhr.abort();
             return;
           }
           if (performance.now() - lastProgressAt > silenceAllowanceMs) {
             settle(false);
             xhr.abort();
           }
         }, 1000);

         xhr.upload.onprogress = (e) => {
           lastProgressAt = performance.now();
           if (e.lengthComputable) {
             reportSliceProgress(e.loaded);
           }
         };
         xhr.upload.onload = () => {
           lastProgressAt = performance.now();
         };
         xhr.onload = () => settle(xhr.status >= 200 && xhr.status < 300);
         xhr.onerror = () => settle(false);
         xhr.ontimeout = () => settle(false);
         xhr.onabort = () => settle(false);
         xhr.send(blob);
       });
     };

     const postChunkWithRetry = async (index) => {
       const start = index * chunkSize;
       let end = start + chunkSize;
       if (end > totalSize) {
         end = totalSize;
       }
       const blob = file.slice(start, end);
       for (let attempt = 0; attempt < retryDelays.length; attempt++) {
         if (isVideoJobCancelled) {
           return -1;
         }
         if (attempt > 0) {
           await sleep(retryDelays[attempt]);
         }
         const ok = await postOneChunk(index, blob);
         if (ok) {
           return end - start;
         }
       }
       return -1;
     };

     const slicingStartedAt = performance.now();
     let bytesUploadedThisRun = 0;

     for (let index = 0; index < chunkTotal; index++) {
       if (isVideoJobCancelled) {
         result.cancelled = true;
         return result;
       }
       if (deadlineAt > 0 && performance.now() > deadlineAt) {
         result.tooSlow = true;
         return result;
       }
       if (stagedIndices.has(index)) {
         continue;
       }
       const uploadedChunkBytes = await postChunkWithRetry(index);
       if (uploadedChunkBytes < 0) {
         if (isVideoJobCancelled) {
           result.cancelled = true;
         }
         return result;
       }
       confirmedBytes += uploadedChunkBytes;
       bytesUploadedThisRun += uploadedChunkBytes;
       reportSliceProgress(0);

       // Same projection as the single-shot watchdog, measured over the slices actually sent this run.
       const elapsedMs = performance.now() - slicingStartedAt;
       const hasUsableMeasurement = deadlineAt > 0 && elapsedMs >= VIDEO_UPLOAD_SPEED_PROBE_MS;
       if (hasUsableMeasurement) {
         const bytesPerSecond = (bytesUploadedThisRun * 1000) / elapsedMs;
         const remainingBytes = totalSize - confirmedBytes;
         const projectedRemainingMs = (remainingBytes / Math.max(1, bytesPerSecond)) * 1000;
         const projectedFinishAt = performance.now() + projectedRemainingMs;
         if (projectedFinishAt > deadlineAt + VIDEO_UPLOAD_PROJECTION_MARGIN_MS) {
           result.tooSlow = true;
           return result;
         }
       }
     }

     for (let attempt = 0; attempt < retryDelays.length; attempt++) {
       if (isVideoJobCancelled) {
         result.cancelled = true;
         return result;
       }
       if (attempt > 0) {
         await sleep(retryDelays[attempt]);
       }
       try {
         const resp = await fetch(baseUrl + 'complete/', {
           method: 'POST',
           headers: { 'X-Media-Id': mediaId, 'X-Chunk-Total': String(chunkTotal), 'X-Total-Size': String(totalSize) }
         });
         if (resp.ok) {
           result.committed = true;
           return result;
         }
         if (resp.status === 409) {
           let missing = [];
           try {
             const data = await resp.json();
             missing = (data && data.missingIndices) || [];
           } catch (e) {
             missing = [];
           }
           for (const missingIndex of missing) {
             const uploadedChunkBytes = await postChunkWithRetry(missingIndex);
             if (uploadedChunkBytes < 0) {
               return result;
             }
             reportSliceProgress(0);
           }
           continue;
         }
         if (resp.status === 413) {
           return result;
         }
       } catch (e) {
       }
     }
     return result;
   };

   // ---------------------------------------------------------------------------------------------------------
   // Direct-to-storage upload. The pod hands out a SAS scoped to this one blob and the bytes go straight to Azure
   // Blob Storage, so a long clip no longer occupies a request for its whole transfer. Everything below degrades
   // to the proxied path above on ANY failure — a disabled setting, an unsignable credential, a storage account
   // with no CORS rule for this origin — because a slower upload is always better than a lost post.
   // ---------------------------------------------------------------------------------------------------------

   const DIRECT_BLOCK_ID_DIGITS = 8;
   const DIRECT_UPLOAD_FORBIDDEN_STATUS = 403;

   const requestDirectUploadGrant = async (baseUrl, mediaId) => {
     try {
       const response = await fetch(baseUrl + 'direct/', {
         method: 'GET',
         headers: { 'X-Media-Id': mediaId }
       });
       if (!response.ok) {
         return null;
       }

       const grant = await response.json();
       if (!grant || !grant.enabled) {
         return null;
       }
       if (!grant.uploadUrl) {
         return null;
       }
       return grant;
     } catch (e) {
       return null;
     }
   };

   // Same fixed-width, base64 shape the server uses on the proxied paths. Azure refuses a Put Block whose id
   // length disagrees with the blocks already staged against the blob, so every path must produce ids of one
   // length; the prefix is what keeps a resume scan from adopting a block the other path sized differently.
   const generateDirectBlockId = (blockIdPrefix, index) => {
     let indexText = String(index);
     while (indexText.length < DIRECT_BLOCK_ID_DIGITS) {
       indexText = '0' + indexText;
     }
     return btoa(blockIdPrefix + indexText);
   };

   const decodeDirectBlockIndex = (blockIdPrefix, blockId) => {
     try {
       const raw = atob(blockId);
       if (raw.indexOf(blockIdPrefix) !== 0) {
         return -1;
       }
       const indexText = raw.substring(blockIdPrefix.length, raw.length);
       const index = parseInt(indexText, 10);
       if (isNaN(index)) {
         return -1;
       }
       return index;
     } catch (e) {
       return -1;
     }
   };

   // Blocks this browser (or a previous session) already staged against the blob, so a resumed upload re-sends
   // only what is missing. A blob that was never staged answers 404, which is a clean empty list, not an error.
   // isStorageReachable separates "storage answered" from "the request never got a response at all". A storage
   // account with no CORS rule for this origin produces the second, and it refuses every later request the same
   // way, so the caller treats it as "direct is not available here" instead of retrying into the same wall.
   const retrieveDirectStagedBlocks = async (uploadUrl, blockIdPrefix) => {
     const scan = { isStorageReachable: true, blocks: [] };

     let response = null;
     try {
       response = await fetch(uploadUrl + '&comp=blocklist&blocklisttype=uncommitted', { method: 'GET' });
     } catch (e) {
       scan.isStorageReachable = false;
       return scan;
     }
     if (!response.ok) {
       return scan;
     }

     try {
       const xmlText = await response.text();
       const parser = new DOMParser();
       const blockListDocument = parser.parseFromString(xmlText, 'application/xml');
       const blockNodes = blockListDocument.getElementsByTagName('Block');
       for (let i = 0; i < blockNodes.length; i++) {
         const nameNodes = blockNodes[i].getElementsByTagName('Name');
         const sizeNodes = blockNodes[i].getElementsByTagName('Size');
         if (nameNodes.length === 0 || sizeNodes.length === 0) {
           continue;
         }
         const index = decodeDirectBlockIndex(blockIdPrefix, nameNodes[0].textContent);
         if (index < 0) {
           continue;
         }
         const sizeBytes = parseInt(sizeNodes[0].textContent, 10);
         if (isNaN(sizeBytes)) {
           continue;
         }
         scan.blocks.push({ index: index, size: sizeBytes });
       }
     } catch (e) {
       scan.blocks = [];
     }
     return scan;
   };

   const putDirectBlock = (uploadUrl, blockId, blob, onProgress) => {
     return new Promise((resolve) => {
       const xhr = new XMLHttpRequest();
       xhr.open('PUT', uploadUrl + '&comp=block&blockid=' + encodeURIComponent(blockId), true);

       let lastProgressAt = performance.now();
       let silenceAllowanceMs = Math.max(
         VIDEO_UPLOAD_RESPONSE_WAIT_MS,
         Math.ceil((blob.size / VIDEO_UPLOAD_FLOOR_BYTES_PER_SECOND) * 1000));
       if (silenceAllowanceMs > VIDEO_UPLOAD_MAX_DRAIN_MS) {
         silenceAllowanceMs = VIDEO_UPLOAD_MAX_DRAIN_MS;
       }

       let settled = false;
       const settle = (status) => {
         if (settled) {
           return;
         }
         settled = true;
         clearInterval(stallTimer);
         resolve(status);
       };
       const stallTimer = setInterval(() => {
         if (isVideoJobCancelled) {
           settle(0);
           xhr.abort();
           return;
         }
         if (performance.now() - lastProgressAt > silenceAllowanceMs) {
           settle(0);
           xhr.abort();
         }
       }, 1000);

       xhr.upload.onprogress = (e) => {
         lastProgressAt = performance.now();
         if (e.lengthComputable) {
           onProgress(e.loaded);
         }
       };
       xhr.upload.onload = () => {
         lastProgressAt = performance.now();
       };
       xhr.onload = () => settle(xhr.status);
       xhr.onerror = () => settle(0);
       xhr.ontimeout = () => settle(0);
       xhr.onabort = () => settle(0);
       xhr.send(blob);
     });
   };

   // Commits the staged blocks in order. The content type is stated here so the blob is already servable the
   // instant it exists; the server re-stamps it at direct-complete/ because this value is client-controlled.
   const putDirectBlockList = async (uploadUrl, blockIds) => {
     let blockListXml = '<?xml version="1.0" encoding="utf-8"?><BlockList>';
     for (const blockId of blockIds) {
       blockListXml += '<Latest>' + blockId + '</Latest>';
     }
     blockListXml += '</BlockList>';

     try {
       const response = await fetch(uploadUrl + '&comp=blocklist', {
         method: 'PUT',
         headers: {
           'Content-Type': 'application/xml',
           'x-ms-blob-content-type': 'video/mp4'
         },
         body: blockListXml
       });
       return response.status;
     } catch (e) {
       return 0;
     }
   };

   const uploadDirectToBlob = async (file, grant, baseUrl, mediaId, reportProgress, deadlineAt, skipResume) => {
     const result = { committed: false, tooSlow: false, cancelled: false, failureReason: '' };

     const retryDelays = [0, 3000, 5000, 10000, 20000];
     const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

     const totalSize = file.size;
     let blockSize = grant.blockSizeBytes;
     if (!(blockSize > 0)) {
       blockSize = VIDEO_UPLOAD_DEFAULT_SLICE_BYTES;
     }
     const blockIdPrefix = grant.blockIdPrefix;
     const blockTotal = Math.max(1, Math.ceil(totalSize / blockSize));

     // The SAS expires; a refresh is one cheap call and is the difference between a resumable transfer and a lost
     // one on a link slow enough to outlive the grant.
     let uploadUrl = grant.uploadUrl;
     const refreshUploadUrlAsync = async () => {
       const refreshedGrant = await requestDirectUploadGrant(baseUrl, mediaId);
       if (!refreshedGrant) {
         return false;
       }
       uploadUrl = refreshedGrant.uploadUrl;
       return true;
     };

     const expectedBlockSize = (index) => {
       const start = index * blockSize;
       let end = start + blockSize;
       if (end > totalSize) {
         end = totalSize;
       }
       return end - start;
     };

     // Flipped by the first request in this run that comes back with an HTTP status of any kind. Until it is true,
     // a status of 0 means the request never reached storage, which no amount of retrying changes.
     let hasStorageAnswered = false;

     // A staged block is only reusable when its size is exactly what this run would send for that index —
     // anything else was staged for different bytes (a re-encode, another block size) and must be overwritten.
     const stagedIndices = new Set();
     let confirmedBytes = 0;
     if (!skipResume) {
       const stagedScan = await retrieveDirectStagedBlocks(uploadUrl, blockIdPrefix);
       // Storage did not answer the scan at all. Staging blocks from here would walk the whole retry ladder — some
       // forty seconds with the progress bar pinned at zero — only to land on the proxied upload anyway, so hand
       // over now and let the caller log the reason.
       if (!stagedScan.isStorageReachable) {
         result.failureReason = 'storage did not answer the resume scan — no CORS rule for this origin, or no network';
         return result;
       }
       hasStorageAnswered = true;

       for (const stagedBlock of stagedScan.blocks) {
         if (stagedBlock.index < 0 || stagedBlock.index >= blockTotal) {
           continue;
         }
         const expectedSize = expectedBlockSize(stagedBlock.index);
         if (stagedBlock.size !== expectedSize) {
           continue;
         }
         stagedIndices.add(stagedBlock.index);
         confirmedBytes += stagedBlock.size;
       }
     }

     let maxReportedBytes = 0;
     const reportBlockProgress = (extraLoaded) => {
       const uploadedBytes = confirmedBytes + extraLoaded;
       if (uploadedBytes > maxReportedBytes) {
         maxReportedBytes = uploadedBytes;
       }
       reportProgress(maxReportedBytes);
     };
     reportBlockProgress(0);

     const putBlockWithRetry = async (index) => {
       const start = index * blockSize;
       let end = start + blockSize;
       if (end > totalSize) {
         end = totalSize;
       }
       const blob = file.slice(start, end);

       for (let attempt = 0; attempt < retryDelays.length; attempt++) {
         if (isVideoJobCancelled) {
           return -1;
         }
         if (attempt > 0) {
           await sleep(retryDelays[attempt]);
         }

         const blockId = generateDirectBlockId(blockIdPrefix, index);
         const onBlockProgress = (loadedBytes) => reportBlockProgress(loadedBytes);
         const status = await putDirectBlock(uploadUrl, blockId, blob, onBlockProgress);
         if (status > 0) {
           hasStorageAnswered = true;
         }
         if (status >= 200 && status < 300) {
           return end - start;
         }
         // Nothing in this run has ever reached storage and this attempt got no response either — the same wall the
         // resume scan hits when the origin has no CORS rule. The remaining attempts would only sleep through it.
         if (status === 0 && !hasStorageAnswered) {
           return -1;
         }
         if (status === DIRECT_UPLOAD_FORBIDDEN_STATUS) {
           // Expired or revoked credential — one refresh, then the remaining attempts run against the new url.
           const refreshed = await refreshUploadUrlAsync();
           if (!refreshed) {
             return -1;
           }
         }
       }
       return -1;
     };

     const directStartedAt = performance.now();
     let bytesUploadedThisRun = 0;

     for (let index = 0; index < blockTotal; index++) {
       if (isVideoJobCancelled) {
         result.cancelled = true;
         return result;
       }
       if (deadlineAt > 0 && performance.now() > deadlineAt) {
         result.tooSlow = true;
         return result;
       }
       if (stagedIndices.has(index)) {
         continue;
       }

       const uploadedBlockBytes = await putBlockWithRetry(index);
       if (uploadedBlockBytes < 0) {
         if (isVideoJobCancelled) {
           result.cancelled = true;
           return result;
         }
         result.failureReason = 'put-block failed for index ' + index;
         return result;
       }
       confirmedBytes += uploadedBlockBytes;
       bytesUploadedThisRun += uploadedBlockBytes;
       reportBlockProgress(0);

       // Same projection the proxied paths run: bail to the optimise stage rather than burn the whole window.
       const elapsedMs = performance.now() - directStartedAt;
       const hasUsableMeasurement = deadlineAt > 0 && elapsedMs >= VIDEO_UPLOAD_SPEED_PROBE_MS;
       if (hasUsableMeasurement) {
         const bytesPerSecond = (bytesUploadedThisRun * 1000) / elapsedMs;
         const remainingBytes = totalSize - confirmedBytes;
         const projectedRemainingMs = (remainingBytes / Math.max(1, bytesPerSecond)) * 1000;
         const projectedFinishAt = performance.now() + projectedRemainingMs;
         if (projectedFinishAt > deadlineAt + VIDEO_UPLOAD_PROJECTION_MARGIN_MS) {
           result.tooSlow = true;
           return result;
         }
       }
     }

     const blockIds = [];
     for (let index = 0; index < blockTotal; index++) {
       blockIds.push(generateDirectBlockId(blockIdPrefix, index));
     }

     let isBlockListCommitted = false;
     for (let attempt = 0; attempt < retryDelays.length && !isBlockListCommitted; attempt++) {
       if (isVideoJobCancelled) {
         result.cancelled = true;
         return result;
       }
       if (attempt > 0) {
         await sleep(retryDelays[attempt]);
       }

       const status = await putDirectBlockList(uploadUrl, blockIds);
       if (status >= 200 && status < 300) {
         isBlockListCommitted = true;
       }
       else if (status === DIRECT_UPLOAD_FORBIDDEN_STATUS) {
         const refreshed = await refreshUploadUrlAsync();
         if (!refreshed) {
           result.failureReason = 'block list commit forbidden and the grant could not be refreshed';
           return result;
         }
       }
     }

     if (!isBlockListCommitted) {
       result.failureReason = 'block list commit failed';
       return result;
     }

     // The blob exists but nothing has vetted it yet: the byte cap and the stored content type are the server's
     // to decide, and a post must not be written against a blob that failed either check.
     for (let attempt = 0; attempt < retryDelays.length; attempt++) {
       if (attempt > 0) {
         await sleep(retryDelays[attempt]);
       }
       try {
         const response = await fetch(baseUrl + 'direct-complete/', {
           method: 'POST',
           headers: { 'X-Media-Id': mediaId, 'X-Total-Size': String(totalSize) }
         });
         if (response.ok) {
           reportBlockProgress(0);
           result.committed = true;
           return result;
         }
         if (response.status === 413 || response.status === 401 || response.status === 403) {
           result.failureReason = 'direct-complete refused with ' + response.status;
           return result;
         }
       } catch (e) {
       }
     }

     result.failureReason = 'direct-complete never answered';
     return result;
   };

   window.cancelVideoJob = () => {
     isVideoJobCancelled = true;
     const instance = window.__ffmpegInstance;
     window.__ffmpegInstance = null;
     window.__ffmpegLoadPromise = null;
     if (instance) {
       try { instance.terminate(); } catch (e) { }
     }
   };

   window.uploadStashedVideo = async (token, baseUrl, mediaId, dotNetRef, progressMethod, options) => {
     const outcome = { committed: false, tooSlow: false, cancelled: false, usedDirectUpload: false, directFailureReason: '' };
     isVideoJobCancelled = false;

     const switchAfterMs = (options && options.switchToOptimiseAfterMs) || 0;
     const skipResume = !!(options && options.skipResume);
     // VideoUploadMode, as resolved server-side. False means the grant endpoint would refuse anyway, so asking it
     // would only cost a round trip before every proxied upload.
     const allowDirectUpload = !!(options && options.allowDirectUpload);
     let deadlineAt = 0;
     if (switchAfterMs > 0) {
       deadlineAt = performance.now() + switchAfterMs;
     }

     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return outcome;
     }
     if (!(file.size > 0)) {
       return outcome;
     }

     let lastReportedPercent = -1;
     const reportProgress = (uploadedBytes) => {
       if (!dotNetRef || !progressMethod) {
         return;
       }

       const percent = Math.floor((uploadedBytes * 100) / file.size);
       if (percent === lastReportedPercent) {
         return;
       }
       lastReportedPercent = percent;

       try { dotNetRef.invokeMethodAsync(progressMethod, uploadedBytes); } catch (err) { }
     };

     const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
     let isKnownSlowLink = false;
     let assumedBytesPerSecond = 0;
     if (conn) {
       if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') {
         isKnownSlowLink = true;
         assumedBytesPerSecond = 5 * 1024;
       }
       if (conn.effectiveType === '3g') {
         isKnownSlowLink = true;
         assumedBytesPerSecond = 15 * 1024;
       }
       if (conn.saveData === true) {
         isKnownSlowLink = true;
       }
     }

     // Known-slow link: the projection is available before any byte is sent, so optimise first and skip the probe.
     if (deadlineAt > 0 && assumedBytesPerSecond > 0) {
       const projectedUploadMs = (file.size / assumedBytesPerSecond) * 1000;
       if (projectedUploadMs > switchAfterMs + VIDEO_UPLOAD_PROJECTION_MARGIN_MS) {
         outcome.tooSlow = true;
         return outcome;
       }
     }

     // Direct to storage first, when the mode selects it and the pod says it is available. A clip already over the
     // caller's tier cap is left to the proxied path, which refuses it with the same 413 it always did instead of
     // having the bytes travel to storage only to be deleted at direct-complete/.
     let directGrant = null;
     if (allowDirectUpload) {
       directGrant = await requestDirectUploadGrant(baseUrl, mediaId);
     }
     let isWithinDirectUploadCap = true;
     if (directGrant && directGrant.maxUploadBytes > 0 && file.size > directGrant.maxUploadBytes) {
       isWithinDirectUploadCap = false;
     }
     if (directGrant && isWithinDirectUploadCap) {
       const directOutcome = await uploadDirectToBlob(file, directGrant, baseUrl, mediaId, reportProgress,
         deadlineAt, skipResume);

       if (directOutcome.committed) {
         outcome.committed = true;
         outcome.usedDirectUpload = true;
         return outcome;
       }
       if (directOutcome.cancelled) {
         outcome.cancelled = true;
         outcome.usedDirectUpload = true;
         return outcome;
       }
       if (directOutcome.tooSlow) {
         outcome.tooSlow = true;
         outcome.usedDirectUpload = true;
         return outcome;
       }

       // Anything else falls through to the proxied upload below, which is the whole point of not making the
       // direct path load-bearing: a missing CORS rule or a revoked key costs speed, never the post. The reason
       // rides back to .NET so the failure is in the pod log instead of only in this browser's console.
       outcome.directFailureReason = directOutcome.failureReason;
       reportProgress(0);
     }

     const isTooLargeForOneRequest = file.size > VIDEO_UPLOAD_SINGLE_SHOT_MAX_BYTES;

     const hasSlicedBefore = videoUploadSlicedMediaIds.has(mediaId);
     let measuredBytesPerSecond = assumedBytesPerSecond;
     if (!isKnownSlowLink && !hasSlicedBefore && !isTooLargeForOneRequest) {
       const attempt = await uploadWholeFile(file, baseUrl, mediaId, reportProgress, deadlineAt);
       if (attempt.verdict === 'ok') {
         outcome.committed = true;
         return outcome;
       }
       if (attempt.verdict === 'rejected') {
         return outcome;
       }
       if (attempt.verdict === 'too-slow') {
         outcome.tooSlow = true;
         return outcome;
       }
       if (attempt.verdict === 'cancelled') {
         outcome.cancelled = true;
         return outcome;
       }
       measuredBytesPerSecond = attempt.bytesPerSecond;
       reportProgress(0);
     }

     videoUploadSlicedMediaIds.add(mediaId);
     const sliced = await uploadInSlices(file, baseUrl, mediaId, reportProgress, measuredBytesPerSecond, deadlineAt, skipResume);
     outcome.committed = sliced.committed;
     outcome.tooSlow = sliced.tooSlow;
     outcome.cancelled = sliced.cancelled;
     return outcome;
   };

   // Instagram Reel preview — video element helpers invoked from InstagramPostPreview.razor via ElementReference.
   // A Blazor ElementReference whose element was NOT rendered this pass still marshals across as a plain,
   // truthy object - so a bare `if (el)` guard passes and the first real DOM call throws a JSException at the
   // caller. Every igp* video helper takes an element straight from an ElementReference, so they all check that
   // what arrived is actually a media element and no-op otherwise: a preview nicety must never break a render.
   const isPlayableMediaElement = (el) => el instanceof HTMLMediaElement;

   window.igpPlayVideo = (el) => { if (isPlayableMediaElement(el)) el.play(); };
   window.igpPauseVideo = (el) => { if (isPlayableMediaElement(el)) el.pause(); };

   // In-preview photo framing for PostPreviewFramedPhoto.razor: drag to pan, pinch or slide to zoom. Applies the
   // geometry live in the browser (no Blazor round-trip per pointer move) and reports the committed framing to .NET
   // once the gesture ends. The size/offset formula below is the exact mirror of ImageHelpers.DisplayedSizePercent —
   // that parity is what makes the preview equal to the uploaded crop, so change both or neither.
   //
   // Zoom 1 = the whole photo fitted inside the frame (bars on the two short sides). Above the cover zoom the photo
   // overflows the frame and the overflow is what panning traverses; an axis that does not overflow stays centred and
   // ignores drags, so a zoomed-out photo cannot be nudged off-centre into a lie about what will be posted.
   window.cpInitFraming = (imgElement, sliderElement, dotNetRef,
                           sourceAspect, frameAspect, zoom, minZoom, maxZoom, focalX, focalY) => {
     if (!imgElement) { return; }
     // The frame is whatever the host positioned the photo inside (Instagram's .igp-image-wrap, TikTok's .tkp-frame),
     // so it needs no ElementReference plumbing of its own — and gestures bind to the frame rather than the image so
     // a drag that leaves the photo's own bounds (easy at low zoom, where the photo is smaller than the frame) still
     // tracks.
     const frameElement = imgElement.parentElement;
     if (!frameElement) { return; }
     // Re-initialising over a live wiring (same frame, new photo) would stack a second listener set and double every
     // gesture, so drop the previous one first.
     window.cpDisposeFraming(imgElement);

     let currentZoom = zoom;
     let currentFocalX = focalX;
     let currentFocalY = focalY;

     let panning = false;
     let panStartX = 0;
     let panStartY = 0;
     let panStartFocalX = 0.5;
     let panStartFocalY = 0.5;

     // Live pointers, so a second finger switches the gesture from pan to pinch.
     const activePointers = new Map();
     let pinchStartDistance = 0;
     let pinchStartZoom = 1;

     const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

     const displayedSizePercent = () => {
       let widthFraction = 1;
       let heightFraction = 1;
       if (sourceAspect >= frameAspect) {
         heightFraction = frameAspect / sourceAspect;
       } else {
         widthFraction = sourceAspect / frameAspect;
       }
       return { w: widthFraction * currentZoom * 100, h: heightFraction * currentZoom * 100 };
     };

     const apply = () => {
       const size = displayedSizePercent();

       let left = (100 - size.w) / 2;
       if (size.w > 100) { left = -(size.w - 100) * currentFocalX; }
       let top = (100 - size.h) / 2;
       if (size.h > 100) { top = -(size.h - 100) * currentFocalY; }

       imgElement.style.width = size.w + '%';
       imgElement.style.height = size.h + '%';
       imgElement.style.left = left + '%';
       imgElement.style.top = top + '%';

       // Only an overflowing photo can be panned; say so with the cursor rather than offering a dead grab.
       const canPan = size.w > 100 || size.h > 100;
       if (canPan) {
         frameElement.style.cursor = 'grab';
       } else {
         frameElement.style.cursor = '';
       }
     };

     const commit = () => {
       dotNetRef.invokeMethodAsync('OnFramingCommitted', currentFocalX, currentFocalY, currentZoom).catch(() => { });
     };

     const onPointerDown = (e) => {
       if (e.target !== imgElement) { return; }
       activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

       if (activePointers.size === 2) {
         // Second finger down: abandon the pan and start a pinch from the current separation.
         panning = false;
         const points = Array.from(activePointers.values());
         const dx = points[0].x - points[1].x;
         const dy = points[0].y - points[1].y;
         pinchStartDistance = Math.max(1, Math.sqrt(dx * dx + dy * dy));
         pinchStartZoom = currentZoom;
         return;
       }

       if (activePointers.size !== 1) { return; }
       panning = true;
       panStartX = e.clientX;
       panStartY = e.clientY;
       panStartFocalX = currentFocalX;
       panStartFocalY = currentFocalY;
       try { frameElement.setPointerCapture(e.pointerId); } catch (err) { }
       frameElement.style.cursor = 'grabbing';
     };

     const onPointerMove = (e) => {
       if (!activePointers.has(e.pointerId)) { return; }
       activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

       if (activePointers.size === 2) {
         e.preventDefault();
         const points = Array.from(activePointers.values());
         const dx = points[0].x - points[1].x;
         const dy = points[0].y - points[1].y;
         const distance = Math.max(1, Math.sqrt(dx * dx + dy * dy));
         currentZoom = clamp(pinchStartZoom * (distance / pinchStartDistance), minZoom, maxZoom);
         if (sliderElement) { sliderElement.value = currentZoom; }
         apply();
         return;
       }

       if (!panning) { return; }
       e.preventDefault();

       const rect = frameElement.getBoundingClientRect();
       const size = displayedSizePercent();

       // Focal 0..1 spans the OVERFLOW, not the frame, so a drag of N pixels must move the focal point by N over the
       // overflow in pixels. Dividing by the frame instead would make panning progressively too fast as zoom grows.
       const overflowX = Math.max(0, (size.w - 100) / 100) * Math.max(rect.width, 1);
       const overflowY = Math.max(0, (size.h - 100) / 100) * Math.max(rect.height, 1);

       // Dragging right reveals more of the photo's left edge, i.e. lowers the focal point.
       if (overflowX > 0) {
         currentFocalX = clamp(panStartFocalX - (e.clientX - panStartX) / overflowX, 0, 1);
       }
       if (overflowY > 0) {
         currentFocalY = clamp(panStartFocalY - (e.clientY - panStartY) / overflowY, 0, 1);
       }
       apply();
     };

     const onPointerUp = (e) => {
       const wasTracked = activePointers.delete(e.pointerId);
       if (!wasTracked) { return; }
       try { frameElement.releasePointerCapture(e.pointerId); } catch (err) { }

       // Commit once the last finger lifts, so a pinch reports a single result rather than one per finger.
       if (activePointers.size === 0) {
         panning = false;
         apply();
         commit();
       }
     };

     // The slider drives the zoom directly for pointer users. 'input' is live (no round-trip), 'change' commits.
     const onSliderInput = () => {
       currentZoom = clamp(parseFloat(sliderElement.value), minZoom, maxZoom);
       apply();
     };
     const onSliderChange = () => {
       onSliderInput();
       commit();
     };
     // A slider drag starts on the image's frame; stop it from also panning the photo underneath.
     const onSliderPointerDown = (e) => { e.stopPropagation(); };

     frameElement.addEventListener('pointerdown', onPointerDown);
     frameElement.addEventListener('pointermove', onPointerMove);
     frameElement.addEventListener('pointerup', onPointerUp);
     frameElement.addEventListener('pointercancel', onPointerUp);
     if (sliderElement) {
       sliderElement.addEventListener('input', onSliderInput);
       sliderElement.addEventListener('change', onSliderChange);
       sliderElement.addEventListener('pointerdown', onSliderPointerDown);
     }
     imgElement.style.touchAction = 'none';
     apply();

     frameElement._cpFraming = {
       onPointerDown, onPointerMove, onPointerUp,
       sliderElement, onSliderInput, onSliderChange, onSliderPointerDown
     };
   };

   window.cpDisposeFraming = (imgElement) => {
     if (!imgElement) { return; }
     const frameElement = imgElement.parentElement;
     if (!frameElement || !frameElement._cpFraming) { return; }
     const h = frameElement._cpFraming;
     frameElement.removeEventListener('pointerdown', h.onPointerDown);
     frameElement.removeEventListener('pointermove', h.onPointerMove);
     frameElement.removeEventListener('pointerup', h.onPointerUp);
     frameElement.removeEventListener('pointercancel', h.onPointerUp);
     if (h.sliderElement) {
       h.sliderElement.removeEventListener('input', h.onSliderInput);
       h.sliderElement.removeEventListener('change', h.onSliderChange);
       h.sliderElement.removeEventListener('pointerdown', h.onSliderPointerDown);
     }
     frameElement.style.cursor = '';
     delete frameElement._cpFraming;
   };

   // Facebook preview — bottom control bar (time readout + seekable progress) plus the mobile chrome (muted
   // autoplay + thin non-interactive progress track). Pure JS like the TikTok preview's controls, so per-frame
   // updates and scrubbing never round-trip the Blazor circuit.
   window.fbpInitVideoControls = (videoEl, timeEl, progressFillEl, progressBarEl, mobileTrackFillEl, dotNetRef) => {
     // Same unresolved-ElementReference hazard as the igp* helpers above: the chrome refs only exist alongside
     // the <video>, so all of them are checked for a real node rather than mere truthiness.
     if (!isPlayableMediaElement(videoEl)) return;
     if (!(timeEl instanceof Element) || !(progressFillEl instanceof Element) || !(progressBarEl instanceof Element)) return;
     let seeking = false;

     // Mobile Facebook autoplays feed clips muted; mirror that under the same breakpoint that swaps the CSS chrome.
     // The .NET side is told so its play state matches the element.
     if (window.matchMedia('(max-width: 640px)').matches) {
       videoEl.muted = true;
       videoEl.play().then(() => {
         if (dotNetRef) dotNetRef.invokeMethodAsync('OnVideoAutoPlayed');
       }).catch(() => {});
     }

     const formatTime = (seconds) => {
       if (!isFinite(seconds) || seconds < 0) seconds = 0;
       const total = Math.floor(seconds);
       const minutes = Math.floor(total / 60);
       const secs = String(total % 60).padStart(2, '0');
       return minutes + ':' + secs;
     };

     const update = () => {
       if (!document.body.contains(videoEl)) return;
       if (!seeking && videoEl.duration > 0) {
         const fillWidth = (videoEl.currentTime / videoEl.duration * 100) + '%';
         progressFillEl.style.width = fillWidth;
         if (mobileTrackFillEl) mobileTrackFillEl.style.width = fillWidth;
       }
       timeEl.textContent = formatTime(videoEl.currentTime) + ' / ' + formatTime(videoEl.duration);
       requestAnimationFrame(update);
     };
     requestAnimationFrame(update);

     const seekToX = (clientX) => {
       const rect = progressBarEl.getBoundingClientRect();
       let ratio = (clientX - rect.left) / rect.width;
       if (ratio < 0) ratio = 0;
       if (ratio > 1) ratio = 1;
       if (videoEl.duration > 0) {
         videoEl.currentTime = ratio * videoEl.duration;
       }
       progressFillEl.style.width = (ratio * 100) + '%';
     };

     progressBarEl.addEventListener('mousedown', (e) => {
       seeking = true;
       seekToX(e.clientX);

       const onMove = (ev) => seekToX(ev.clientX);
       const onUp = () => {
         seeking = false;
         document.removeEventListener('mousemove', onMove);
         document.removeEventListener('mouseup', onUp);
       };
       document.addEventListener('mousemove', onMove);
       document.addEventListener('mouseup', onUp);
     });
   };
   window.igpSetMuted = (el, muted) => { if (isPlayableMediaElement(el)) el.muted = muted; };
   window.igpReplayVideo = (el) => { if (isPlayableMediaElement(el)) { el.currentTime = 0; el.play(); } };
   window.igpWireVideoEnded = (el, dotNetRef) => {
     if (!isPlayableMediaElement(el)) return;
     el.addEventListener('ended', () => {
       dotNetRef.invokeMethodAsync('OnVideoEnded');
     });
   };

   // Returns a blob URL for the stashed video so it can be played inline in a <video> preview. The URL lives until
   // the stash entry is discarded or the page unloads — no manual revoke needed since the File is already in memory.
   window.getStashedVideoBlobUrl = (token) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return '';
     }
     return URL.createObjectURL(file);
   };

   // Drag-and-drop bridge for the create-post media dialog. A file dropped anywhere on the dialog is routed
   // through the SAME hidden <InputFile> the "Choose media" button opens: the dropped file is assigned to the
   // input and a synthetic change event is dispatched, so Blazor's OnMediaSelected runs the exact same
   // photo-vs-video classification / cropper / preview path — no logic is duplicated here. Idempotent per
   // element (a re-render never double-wires); the active class highlights the target while a file is over it.
   window.wireMediaDropZone = (dropZoneId, inputId) => {
     const zone = document.getElementById(dropZoneId);
     const input = document.getElementById(inputId);
     if (!zone || !input || zone._mediaDropWired) {
       return;
     }
     zone._mediaDropWired = true;

     const stop = (e) => {
       e.preventDefault();
       e.stopPropagation();
     };

     ['dragenter', 'dragover'].forEach((evt) => zone.addEventListener(evt, (e) => {
       stop(e);
       zone.classList.add('post-media-dropzone-active');
     }));

     ['dragleave', 'dragend'].forEach((evt) => zone.addEventListener(evt, (e) => {
       stop(e);
       zone.classList.remove('post-media-dropzone-active');
     }));

     zone.addEventListener('drop', (e) => {
       stop(e);
       zone.classList.remove('post-media-dropzone-active');
       const files = e.dataTransfer && e.dataTransfer.files;
       if (!files || files.length === 0) {
         return;
       }
       // One file at a time — the dialog stages a single photo or clip, same as the picker. Assigning via a
       // DataTransfer then dispatching 'change' is how Blazor's InputFile picks up a programmatically-set file.
       const transfer = new DataTransfer();
       transfer.items.add(files[0]);
       input.files = transfer.files;
       input.dispatchEvent(new Event('change', { bubbles: true }));
     });
   };

   // Recovers a selection made before Blazor finished arming the picker.
   //
   // <InputFile> only becomes functional once the framework's shim is installed, and that happens on a
   // separate OnAfterRenderAsync -> Blazor._internal.InputFile.init interop round-trip - strictly AFTER the
   // render batch that puts the input (and the "drag and drop a file here" hint) in the DOM. A file assigned
   // in that window fires 'change' with nothing listening yet and is dropped on the floor. It cannot be
   // recovered by picking the same file again either: the browser only dispatches 'change' when the FileList
   // actually differs, so re-assigning an identical file is a no-op and the dialog sits on its empty hint with
   // Save disabled forever. Measured window on a loaded circuit is a few hundred ms, which a drag-and-drop
   // landing right as the dialog opens - and every automated run - is well inside of. See TKR-10946.
   //
   // The shim writes _blazorInputFileNextFileId on init and creates _blazorFilesById the first time its change
   // handler runs, so "armed but never fired, with a file already sitting on it" is exactly detectable, and the
   // replay below can never fire for a selection Blazor already saw.
   window.ensureMediaPickerArmed = (inputId) => {
     const input = document.getElementById(inputId);
     if (!input) {
       return;
     }

     let attemptsLeft = 100;

     const replayEarlySelection = () => {
       if (input._blazorInputFileNextFileId === undefined) {
         attemptsLeft = attemptsLeft - 1;
         if (attemptsLeft > 0) {
           window.setTimeout(replayEarlySelection, 50);
         }
         return;
       }

       if (input._blazorFilesById !== undefined) {
         return;
       }

       if (!input.files || input.files.length === 0) {
         return;
       }

       input.dispatchEvent(new Event('change', { bubbles: true }));
     };

     replayEarlySelection();
   };

   // Lazily loads the self-hosted single-thread ffmpeg.wasm build the first time a clip actually needs
   // trimming. The single-thread core needs no SharedArrayBuffer, so the site does NOT require cross-origin
   // isolation (COOP/COEP) — important because enabling that would break third-party embeds (YouTube, etc.).
   // The wasm core is fetched only on the first trim; an in-limits, untrimmed clip never triggers this path.
   window.__ffmpegInstance = window.__ffmpegInstance || null;
   window.__ffmpegLoadPromise = window.__ffmpegLoadPromise || null;

   // Memoised by URL rather than by a DOM lookup: two callers racing on the same src must await the SAME load,
   // and a <script> that is merely present in the head has not necessarily executed yet.
   const scriptLoadPromises = {};

   const loadScriptOnce = (src) => {
     if (scriptLoadPromises[src]) {
       return scriptLoadPromises[src];
     }
     scriptLoadPromises[src] = new Promise((resolve, reject) => {
       const script = document.createElement('script');
       script.src = src;
       script.async = true;
       script.setAttribute('data-script-once', src);
       script.onload = () => resolve();
       script.onerror = () => reject(new Error('failed to load ' + src));
       document.head.appendChild(script);
     });
     // Drop the memo on failure so a later attempt retries instead of replaying the rejection forever.
     scriptLoadPromises[src].catch(() => { scriptLoadPromises[src] = null; });
     return scriptLoadPromises[src];
   };

   const ensureFfmpegLoaded = () => {
     if (window.__ffmpegInstance) {
       return Promise.resolve(window.__ffmpegInstance);
     }
     if (window.__ffmpegLoadPromise) {
       return window.__ffmpegLoadPromise;
     }

     const base = '/_content/ffmpeg';
     window.__ffmpegLoadPromise = (async () => {
       // The UMD build attaches FFmpegWASM to window; its worker chunk (814.ffmpeg.js) is loaded automatically
       // from the same folder via the bundle's public path, so no classWorkerURL is needed. (@ffmpeg/util is
       // deliberately NOT loaded — its UMD build references a bare `exports` and throws in a plain <script>; the
       // one helper we'd use from it, fetchFile, is inlined in trimStashedVideo instead.)
       await loadScriptOnce(base + '/ffmpeg/ffmpeg.js');
       const ffmpeg = new window.FFmpegWASM.FFmpeg();
       await ffmpeg.load({
         coreURL: base + '/core/ffmpeg-core.js',
         wasmURL: base + '/core/ffmpeg-core.wasm'
       });
       window.__ffmpegInstance = ffmpeg;
       return ffmpeg;
     })();
     // Drop the cached promise on failure so a later trim can retry instead of being stuck on a rejected load.
     window.__ffmpegLoadPromise.catch(() => { window.__ffmpegLoadPromise = null; });
     return window.__ffmpegLoadPromise;
   };

   // Preferred transcode path: WebCodecs drives the platform's own (usually hardware) H.264 decoder and encoder,
   // which runs roughly an order of magnitude faster than the single-thread wasm encoder and needs ~230 KB of
   // script instead of the 32 MB ffmpeg core. WebCodecs handles raw codec access only — it neither demuxes nor
   // muxes MP4 — so mp4box.js reads the source samples and mp4-muxer writes the output container.
   const VIDEO_VENDOR_BASE = '/_content/Tokero.Shared.Blazor/js/vendor';

   const isWebCodecsTranscodeSupported = () => {
     if (typeof window.VideoEncoder !== 'function') {
       return false;
     }
     if (typeof window.VideoDecoder !== 'function') {
       return false;
     }
     if (typeof window.EncodedVideoChunk !== 'function') {
       return false;
     }
     if (typeof window.VideoFrame !== 'function') {
       return false;
     }
     if (typeof window.OffscreenCanvas !== 'function') {
       return false;
     }
     return true;
   };

   const ensureVideoContainerToolsLoaded = async () => {
     await Promise.all([
       loadScriptOnce(VIDEO_VENDOR_BASE + '/mp4box.all.min.js'),
       loadScriptOnce(VIDEO_VENDOR_BASE + '/mp4-muxer.js')
     ]);
     if (typeof window.MP4Box === 'undefined' || typeof window.Mp4Muxer === 'undefined') {
       throw new Error('mp4 container tools did not register their globals');
     }
   };

   const VIDEO_COMPRESS_PHASE_LOADING_TOOLS = 0;
   const VIDEO_COMPRESS_PHASE_PREPARING = 1;
   const VIDEO_COMPRESS_PHASE_ENCODING = 2;

   const VIDEO_TOOLS_WASM_URL = '/_content/ffmpeg/core/ffmpeg-core.wasm';
   window.__videoToolsWarmPromise = window.__videoToolsWarmPromise || null;
   window.__videoToolsWarmPercent = 0;

   const warmVideoToolsCache = () => {
     if (window.__videoToolsWarmPromise) {
       return window.__videoToolsWarmPromise;
     }
     window.__videoToolsWarmPromise = (async () => {
       try {
         const response = await fetch(VIDEO_TOOLS_WASM_URL);
         if (!response.ok || !response.body) {
           window.__videoToolsWarmPercent = 100;
           return;
         }
         let totalBytes = 0;
         const totalHeader = response.headers.get('Content-Length');
         if (totalHeader) {
           totalBytes = parseInt(totalHeader, 10);
         }
         const reader = response.body.getReader();
         let receivedBytes = 0;
         for (; ;) {
           const step = await reader.read();
           if (step.done) {
             break;
           }
           receivedBytes += step.value.length;
           if (totalBytes > 0) {
             window.__videoToolsWarmPercent = Math.floor((receivedBytes * 100) / totalBytes);
           }
         }
         window.__videoToolsWarmPercent = 100;
       } catch (e) {
         window.__videoToolsWarmPercent = 100;
       }
     })();
     return window.__videoToolsWarmPromise;
   };

   // Warmed when the media dialog opens. On a WebCodecs browser this pulls ~230 KB of demux/mux script and
   // nothing else — deliberately NOT the 32 MB wasm core, which the optimise path only ever reaches as a
   // fallback and which would otherwise be fetched over the very link we are optimising for.
   window.preloadVideoTools = () => {
     if (isWebCodecsTranscodeSupported()) {
       ensureVideoContainerToolsLoaded().catch(() => { });
       return;
     }
     warmVideoToolsCache().then(() => { ensureFfmpegLoaded().catch(() => { }); });
   };

   const VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE = {
     'slow-2g': 4 * 1024,
     '2g': 5 * 1024,
     '3g': 15 * 1024,
     '4g': 300 * 1024
   };
   const VIDEO_PLAN_UPLINK_ASSUMED_MOBILE = 80 * 1024;
   const VIDEO_PLAN_UPLINK_ASSUMED_DESKTOP = 1024 * 1024;

   // Encode speed as a multiple of realtime (1.0 = a 60 s clip takes 60 s to transcode). The WebCodecs rate is
   // a deliberately conservative floor for a hardware encoder; the wasm rates are what a SINGLE-THREAD software
   // x264 actually manages, which is well below realtime and unaffected by core count because the wasm core
   // cannot use more than one thread. Over-estimating here is what used to make the plan promise a one-minute
   // optimise that then ran for ten, and made it pick an encode that was slower than just uploading.
   // Measured ~5x realtime for 720p -> 480p on a desktop hardware encoder (demux and file read included); 4.0
   // leaves headroom for slower mobile encoders, and under-promising is the safe direction for an ETA.
   const VIDEO_PLAN_ENCODE_RATE_WEBCODECS = 4.0;
   const VIDEO_PLAN_ENCODE_RATE_WASM_DESKTOP = 0.5;
   const VIDEO_PLAN_ENCODE_RATE_WASM_MOBILE = 0.2;

   // Cost of the ffmpeg.wasm core, charged to the plan when the fallback path would have to fetch it.
   const VIDEO_PLAN_TOOLS_DOWNLOAD_BYTES = 32 * 1024 * 1024;

   const VIDEO_PLAN_OUTPUT_BYTES_PER_SECOND_720 = 210 * 1024;
   const VIDEO_PLAN_OUTPUT_BYTES_PER_SECOND_480 = 105 * 1024;

   // Fallback for the OptimiseVideoMaxEdgePixels setting, used when the caller passes nothing usable. The C#
   // side supplies the configured value, so this only covers a plan requested before the setting is readable.
   const VIDEO_PLAN_MAX_EDGE_PIXELS_FALLBACK = 1080;
   // Edges the planner picks for itself once it decides a constrained uplink is worth an encode. Both are
   // clamped to the configured ceiling, so lowering that lowers every optimise output.
   const VIDEO_PLAN_MAX_EDGE_PIXELS_CONSTRAINED = 720;
   const VIDEO_PLAN_MAX_EDGE_PIXELS_CONSTRAINED_SLOW = 480;

   const VIDEO_PLAN_MIN_UPLOAD_SECONDS_TO_BOTHER = 25;
   const VIDEO_PLAN_REQUIRED_SAVING = 0.7;

   const isMobileDevice = () => {
     if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
       return navigator.userAgentData.mobile;
     }
     return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
   };

   const VIDEO_PLAN_LOW_DOWNLINK_MBPS = 3;

   const evaluateLink = () => {
     const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
     const isMobile = isMobileDevice();

     if (conn) {
       if (conn.saveData === true) {
         return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE['2g'], isConstrained: true, label: 'save-data' };
       }

       const known = VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE[conn.effectiveType];
       const isSlowType = conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g' || conn.effectiveType === '3g';
       if (isSlowType && known) {
         return { uplinkBytesPerSecond: known, isConstrained: true, label: conn.effectiveType };
       }

       const isLowDownlink = conn.downlink > 0 && conn.downlink < VIDEO_PLAN_LOW_DOWNLINK_MBPS;
       if (isLowDownlink) {
         return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE['3g'], isConstrained: true, label: 'low-bandwidth' };
       }
       if (isMobile) {
         return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE['4g'], isConstrained: true, label: '4g-mobile' };
       }
       return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_ASSUMED_DESKTOP, isConstrained: false, label: 'fast' };
     }

     if (isMobile) {
       return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_ASSUMED_MOBILE, isConstrained: true, label: 'mobile-assumed' };
     }
     return { uplinkBytesPerSecond: VIDEO_PLAN_UPLINK_ASSUMED_DESKTOP, isConstrained: false, label: 'desktop-assumed' };
   };

   const estimateEncodeRate = () => {
     if (isWebCodecsTranscodeSupported()) {
       return VIDEO_PLAN_ENCODE_RATE_WEBCODECS;
     }
     if (isMobileDevice()) {
       return VIDEO_PLAN_ENCODE_RATE_WASM_MOBILE;
     }
     return VIDEO_PLAN_ENCODE_RATE_WASM_DESKTOP;
   };

   // Seconds the optimise stage must spend fetching its own tooling before it can encode a single frame. Zero on
   // the WebCodecs path (the demux/mux scripts are ~230 KB and already warmed when the dialog opened) and zero
   // once the wasm core has been warmed this session; otherwise it is 32 MB over the same link we are trying to
   // spare, which is routinely more expensive than uploading the clip untouched.
   const estimateToolsDownloadSeconds = (uplinkBytesPerSecond) => {
     if (isWebCodecsTranscodeSupported()) {
       return 0;
     }
     if (window.__videoToolsWarmPercent >= 100) {
       return 0;
     }
     const downloadSeconds = VIDEO_PLAN_TOOLS_DOWNLOAD_BYTES / uplinkBytesPerSecond;
     return downloadSeconds;
   };

   // configuredMaxEdgePixels is the OptimiseVideoMaxEdgePixels setting, supplied by the caller. It is the edge a
   // FORCED optimise uses (rescueMaxEdgePixels) and the ceiling clamping the edge the planner picks for itself.
   window.planVideoProcessing = (sizeBytes, durationSeconds, configuredMaxEdgePixels) => {
     const link = evaluateLink();
     const uplinkBytesPerSecond = link.uplinkBytesPerSecond;
     const encodeRate = estimateEncodeRate();
     const uploadAsIsSeconds = sizeBytes / uplinkBytesPerSecond;

     let rescueMaxEdgePixels = VIDEO_PLAN_MAX_EDGE_PIXELS_FALLBACK;
     if (configuredMaxEdgePixels > 0) {
       rescueMaxEdgePixels = configuredMaxEdgePixels;
     }

     const plan = {
       shouldCompress: false,
       maxEdgePixels: rescueMaxEdgePixels,
       rescueMaxEdgePixels: rescueMaxEdgePixels,
       quality: 28,
       uplinkBytesPerSecond: Math.round(uplinkBytesPerSecond),
       uploadAsIsSeconds: Math.round(uploadAsIsSeconds),
       estimatedTotalSeconds: Math.round(uploadAsIsSeconds),
       isMobile: isMobileDevice(),
       linkLabel: link.label,
       reason: 'upload-as-is'
     };

     if (!link.isConstrained) {
       plan.reason = 'link-not-constrained';
       return plan;
     }

     if (!(durationSeconds > 0) || !(sizeBytes > 0)) {
       plan.reason = 'unknown-duration';
       return plan;
     }

     if (uploadAsIsSeconds < VIDEO_PLAN_MIN_UPLOAD_SECONDS_TO_BOTHER) {
       plan.reason = 'upload-already-short';
       return plan;
     }

     let maxEdgePixels = VIDEO_PLAN_MAX_EDGE_PIXELS_CONSTRAINED;
     let quality = 28;
     let outputBytesPerSecond = VIDEO_PLAN_OUTPUT_BYTES_PER_SECOND_720;
     if (uplinkBytesPerSecond <= VIDEO_PLAN_UPLINK_BY_EFFECTIVE_TYPE['3g']) {
       maxEdgePixels = VIDEO_PLAN_MAX_EDGE_PIXELS_CONSTRAINED_SLOW;
       quality = 30;
       outputBytesPerSecond = VIDEO_PLAN_OUTPUT_BYTES_PER_SECOND_480;
     }
     // A ceiling below the constrained edge wins — the setting is the hard cap on what the optimise stage emits.
     if (maxEdgePixels > rescueMaxEdgePixels) {
       maxEdgePixels = rescueMaxEdgePixels;
     }

     const compressedBytes = durationSeconds * outputBytesPerSecond;
     if (compressedBytes >= sizeBytes) {
       plan.reason = 'already-efficient';
       return plan;
     }

     const encodeSeconds = durationSeconds / encodeRate;
     const uploadAfterSeconds = compressedBytes / uplinkBytesPerSecond;
     const toolsDownloadSeconds = estimateToolsDownloadSeconds(uplinkBytesPerSecond);
     const totalWithCompression = toolsDownloadSeconds + encodeSeconds + uploadAfterSeconds;

     if (totalWithCompression > uploadAsIsSeconds * VIDEO_PLAN_REQUIRED_SAVING) {
       plan.reason = 'encode-not-worth-it';
       plan.estimatedTotalSeconds = Math.round(uploadAsIsSeconds);
       return plan;
     }

     plan.shouldCompress = true;
     plan.maxEdgePixels = maxEdgePixels;
     plan.quality = quality;
     plan.estimatedTotalSeconds = Math.round(totalWithCompression);
     plan.reason = 'compress';
     return plan;
   };

   const VIDEO_OPTIMISED_CACHE_NAME = 'tokero-optimised-video-v1';
   const VIDEO_CACHE_VARIANT_ORIGINAL = 'original';
   const VIDEO_CACHE_VARIANT_OPTIMISED = 'optimised';
   const videoCacheUrl = (mediaId, variant) => {
     return '/__tokero-video/' + encodeURIComponent(mediaId) + '/' + variant;
   };

   window.cacheStashedVideo = async (mediaId, token, variant) => {
     const blob = window.__pendingVideoUploads[token];
     if (!blob || !window.caches) {
       return false;
     }
     try {
       const cache = await window.caches.open(VIDEO_OPTIMISED_CACHE_NAME);
       await cache.put(videoCacheUrl(mediaId, variant), new Response(blob, {
         headers: { 'Content-Type': blob.type || 'video/mp4' }
       }));
       return true;
     } catch (e) {
       console.error('cacheStashedVideo failed', e);
       return false;
     }
   };

   window.restoreStashedVideoFromCache = async (mediaId, token, variant) => {
     if (!window.caches) {
       return -1;
     }
     try {
       const cache = await window.caches.open(VIDEO_OPTIMISED_CACHE_NAME);
       const hit = await cache.match(videoCacheUrl(mediaId, variant));
       if (!hit) {
         return -1;
       }
       const blob = await hit.blob();
       if (!(blob.size > 0)) {
         return -1;
       }
       window.__pendingVideoUploads[token] = blob;
       return blob.size;
     } catch (e) {
       console.error('restoreStashedVideoFromCache failed', e);
       return -1;
     }
   };

   window.stashVideoFromUrl = async (url, token) => {
     try {
       const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
       if (!response.ok) {
         return -1;
       }
       const blob = await response.blob();
       if (!(blob.size > 0)) {
         return -1;
       }
       window.__pendingVideoUploads[token] = blob;
       return blob.size;
     } catch (e) {
       console.error('stashVideoFromUrl failed', e);
       return -1;
     }
   };

   window.hasStashedVideo = (token) => {
     const file = window.__pendingVideoUploads[token];
     return !!(file && file.size > 0);
   };

   window.restoreVideoForResume = async (mediaId, token) => {
     const optimised = await window.restoreStashedVideoFromCache(mediaId, token, VIDEO_CACHE_VARIANT_OPTIMISED);
     if (optimised > 0) {
       return { sizeBytes: optimised, isOptimised: true };
     }
     const original = await window.restoreStashedVideoFromCache(mediaId, token, VIDEO_CACHE_VARIANT_ORIGINAL);
     return { sizeBytes: original, isOptimised: false };
   };

   const VIDEO_PENDING_POST_KEY = 'tokero-pending-video-post';

   window.savePendingVideoPost = (recordJson) => {
     try {
       window.localStorage.setItem(VIDEO_PENDING_POST_KEY, recordJson);
       return true;
     } catch (e) {
       return false;
     }
   };

   window.loadPendingVideoPost = () => {
     try {
       return window.localStorage.getItem(VIDEO_PENDING_POST_KEY);
     } catch (e) {
       return null;
     }
   };

   window.clearPendingVideoPost = () => {
     try {
       window.localStorage.removeItem(VIDEO_PENDING_POST_KEY);
     } catch (e) {
     }
   };

   window.clearVideoCache = async (mediaId) => {
     if (!window.caches) {
       return;
     }
     try {
       const cache = await window.caches.open(VIDEO_OPTIMISED_CACHE_NAME);
       await cache.delete(videoCacheUrl(mediaId, VIDEO_CACHE_VARIANT_ORIGINAL));
       await cache.delete(videoCacheUrl(mediaId, VIDEO_CACHE_VARIANT_OPTIMISED));
     } catch (e) {
     }
   };

   let videoJobWakeLock = null;
   let isVideoJobWakeLockWanted = false;

   const requestVideoJobWakeLock = async () => {
     if (!isVideoJobWakeLockWanted || videoJobWakeLock) {
       return;
     }
     if (!navigator.wakeLock) {
       return;
     }
     if (document.visibilityState !== 'visible') {
       return;
     }
     try {
       videoJobWakeLock = await navigator.wakeLock.request('screen');
       videoJobWakeLock.addEventListener('release', () => { videoJobWakeLock = null; });
     } catch (e) {
       videoJobWakeLock = null;
     }
   };

   const onVideoJobVisibilityChange = () => {
     if (document.visibilityState === 'visible') {
       requestVideoJobWakeLock();
     }
   };

   window.acquireVideoJobWakeLock = () => {
     if (isVideoJobWakeLockWanted) {
       return;
     }
     isVideoJobWakeLockWanted = true;
     document.addEventListener('visibilitychange', onVideoJobVisibilityChange);
     requestVideoJobWakeLock();
   };

   window.releaseVideoJobWakeLock = async () => {
     isVideoJobWakeLockWanted = false;
     document.removeEventListener('visibilitychange', onVideoJobVisibilityChange);
     if (!videoJobWakeLock) {
       return;
     }
     try {
       await videoJobWakeLock.release();
     } catch (e) {
     }
     videoJobWakeLock = null;
   };

   // Cuts the stashed clip to [startSeconds, endSeconds] in the browser and REPLACES the stash entry with the
   // (-c copy) keeps it near-instant and lossless; the start snaps to the nearest prior keyframe (~1-2s), which
   // is acceptable for social clips. -movflags faststart relocates the moov atom to the front so the clip
   // streams/plays immediately from the CDN. Returns the trimmed byte size, or -1 on failure (the caller then
   // keeps the original stashed file and lets the server size cap be the final gate).
   window.trimStashedVideo = async (token, startSeconds, endSeconds) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return -1;
     }

     let clipSeconds = endSeconds - startSeconds;
     if (!(clipSeconds > 0)) {
       return -1;
     }

     try {
       const ffmpeg = await ensureFfmpegLoaded();
       const inputName = 'in.mp4';
       const outputName = 'out.mp4';

       // Read the picked File/Blob into ffmpeg's virtual FS. (This is what @ffmpeg/util's fetchFile does, but
       // that package's UMD build isn't browser-global safe, so we inline the single line we need.)
       const inputData = new Uint8Array(await file.arrayBuffer());
       await ffmpeg.writeFile(inputName, inputData);
       // -ss / -t as INPUT-side options (before -i) do a fast keyframe seek; -t is an explicit duration so the
       // end point is unambiguous regardless of ffmpeg version.
       await ffmpeg.exec([
         '-ss', String(startSeconds),
         '-t', String(clipSeconds),
         '-i', inputName,
         '-c', 'copy',
         '-movflags', 'faststart',
         outputName
       ]);
       const data = await ffmpeg.readFile(outputName);

       // Free the in-memory virtual FS entries so repeated trims in one session don't accumulate memory.
       try { await ffmpeg.deleteFile(inputName); } catch (e) { }
       try { await ffmpeg.deleteFile(outputName); } catch (e) { }

       const trimmedBlob = new Blob([data], { type: 'video/mp4' });
       window.__pendingVideoUploads[token] = trimmedBlob;
       return trimmedBlob.size;
     } catch (err) {
       console.error('trimStashedVideo failed', err);
       return -1;
     }
   };

   // ── Academy course importer: the author's own transcript or recording ──
   // Reads a subtitle or plain-text transcript straight off the picker - a read, not a conversion.
   // Returns { ok, text, truncated, error }; a rejected file surfaces as a dialog message, not an exception.
   window.academyReadTranscriptFile = async (inputId, maximumCharacters) => {
     try {
       const input = document.getElementById(inputId);
       if (!input || !input.files || input.files.length === 0) {
         return { ok: false, text: '', truncated: false, error: 'no-file' };
       }

       const file = input.files[0];
       const raw = await file.text();
       if (!raw || raw.trim().length === 0) {
         return { ok: false, text: '', truncated: false, error: 'empty' };
       }

       // Cut to the same ceiling the textarea enforces; the server normalises and bounds again afterwards.
       let text = raw;
       let truncated = false;
       if (maximumCharacters > 0 && text.length > maximumCharacters) {
         text = text.substring(0, maximumCharacters);
         truncated = true;
       }

       return { ok: true, text: text, truncated: truncated, error: '' };
     } catch (err) {
       console.error('academyReadTranscriptFile failed', err);
       return { ok: false, text: '', truncated: false, error: 'exception' };
     }
   };

   // Reduces any audio or video file to a 16 kHz mono 32 kbps mp3 - about as low as speech survives, roughly 14 MB an
   // hour - in the browser, so the source file itself never has to be uploaded. Shared by the Academy's transcription
   // and the AI Twin materials page.
   const transcodeToSpeechMp3 = async (file) => {
     const ffmpeg = await ensureFfmpegLoaded();

     // Keep the original extension: ffmpeg picks its demuxer from the name.
     let extension = 'mp4';
     const dotIndex = file.name.lastIndexOf('.');
     if (dotIndex > -1 && dotIndex < file.name.length - 1) {
       extension = file.name.substring(dotIndex + 1).toLowerCase();
     }

     const inputName = 'speech-in.' + extension;
     const outputName = 'speech-out.mp3';

     const inputData = new Uint8Array(await file.arrayBuffer());
     await ffmpeg.writeFile(inputName, inputData);

     // -vn drops video, -ac 1 mono, -ar 16000 resample, -b:a 32k.
     await ffmpeg.exec([
       '-i', inputName,
       '-vn',
       '-ac', '1',
       '-ar', '16000',
       '-b:a', '32k',
       outputName
     ]);

     const audioData = await ffmpeg.readFile(outputName);

     try { await ffmpeg.deleteFile(inputName); } catch (e) { }
     try { await ffmpeg.deleteFile(outputName); } catch (e) { }

     const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
     return audioBlob;
   };

   // Reduces the picked recording to a speech mp3 and uploads only that to the AI Twin materials endpoint, which queues
   // it for transcription. Returns { ok, error } where error is a Resources key from the server, or a short code
   // ('no-file', 'too-large', 'http-<status>', 'exception'); never throws at the C# side.
   window.aiTwinUploadRecording = async (inputId, endpointUrl, maximumAudioBytes, dotNetRef, phaseMethod) => {
     const reportPhase = (phase) => {
       if (!dotNetRef || !phaseMethod) {
         return;
       }
       try {
         dotNetRef.invokeMethodAsync(phaseMethod, phase);
       } catch (e) {
         /* The page may already be gone; progress is not worth failing the upload over. */
       }
     };

     try {
       const input = document.getElementById(inputId);
       if (!input || !input.files || input.files.length === 0) {
         return { ok: false, error: 'no-file' };
       }

       const file = input.files[0];

       // Phase 0: preparing the audio (the ffmpeg core is a 32 MB fetch on first use).
       reportPhase(0);
       const audioBlob = await transcodeToSpeechMp3(file);
       if (maximumAudioBytes > 0 && audioBlob.size > maximumAudioBytes) {
         return { ok: false, error: 'too-large' };
       }

       // Phase 1: uploading.
       reportPhase(1);
       const formData = new FormData();
       formData.append('audio', audioBlob, 'recording.mp3');
       formData.append('originalFileName', file.name);

       const response = await fetch(endpointUrl, {
         method: 'POST',
         body: formData,
         credentials: 'same-origin'
       });

       if (!response.ok) {
         let serverError = 'http-' + response.status;
         try {
           const errorPayload = await response.json();
           if (errorPayload && errorPayload.error) {
             serverError = errorPayload.error;
           }
         } catch (e) {
           /* Not JSON - the status code is the whole story. */
         }
         return { ok: false, error: serverError };
       }

       input.value = '';
       return { ok: true, error: '' };
     } catch (err) {
       console.error('aiTwinUploadRecording failed', err);
       return { ok: false, error: 'exception' };
     }
   };

   // Strips the audio to 16 kHz mono mp3 (~14 MB) and posts only that, so a 2 GB source never enters C#.
   // Returns { ok, transcript, error } and never throws at the C# side.
   window.academyTranscribeMediaFile = async (inputId, endpointUrl, maximumAudioBytes, dotNetRef, phaseMethod) => {
     const reportPhase = (phase) => {
       if (!dotNetRef || !phaseMethod) {
         return;
       }
       try {
         dotNetRef.invokeMethodAsync(phaseMethod, phase);
       } catch (e) {
         /* The dialog may already be gone; progress is not worth failing the run over. */
       }
     };

     try {
       const input = document.getElementById(inputId);
       if (!input || !input.files || input.files.length === 0) {
         return { ok: false, transcript: '', error: 'no-file' };
       }

       const file = input.files[0];

       // Phase 0: preparing the audio. The ffmpeg core alone is a 32 MB fetch on first use.
       reportPhase(0);

       const audioBlob = await transcodeToSpeechMp3(file);

       // Checked before the upload so an oversized recording costs a transcode, not a transcode plus a round trip.
       if (maximumAudioBytes > 0 && audioBlob.size > maximumAudioBytes) {
         return { ok: false, transcript: '', error: 'too-large' };
       }

       // Phase 1: uploading and transcribing. This is the slow half on a long recording.
       reportPhase(1);

       const formData = new FormData();
       formData.append('audio', audioBlob, 'academy-audio.mp3');

       const response = await fetch(endpointUrl, {
         method: 'POST',
         body: formData,
         credentials: 'same-origin'
       });

       if (!response.ok) {
         return { ok: false, transcript: '', error: 'http-' + response.status };
       }

       const payload = await response.json();
       if (!payload || !payload.transcript) {
         return { ok: false, transcript: '', provider: '', model: '', error: 'empty' };
       }

       // provider/model come back so the dialog can attribute the transcript to the model, not the author.
       return {
         ok: true,
         transcript: payload.transcript,
         provider: payload.provider || '',
         model: payload.model || '',
         error: ''
       };
     } catch (err) {
       console.error('academyTranscribeMediaFile failed', err);
       return { ok: false, transcript: '', provider: '', model: '', error: 'exception' };
     }
   };

   // Progress plumbing shared by both transcode paths, so the C# side sees one phase/percent/ETA contract
   // regardless of which encoder actually ran.
   const createVideoCompressReporter = (dotNetRef, progressMethod) => {
     let encodeStartedAt = 0;
     let lastReportedPhase = -1;
     let lastReportedPercent = -1;

     const reportPhase = (phase, percent, secondsRemaining) => {
       if (!dotNetRef || !progressMethod) {
         return;
       }
       if (phase === lastReportedPhase && percent === lastReportedPercent) {
         return;
       }
       lastReportedPhase = phase;
       lastReportedPercent = percent;
       try { dotNetRef.invokeMethodAsync(progressMethod, phase, percent, secondsRemaining); } catch (err) { }
     };

     const startEncodeTimer = () => {
       encodeStartedAt = performance.now();
     };

     const reportEncodeProgress = (ratio) => {
       let percent = Math.floor(ratio * 100);
       if (percent < 0) {
         percent = 0;
       }
       if (percent > 100) {
         percent = 100;
       }

       let secondsRemaining = -1;
       if (ratio > 0.01) {
         const elapsedMs = performance.now() - encodeStartedAt;
         secondsRemaining = Math.ceil(((elapsedMs / ratio) - elapsedMs) / 1000);
       }

       reportPhase(VIDEO_COMPRESS_PHASE_ENCODING, percent, secondsRemaining);
     };

     return {
       reportPhase: reportPhase,
       startEncodeTimer: startEncodeTimer,
       reportEncodeProgress: reportEncodeProgress
     };
   };

   // Output bitrate is derived from pixels x frames, because H.264 in WebCodecs exposes rate control only as a
   // bitrate; the plan's CRF is translated into this scale by computeBitsPerPixelForQuality. 0.06 bits per pixel
   // per frame lands within a few percent of the sizes the planner already assumes for 720p and 480p
   // (VIDEO_PLAN_OUTPUT_BYTES_PER_SECOND_*), so the plan's estimate and the real output stay consistent.
   const VIDEO_WEBCODECS_BITS_PER_PIXEL = 0.06;
   const VIDEO_WEBCODECS_MIN_BITS_PER_SECOND = 300 * 1000;
   const VIDEO_WEBCODECS_MAX_BITS_PER_SECOND = 6 * 1000 * 1000;
   // The plan's quality is an x264 CRF, which the WebCodecs encoder cannot take. VIDEO_WEBCODECS_BITS_PER_PIXEL is
   // calibrated for this CRF, so the value below is the anchor the translation scales away from.
   const VIDEO_WEBCODECS_BASELINE_CRF = 28;
   // x264 rule of thumb: every 6 points of CRF roughly halves (or doubles) the bitrate.
   const VIDEO_WEBCODECS_CRF_HALVING_STEP = 6;
   // Guard rails for the translated value, so an out-of-range CRF row in the DB cannot ask for an unwatchable
   // clip or for one bigger than the source. 0.02 is roughly CRF 37, 0.24 roughly CRF 16.
   const VIDEO_WEBCODECS_MIN_BITS_PER_PIXEL = 0.02;
   const VIDEO_WEBCODECS_MAX_BITS_PER_PIXEL = 0.24;
   const VIDEO_WEBCODECS_KEYFRAME_SECONDS = 2;
   const VIDEO_WEBCODECS_CODEC_QUEUE_LIMIT = 8;
   const VIDEO_WEBCODECS_SAMPLE_HIGH_WATER = 90;
   const VIDEO_WEBCODECS_VIDEO_BATCH_SAMPLES = 30;
   const VIDEO_WEBCODECS_AUDIO_BATCH_SAMPLES = 100;
   const VIDEO_WEBCODECS_DEFAULT_FRAME_RATE = 30;
   const VIDEO_WEBCODECS_MAX_FRAME_RATE = 120;
   const VIDEO_WEBCODECS_AAC_LC_CODEC = 'mp4a.40.2';
   const VIDEO_WEBCODECS_AAC_LC_OBJECT_TYPE = 2;
   const VIDEO_WEBCODECS_QUEUE_POLL_MS = 5;
   const VIDEO_WEBCODECS_LEVEL_31_MACROBLOCKS = 3600;
   const MICROSECONDS_PER_SECOND = 1000000;

   // Holds the feed loop back while the codecs are saturated. The 'dequeue' event is not available everywhere
   // WebCodecs is, so this polls the queue depth instead.
   const waitForCodecQueue = async (readQueueDepth) => {
     while (readQueueDepth() > VIDEO_WEBCODECS_CODEC_QUEUE_LIMIT) {
       if (isVideoJobCancelled) {
         return;
       }
       await new Promise((resolve) => { window.setTimeout(resolve, VIDEO_WEBCODECS_QUEUE_POLL_MS); });
     }
   };

   // The decoder needs the codec-private bytes (AVCDecoderConfigurationRecord / HEVCDecoderConfigurationRecord).
   // mp4box exposes them only as a writable box, so serialise the box and drop its 8-byte size+type header.
   const readVideoDecoderDescription = (mp4boxFile, trackId) => {
     const trak = mp4boxFile.getTrackById(trackId);
     if (!trak) {
       return null;
     }
     const entries = trak.mdia.minf.stbl.stsd.entries;
     for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
       let configurationBox = entries[entryIndex].avcC;
       if (!configurationBox) {
         configurationBox = entries[entryIndex].hvcC;
       }
       if (!configurationBox) {
         continue;
       }
       const stream = new window.DataStream(undefined, 0, window.DataStream.BIG_ENDIAN);
       configurationBox.write(stream);
       const description = new Uint8Array(stream.buffer.slice(8));
       return description;
     }
     return null;
   };

   const MP4_ES_DESCRIPTOR_TAG = 0x03;
   const MP4_DECODER_CONFIG_DESCRIPTOR_TAG = 0x04;
   const MP4_DECODER_SPECIFIC_INFO_TAG = 0x05;
   const MP4_OBJECT_TYPE_MPEG4_AUDIO = 0x40;

   // MPEG-4 descriptor lengths are 7 bits per byte, with the top bit marking "another length byte follows".
   const readDescriptorLength = (bytes, cursor) => {
     let length = 0;
     let offset = cursor;
     for (let step = 0; step < 4; step++) {
       const current = bytes[offset];
       offset = offset + 1;
       length = (length << 7) | (current & 0x7f);
       if ((current & 0x80) === 0) {
         break;
       }
     }
     return { length: length, cursor: offset };
   };

   // AudioSpecificConfig for the copied-through AAC track, read straight out of the raw stsd bytes rather than
   // from mp4box's parsed tree: QuickTime .mov files use a sound sample entry layout mp4box does not descend
   // into, so the parsed tree exposes no esds at all for them even though the box is present. Returns null for
   // anything that is not MPEG-4 audio, which is what keeps MP3-in-MP4 off this path.
   const readAacAudioSpecificConfig = (mp4boxFile, trackId, sourceBuffer) => {
     const trak = mp4boxFile.getTrackById(trackId);
     if (!trak) {
       return null;
     }
     const sampleDescriptionBox = trak.mdia.minf.stbl.stsd;
     if (!sampleDescriptionBox || !(sampleDescriptionBox.size > 0)) {
       return null;
     }

     const bytes = new Uint8Array(sourceBuffer, sampleDescriptionBox.start, sampleDescriptionBox.size);
     let esdsAt = -1;
     for (let index = 0; index + 4 <= bytes.length; index++) {
       if (bytes[index] === 0x65 && bytes[index + 1] === 0x73 && bytes[index + 2] === 0x64 && bytes[index + 3] === 0x73) {
         esdsAt = index;
         break;
       }
     }
     if (esdsAt < 0) {
       return null;
     }

     // Skip the 'esds' fourcc and the full-box version/flags word, then walk ES -> DecoderConfig -> SpecificInfo.
     let cursor = esdsAt + 4 + 4;
     if (bytes[cursor] !== MP4_ES_DESCRIPTOR_TAG) {
       return null;
     }
     cursor = cursor + 1;
     const esDescriptorLength = readDescriptorLength(bytes, cursor);
     cursor = esDescriptorLength.cursor + 2;
     const esFlags = bytes[cursor];
     cursor = cursor + 1;
     if ((esFlags & 0x80) !== 0) {
       cursor = cursor + 2;
     }
     if ((esFlags & 0x40) !== 0) {
       cursor = cursor + 1 + bytes[cursor];
     }
     if ((esFlags & 0x20) !== 0) {
       cursor = cursor + 2;
     }

     if (bytes[cursor] !== MP4_DECODER_CONFIG_DESCRIPTOR_TAG) {
       return null;
     }
     cursor = cursor + 1;
     const decoderConfigLength = readDescriptorLength(bytes, cursor);
     cursor = decoderConfigLength.cursor;
     if (bytes[cursor] !== MP4_OBJECT_TYPE_MPEG4_AUDIO) {
       return null;
     }
     // objectTypeIndication(1) + streamType(1) + bufferSizeDB(3) + maxBitrate(4) + avgBitrate(4)
     cursor = cursor + 13;

     if (bytes[cursor] !== MP4_DECODER_SPECIFIC_INFO_TAG) {
       return null;
     }
     cursor = cursor + 1;
     const specificInfoLength = readDescriptorLength(bytes, cursor);
     cursor = specificInfoLength.cursor;
     if (!(specificInfoLength.length > 0) || cursor + specificInfoLength.length > bytes.length) {
       return null;
     }

     const audioSpecificConfig = bytes.slice(cursor, cursor + specificInfoLength.length);
     return audioSpecificConfig;
   };

   // tkhd display matrix, 16.16 fixed point. Phones store an upright clip as landscape frames plus a rotation,
   // and atan2 is scale-invariant so the raw fixed-point values can be fed in directly.
   const readTrackRotationDegrees = (videoTrack) => {
     const matrix = videoTrack.matrix;
     if (!matrix || matrix.length < 5) {
       return 0;
     }
     const radians = Math.atan2(matrix[1], matrix[0]);
     let degrees = Math.round(radians * 180 / Math.PI);
     if (degrees < 0) {
       degrees = degrees + 360;
     }
     if (degrees === 90 || degrees === 180 || degrees === 270) {
       return degrees;
     }
     return 0;
   };

   // The optimise stage stores a portrait clip in the same 9:16 canvas the feed shows it in, so the file matches the
   // frame and the pixels the player would have cropped away are never encoded, uploaded, stored or served. Shared
   // with the ffmpeg fallback below through the same ratio, so both transcode paths emit the same shape.
   const VIDEO_TRANSCODE_PORTRAIT_ASPECT = 9 / 16;

   // Largest 9:16 rectangle that fits inside a portrait source, in source pixels. A clip already at 9:16 comes back
   // untouched; a 4:5 loses width, a 1:3 loses height. Landscape and square sources are passed straight through —
   // only vertical video is normalised.
   const composePortraitCropSize = (displayWidth, displayHeight) => {
     if (displayHeight <= displayWidth) {
       return { width: displayWidth, height: displayHeight };
     }

     let width = displayWidth;
     let height = Math.round(displayWidth / VIDEO_TRANSCODE_PORTRAIT_ASPECT);
     if (height > displayHeight) {
       height = displayHeight;
       width = Math.round(displayHeight * VIDEO_TRANSCODE_PORTRAIT_ASPECT);
     }
     return { width: width, height: height };
   };

   // Destination rectangle that makes the source frame COVER the target box, centered — the canvas clips whatever
   // overflows. Cropping this way instead of with a source rectangle keeps the rotation transforms below untouched:
   // they already draw into a destination rect, so the crop rides along whatever matrix is in effect.
   const composeCoverDrawRect = (sourceWidth, sourceHeight, targetWidth, targetHeight) => {
     let scale = targetWidth / sourceWidth;
     const scaleToFillHeight = targetHeight / sourceHeight;
     if (scaleToFillHeight > scale) {
       scale = scaleToFillHeight;
     }

     const width = sourceWidth * scale;
     const height = sourceHeight * scale;
     const x = (targetWidth - width) / 2;
     const y = (targetHeight - height) / 2;
     return { x: x, y: y, width: width, height: height };
   };

   // Destination rectangle that makes the source frame FIT ENTIRELY inside the target box, centered, leaving bars on
   // whichever axis it does not fill. The refit path draws with this instead of the cover rect above: it re-frames a
   // clip the author already composed, so reaching a platform's minimum frame must not cut content out of the shot.
   const composeContainDrawRect = (sourceWidth, sourceHeight, targetWidth, targetHeight) => {
     let scale = targetWidth / sourceWidth;
     const scaleToFitHeight = targetHeight / sourceHeight;
     if (scaleToFitHeight < scale) {
       scale = scaleToFitHeight;
     }

     const width = sourceWidth * scale;
     const height = sourceHeight * scale;
     const x = (targetWidth - width) / 2;
     const y = (targetHeight - height) / 2;
     return { x: x, y: y, width: width, height: height };
   };

   const computeTranscodeTargetSize = (displayWidth, displayHeight, maxEdgePixels) => {
     let scale = 1;
     let longestEdge = displayWidth;
     if (displayHeight > longestEdge) {
       longestEdge = displayHeight;
     }
     // Only ever downscale — an in-limits clip must not be blown up just because a target edge was requested.
     if (longestEdge > maxEdgePixels) {
       scale = maxEdgePixels / longestEdge;
     }

     let width = Math.round(displayWidth * scale);
     let height = Math.round(displayHeight * scale);
     // 4:2:0 chroma needs even dimensions on both axes, the same thing ffmpeg's -2 does in the scale filter.
     width = width - (width % 2);
     height = height - (height % 2);
     if (width < 2) {
       width = 2;
     }
     if (height < 2) {
       height = 2;
     }
     return { width: width, height: height };
   };

   // Translates the plan's x264 CRF into the bits-per-pixel figure this path encodes with, so the same quality
   // knob drives both encoders instead of being silently dropped here. Lower CRF = more bits.
   const computeBitsPerPixelForQuality = (quality) => {
     if (!(quality > 0)) {
       return VIDEO_WEBCODECS_BITS_PER_PIXEL;
     }

     const crfStepsBelowBaseline = (VIDEO_WEBCODECS_BASELINE_CRF - quality) / VIDEO_WEBCODECS_CRF_HALVING_STEP;
     const bitrateFactor = Math.pow(2, crfStepsBelowBaseline);
     let bitsPerPixel = VIDEO_WEBCODECS_BITS_PER_PIXEL * bitrateFactor;
     if (bitsPerPixel < VIDEO_WEBCODECS_MIN_BITS_PER_PIXEL) {
       bitsPerPixel = VIDEO_WEBCODECS_MIN_BITS_PER_PIXEL;
     }
     if (bitsPerPixel > VIDEO_WEBCODECS_MAX_BITS_PER_PIXEL) {
       bitsPerPixel = VIDEO_WEBCODECS_MAX_BITS_PER_PIXEL;
     }
     return bitsPerPixel;
   };

   // Main profile first for its better compression, baseline as the compatibility fallback.
   const chooseVideoEncoderConfiguration = async (width, height, frameRate, bitsPerSecond) => {
     const macroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
     let levelSuffix = '1f';
     if (macroblocks > VIDEO_WEBCODECS_LEVEL_31_MACROBLOCKS) {
       levelSuffix = '28';
     }

     const candidateCodecs = ['avc1.4d00' + levelSuffix, 'avc1.4200' + levelSuffix];
     for (let index = 0; index < candidateCodecs.length; index++) {
       const configuration = {
         codec: candidateCodecs[index],
         width: width,
         height: height,
         bitrate: bitsPerSecond,
         bitrateMode: 'variable',
         framerate: frameRate,
         avc: { format: 'avc' }
       };
       try {
         const support = await window.VideoEncoder.isConfigSupported(configuration);
         if (support && support.supported) {
           return configuration;
         }
       } catch (e) {
       }
     }
     return null;
   };

   // Bakes the source rotation into the pixels rather than passing a matrix through to the output, matching
   // what ffmpeg's autorotation produces so both transcode paths yield an upright file. sourceSize is the frame's
   // size in DISPLAY orientation. Cover fit (the optimise path): when the source is a wider ratio than the target box
   // - a portrait clip being normalised to 9:16 - the rect overflows the canvas and the excess is clipped, which is
   // the crop. Contain fit (the refit path): the rect never overflows, and the unfilled remainder stays black.
   const drawFrameIntoTarget = (canvasContext, frame, sourceSize, targetSize, rotationDegrees, isContainFit) => {
     let composeDrawRect = composeCoverDrawRect;
     if (isContainFit) {
       composeDrawRect = composeContainDrawRect;
       // A contained frame leaves bars the drawImage below never touches, so without this they keep whatever the
       // previous frame left behind. Painted in canvas space, before the rotation branches install their transform.
       canvasContext.fillStyle = '#000000';
       canvasContext.fillRect(0, 0, targetSize.width, targetSize.height);
     }

     // The 90 / 270 branches rotate the canvas, so their local axes are swapped relative to the display ones — both
     // the source and the target have to be handed over swapped as well for the fit maths to land the same way.
     if (rotationDegrees === 90) {
       const rotatedRect = composeDrawRect(sourceSize.height, sourceSize.width, targetSize.height, targetSize.width);
       canvasContext.save();
       canvasContext.translate(targetSize.width, 0);
       canvasContext.rotate(Math.PI / 2);
       canvasContext.drawImage(frame, rotatedRect.x, rotatedRect.y, rotatedRect.width, rotatedRect.height);
       canvasContext.restore();
       return;
     }
     if (rotationDegrees === 180) {
       const flippedRect = composeDrawRect(sourceSize.width, sourceSize.height, targetSize.width, targetSize.height);
       canvasContext.save();
       canvasContext.translate(targetSize.width, targetSize.height);
       canvasContext.rotate(Math.PI);
       canvasContext.drawImage(frame, flippedRect.x, flippedRect.y, flippedRect.width, flippedRect.height);
       canvasContext.restore();
       return;
     }
     if (rotationDegrees === 270) {
       const rotatedRect = composeDrawRect(sourceSize.height, sourceSize.width, targetSize.height, targetSize.width);
       canvasContext.save();
       canvasContext.translate(0, targetSize.height);
       canvasContext.rotate(-Math.PI / 2);
       canvasContext.drawImage(frame, rotatedRect.x, rotatedRect.y, rotatedRect.width, rotatedRect.height);
       canvasContext.restore();
       return;
     }

     const fittedRect = composeDrawRect(sourceSize.width, sourceSize.height, targetSize.width, targetSize.height);
     canvasContext.drawImage(frame, fittedRect.x, fittedRect.y, fittedRect.width, fittedRect.height);
   };

   // Transcodes the stashed clip with the platform codecs: mp4box demuxes, VideoDecoder decodes, an OffscreenCanvas
   // rescales (and un-rotates), VideoEncoder re-encodes to H.264 and mp4-muxer writes the container. The AAC track
   // is copied through sample-for-sample — it is a tiny share of the bitrate and re-encoding it would only cost
   // quality and time. Returns a status so the caller can tell "this browser/file needs ffmpeg" apart from
   // "transcoded, but the result was not smaller", which ffmpeg would not improve on either.
   const compressStashedVideoWithWebCodecs = async (file, token, maxEdgePixels, quality, reporter, refitFrame) => {
     const unsupportedResult = { status: 'unsupported', sizeBytes: -1 };
     const cancelledResult = { status: 'cancelled', sizeBytes: -1 };

     let decoder = null;
     let encoder = null;
     let mp4boxFile = null;

     try {
       reporter.reportPhase(VIDEO_COMPRESS_PHASE_LOADING_TOOLS, 0, -1);
       await ensureVideoContainerToolsLoaded();
       reporter.reportPhase(VIDEO_COMPRESS_PHASE_LOADING_TOOLS, 100, -1);

       reporter.reportPhase(VIDEO_COMPRESS_PHASE_PREPARING, 0, -1);
       const sourceBuffer = await file.arrayBuffer();
       if (isVideoJobCancelled) {
         return cancelledResult;
       }

       let readyInfo = null;
       let demuxErrorMessage = null;
       mp4boxFile = window.MP4Box.createFile();
       mp4boxFile.onError = (message) => { demuxErrorMessage = message; };
       mp4boxFile.onReady = (info) => { readyInfo = info; };

       // The whole file is appended in one go, so onReady and the full sample list are available synchronously.
       // flush() is deliberately not called: it cleans up parsed buffers that getSample still needs to read from.
       sourceBuffer.fileStart = 0;
       mp4boxFile.appendBuffer(sourceBuffer);
       if (demuxErrorMessage || !readyInfo) {
         return unsupportedResult;
       }

       const videoTracks = readyInfo.videoTracks || [];
       if (videoTracks.length !== 1) {
         return unsupportedResult;
       }
       const videoTrack = videoTracks[0];
       if (!(videoTrack.nb_samples > 0) || !videoTrack.video) {
         return unsupportedResult;
       }
       const isAvcSource = videoTrack.codec.indexOf('avc1.') === 0;
       const isHevcSource = videoTrack.codec.indexOf('hvc1.') === 0 || videoTrack.codec.indexOf('hev1.') === 0;
       if (!isAvcSource && !isHevcSource) {
         return unsupportedResult;
       }

       // Audio rides through untouched, so the source has to be AAC-LC. The codec string cannot settle that on its
       // own — QuickTime .mov files, which the picker accepts, report a bare 'mp4a' with no profile suffix — so the
       // AudioSpecificConfig decides: its top 5 bits are the MPEG-4 audio object type, and 2 is AAC-LC. A track
       // with no readable config (MP3-in-MP4 carries none) goes to ffmpeg rather than being guessed at.
       const audioTracks = readyInfo.audioTracks || [];
       let audioTrack = null;
       let audioSpecificConfig = null;
       if (audioTracks.length > 0) {
         audioTrack = audioTracks[0];
         if (audioTrack.codec.indexOf('mp4a') !== 0) {
           return unsupportedResult;
         }
         if (!audioTrack.audio || !(audioTrack.audio.sample_rate > 0) || !(audioTrack.audio.channel_count > 0)) {
           return unsupportedResult;
         }
         audioSpecificConfig = readAacAudioSpecificConfig(mp4boxFile, audioTrack.id, sourceBuffer);
         if (!audioSpecificConfig || audioSpecificConfig.length === 0) {
           return unsupportedResult;
         }
         const audioObjectType = audioSpecificConfig[0] >> 3;
         if (audioObjectType !== VIDEO_WEBCODECS_AAC_LC_OBJECT_TYPE) {
           return unsupportedResult;
         }
       }

       const rotationDegrees = readTrackRotationDegrees(videoTrack);
       let displayWidth = videoTrack.video.width;
       let displayHeight = videoTrack.video.height;
       if (rotationDegrees === 90 || rotationDegrees === 270) {
         displayWidth = videoTrack.video.height;
         displayHeight = videoTrack.video.width;
       }
       // Optimising, the output frame is sized from the 9:16 crop rather than from the source: a portrait clip is
       // encoded at the shape the feed presents it in, so the cropped-away pixels never cost bitrate, and landscape
       // sources crop to nothing so their target is exactly what it was before.
       const sourceSize = { width: displayWidth, height: displayHeight };
       let targetSize = null;
       let isContainFit = false;
       if (refitFrame) {
         // A refit encodes into the exact box the caller asked for, so the source shape decides nothing here. The
         // fit is CONTAIN rather than cover: whatever the box does not match is barred, never cropped, because the
         // clip is one the author already composed. Callers size the box to the clip's own shape - the smallest
         // enlargement clearing a platform floor - so in practice the bar is a rounded pixel and nothing shows.
         // A cover refit is the exception: its caller asked for a different shape and for no bars.
         targetSize = { width: refitFrame.width, height: refitFrame.height };
         isContainFit = !refitFrame.isCoverFit;
       }
       else {
         const croppedSize = composePortraitCropSize(displayWidth, displayHeight);
         targetSize = computeTranscodeTargetSize(croppedSize.width, croppedSize.height, maxEdgePixels);
       }

       let frameRate = VIDEO_WEBCODECS_DEFAULT_FRAME_RATE;
       const trackSeconds = videoTrack.duration / videoTrack.timescale;
       if (trackSeconds > 0) {
         frameRate = videoTrack.nb_samples / trackSeconds;
       }
       if (!(frameRate > 0) || frameRate > VIDEO_WEBCODECS_MAX_FRAME_RATE) {
         frameRate = VIDEO_WEBCODECS_DEFAULT_FRAME_RATE;
       }

       const bitsPerPixel = computeBitsPerPixelForQuality(quality);
       let bitsPerSecond = Math.round(targetSize.width * targetSize.height * frameRate * bitsPerPixel);
       if (bitsPerSecond < VIDEO_WEBCODECS_MIN_BITS_PER_SECOND) {
         bitsPerSecond = VIDEO_WEBCODECS_MIN_BITS_PER_SECOND;
       }
       if (bitsPerSecond > VIDEO_WEBCODECS_MAX_BITS_PER_SECOND) {
         bitsPerSecond = VIDEO_WEBCODECS_MAX_BITS_PER_SECOND;
       }

       const decoderDescription = readVideoDecoderDescription(mp4boxFile, videoTrack.id);
       if (!decoderDescription) {
         return unsupportedResult;
       }
       const decoderConfiguration = {
         codec: videoTrack.codec,
         codedWidth: videoTrack.video.width,
         codedHeight: videoTrack.video.height,
         description: decoderDescription,
         hardwareAcceleration: 'no-preference',
         optimizeForLatency: false
       };
       const decoderSupport = await window.VideoDecoder.isConfigSupported(decoderConfiguration);
       if (!decoderSupport || !decoderSupport.supported) {
         return unsupportedResult;
       }

       const encoderConfiguration = await chooseVideoEncoderConfiguration(targetSize.width, targetSize.height, frameRate, bitsPerSecond);
       if (!encoderConfiguration) {
         return unsupportedResult;
       }

       const muxerOptions = {
         target: new window.Mp4Muxer.ArrayBufferTarget(),
         video: { codec: 'avc', width: targetSize.width, height: targetSize.height },
         fastStart: 'in-memory',
         firstTimestampBehavior: 'cross-track-offset'
       };
       let audioChunkMetadata = null;
       if (audioTrack) {
         muxerOptions.audio = {
           codec: 'aac',
           numberOfChannels: audioTrack.audio.channel_count,
           sampleRate: audioTrack.audio.sample_rate
         };
         audioChunkMetadata = {
           decoderConfig: {
             codec: VIDEO_WEBCODECS_AAC_LC_CODEC,
             numberOfChannels: audioTrack.audio.channel_count,
             sampleRate: audioTrack.audio.sample_rate,
             description: audioSpecificConfig
           }
         };
       }
       const muxer = new window.Mp4Muxer.Muxer(muxerOptions);

       const canvas = new window.OffscreenCanvas(targetSize.width, targetSize.height);
       const canvasContext = canvas.getContext('2d', { alpha: false });
       if (!canvasContext) {
         return unsupportedResult;
       }

       let codecErrorMessage = null;
       let encodedFrameCount = 0;
       encoder = new window.VideoEncoder({
         output: (chunk, metadata) => {
           muxer.addVideoChunk(chunk, metadata);
           encodedFrameCount++;
           reporter.reportEncodeProgress(encodedFrameCount / videoTrack.nb_samples);
         },
         error: (error) => { codecErrorMessage = String(error); }
       });
       encoder.configure(encoderConfiguration);

       const keyFrameInterval = Math.max(1, Math.round(frameRate * VIDEO_WEBCODECS_KEYFRAME_SECONDS));
       let decodedFrameCount = 0;
       decoder = new window.VideoDecoder({
         output: (frame) => {
           try {
             drawFrameIntoTarget(canvasContext, frame, sourceSize, targetSize, rotationDegrees, isContainFit);
             const scaledFrameInit = { timestamp: frame.timestamp };
             if (frame.duration > 0) {
               scaledFrameInit.duration = frame.duration;
             }
             const scaledFrame = new window.VideoFrame(canvas, scaledFrameInit);
             const isKeyFrame = (decodedFrameCount % keyFrameInterval) === 0;
             encoder.encode(scaledFrame, { keyFrame: isKeyFrame });
             scaledFrame.close();
             decodedFrameCount++;
           } catch (error) {
             codecErrorMessage = String(error);
           } finally {
             frame.close();
           }
         },
         error: (error) => { codecErrorMessage = String(error); }
       });
       decoder.configure(decoderConfiguration);

       // Video samples are queued and drained under backpressure so only a bounded slice of the clip is ever
       // held as decoded-but-unencoded work. Audio samples are handed straight to the muxer as they surface.
       const pendingVideoSamples = [];
       let emittedVideoSampleCount = 0;
       let emittedAudioSampleCount = 0;
       mp4boxFile.onSamples = (trackId, user, samples) => {
         if (samples.length === 0) {
           return;
         }
         if (trackId === videoTrack.id) {
           for (let index = 0; index < samples.length; index++) {
             pendingVideoSamples.push(samples[index]);
           }
           emittedVideoSampleCount = emittedVideoSampleCount + samples.length;
           if (pendingVideoSamples.length >= VIDEO_WEBCODECS_SAMPLE_HIGH_WATER) {
             mp4boxFile.stop();
           }
           return;
         }
         if (audioTrack && trackId === audioTrack.id) {
           for (let index = 0; index < samples.length; index++) {
             const sample = samples[index];
             const timestampMicroseconds = (sample.cts * MICROSECONDS_PER_SECOND) / sample.timescale;
             const durationMicroseconds = (sample.duration * MICROSECONDS_PER_SECOND) / sample.timescale;
             muxer.addAudioChunkRaw(sample.data, 'key', timestampMicroseconds, durationMicroseconds, audioChunkMetadata);
           }
           emittedAudioSampleCount = emittedAudioSampleCount + samples.length;
           mp4boxFile.releaseUsedSamples(audioTrack.id, samples[samples.length - 1].number + 1);
         }
       };

       mp4boxFile.setExtractionOptions(videoTrack.id, null, { nbSamples: VIDEO_WEBCODECS_VIDEO_BATCH_SAMPLES });
       if (audioTrack) {
         mp4boxFile.setExtractionOptions(audioTrack.id, null, { nbSamples: VIDEO_WEBCODECS_AUDIO_BATCH_SAMPLES });
       }

       reporter.startEncodeTimer();
       reporter.reportPhase(VIDEO_COMPRESS_PHASE_ENCODING, 0, -1);
       mp4boxFile.start();

       for (; ;) {
         if (isVideoJobCancelled) {
           return cancelledResult;
         }
         if (codecErrorMessage) {
           throw new Error(codecErrorMessage);
         }

         if (pendingVideoSamples.length === 0) {
           if (emittedVideoSampleCount >= videoTrack.nb_samples) {
             break;
           }
           // Resume the extractor that stopped itself at the high-water mark.
           mp4boxFile.start();
           if (pendingVideoSamples.length === 0) {
             break;
           }
         }

         const sample = pendingVideoSamples.shift();
         const chunkInit = {
           type: 'delta',
           timestamp: (sample.cts * MICROSECONDS_PER_SECOND) / sample.timescale,
           data: sample.data
         };
         if (sample.is_sync) {
           chunkInit.type = 'key';
         }
         if (sample.duration > 0) {
           chunkInit.duration = (sample.duration * MICROSECONDS_PER_SECOND) / sample.timescale;
         }
         decoder.decode(new window.EncodedVideoChunk(chunkInit));
         mp4boxFile.releaseUsedSamples(videoTrack.id, sample.number + 1);

         await waitForCodecQueue(() => { return decoder.decodeQueueSize + encoder.encodeQueueSize; });
       }

       if (isVideoJobCancelled) {
         return cancelledResult;
       }

       // The extractor serves the video track first, so a clip whose video ends on the high-water mark leaves its
       // audio tail unextracted — the video loop above would have exited before restarting it. Drop the video
       // track from the extractor (so it can no longer trip the high-water stop) and drain the audio tail.
       if (audioTrack) {
         mp4boxFile.unsetExtractionOptions(videoTrack.id);
       }
       while (audioTrack && emittedAudioSampleCount < audioTrack.nb_samples) {
         const audioSampleCountBefore = emittedAudioSampleCount;
         mp4boxFile.start();
         if (emittedAudioSampleCount === audioSampleCountBefore) {
           break;
         }
       }

       await decoder.flush();
       await encoder.flush();
       if (codecErrorMessage) {
         throw new Error(codecErrorMessage);
       }
       if (encodedFrameCount === 0) {
         return unsupportedResult;
       }
       muxer.finalize();

       const transcodedBlob = new Blob([muxer.target.buffer], { type: 'video/mp4' });
       if (!(transcodedBlob.size > 0)) {
         return unsupportedResult;
       }
       // A refit exists to change the FRAME, not the file size: upscaling to reach a platform's floor legitimately
       // grows the file, so only the optimise path holds out for a smaller result.
       if (!refitFrame && transcodedBlob.size >= file.size) {
         return { status: 'not-smaller', sizeBytes: -1 };
       }

       window.__pendingVideoUploads[token] = transcodedBlob;
       reporter.reportEncodeProgress(1);
       return { status: 'ok', sizeBytes: transcodedBlob.size };
     } catch (err) {
       console.error('compressStashedVideo: WebCodecs path failed, falling back to ffmpeg.wasm', err);
       return unsupportedResult;
     } finally {
       if (decoder && decoder.state !== 'closed') {
         try { decoder.close(); } catch (e) { }
       }
       if (encoder && encoder.state !== 'closed') {
         try { encoder.close(); } catch (e) { }
       }
       if (mp4boxFile) {
         try { mp4boxFile.stop(); } catch (e) { }
         mp4boxFile.onSamples = null;
       }
     }
   };

   // Fallback transcode for browsers without WebCodecs and for sources the WebCodecs path cannot handle
   // (unexpected codec, unreadable sample tables). Single-threaded and preceded by a 32 MB core download, so it
   // is slow by construction — planVideoProcessing charges both against the decision to optimise at all.
   const compressStashedVideoWithFfmpeg = async (file, token, maxEdgePixels, quality, reporter, refitFrame) => {
     let ffmpeg = null;
     const onFfmpegProgress = (event) => {
       reporter.reportEncodeProgress(event && event.progress);
     };

     try {
       reporter.reportPhase(VIDEO_COMPRESS_PHASE_LOADING_TOOLS, window.__videoToolsWarmPercent, -1);
       const warmPoll = setInterval(() => {
         reporter.reportPhase(VIDEO_COMPRESS_PHASE_LOADING_TOOLS, window.__videoToolsWarmPercent, -1);
       }, 500);
       try {
         await warmVideoToolsCache();
         ffmpeg = await ensureFfmpegLoaded();
       } finally {
         clearInterval(warmPoll);
       }

       reporter.reportPhase(VIDEO_COMPRESS_PHASE_PREPARING, 0, -1);
       const inputName = 'cin.mp4';
       const outputName = 'cout.mp4';

       const inputData = new Uint8Array(await file.arrayBuffer());
       await ffmpeg.writeFile(inputName, inputData);

       if (isVideoJobCancelled) {
         return -1;
       }

       reporter.startEncodeTimer();
       reporter.reportPhase(VIDEO_COMPRESS_PHASE_ENCODING, 0, -1);
       ffmpeg.on('progress', onFfmpegProgress);

       let videoFilterChain = '';
       if (refitFrame) {
         // The contain-and-pad the WebCodecs path draws, expressed as filters: scale to fit inside the frame keeping
         // the source ratio, then centre it in the exact frame over black. force_divisible_by=2 keeps both scaled
         // edges even for 4:2:0 chroma, and setsar=1 stops a non-square source SAR re-stretching the padded frame.
         const refitWidth = String(refitFrame.width);
         const refitHeight = String(refitFrame.height);
         const refitScaleFilter = "scale=w=" + refitWidth + ":h=" + refitHeight
           + ":force_original_aspect_ratio=decrease:force_divisible_by=2";
         const refitPadFilter = "pad=" + refitWidth + ":" + refitHeight + ":(ow-iw)/2:(oh-ih)/2:black";
         videoFilterChain = refitScaleFilter + "," + refitPadFilter + ",setsar=1";
         if (refitFrame.isCoverFit) {
           // Cover: scale until the frame is filled, then crop the overflow centred - no bars.
           const refitCoverScaleFilter = "scale=w=" + refitWidth + ":h=" + refitHeight
             + ":force_original_aspect_ratio=increase:force_divisible_by=2";
           const refitCropFilter = "crop=" + refitWidth + ":" + refitHeight;
           videoFilterChain = refitCoverScaleFilter + "," + refitCropFilter + ",setsar=1";
         }
       }
       else {
         // Same 9:16 normalisation composePortraitCropSize does on the WebCodecs path, expressed as an ffmpeg
         // expression because this path never learns the clip's dimensions on the JS side. gt(ih,iw) keeps it a
         // no-op for landscape and square sources; for portrait it takes the largest 9:16 rectangle that fits,
         // centered (crop defaults x/y to centre). trunc(../2)*2 keeps both edges even, which 4:2:0 chroma requires.
         const portraitCropWidth = "if(gt(ih,iw),trunc(min(iw,ih*9/16)/2)*2,iw)";
         const portraitCropHeight = "if(gt(ih,iw),trunc(min(ih,iw*16/9)/2)*2,ih)";
         const cropFilter = "crop=w='" + portraitCropWidth + "':h='" + portraitCropHeight + "'";
         const scaleFilter = "scale=w='if(gte(iw,ih),min(iw," + maxEdgePixels + "),-2)':h='if(gte(iw,ih),-2,min(ih," + maxEdgePixels + "))'";
         videoFilterChain = cropFilter + "," + scaleFilter;
       }

       await ffmpeg.exec([
         '-i', inputName,
         '-vf', videoFilterChain,
         '-c:v', 'libx264',
         '-preset', 'veryfast',
         '-crf', String(quality),
         '-c:a', 'aac',
         '-b:a', '96k',
         '-movflags', 'faststart',
         outputName
       ]);
       const data = await ffmpeg.readFile(outputName);

       try { await ffmpeg.deleteFile(inputName); } catch (e) { }
       try { await ffmpeg.deleteFile(outputName); } catch (e) { }

       const compressedBlob = new Blob([data], { type: 'video/mp4' });
       if (!(compressedBlob.size > 0)) {
         return -1;
       }
       // Same reasoning as the WebCodecs path: a refit is allowed to come back bigger, an optimise is not.
       if (!refitFrame && compressedBlob.size >= file.size) {
         return -1;
       }

       window.__pendingVideoUploads[token] = compressedBlob;
       reporter.reportEncodeProgress(1);
       return compressedBlob.size;
     } catch (err) {
       console.error('compressStashedVideo: ffmpeg.wasm path failed', err);
       return -1;
     } finally {
       if (ffmpeg) {
         try { ffmpeg.off('progress', onFfmpegProgress); } catch (e) { }
       }
     }
   };

   window.compressStashedVideo = async (token, maxEdgePixels, quality, dotNetRef, progressMethod) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return -1;
     }

     // cancelVideoJob leaves the flag raised and only uploadStashedVideo lowers it again, so without this a compress
     // that follows a cancelled job bails out on its first check and the clip goes up unoptimised, silently.
     isVideoJobCancelled = false;

     const reporter = createVideoCompressReporter(dotNetRef, progressMethod);

     if (isWebCodecsTranscodeSupported()) {
       const webCodecsResult = await compressStashedVideoWithWebCodecs(file, token, maxEdgePixels, quality, reporter, null);
       if (webCodecsResult.status === 'ok') {
         return webCodecsResult.sizeBytes;
       }
       // A clip the platform codecs already failed to shrink will not shrink under ffmpeg either, and the job
       // was cancelled means cancelled — neither is worth a second, far slower pass.
       if (webCodecsResult.status === 'not-smaller' || webCodecsResult.status === 'cancelled') {
         return -1;
       }
     }

     if (isVideoJobCancelled) {
       return -1;
     }

     const ffmpegSizeBytes = await compressStashedVideoWithFfmpeg(file, token, maxEdgePixels, quality, reporter, null);
     return ffmpegSizeBytes;
   };

   // The CRF a refit encodes at. Below the optimise baseline because a refit exists to make a clip acceptable, not to
   // make it smaller - and because upscaling to a platform floor is exactly where a thin bitrate shows as mush.
   const VIDEO_REFIT_QUALITY_CRF = 24;

   // Re-encodes the stashed clip into an EXACT frame, the inverse of the optimise path's "same shape, fewer pixels".
   // By default the clip is fitted WHOLE inside that frame and any remainder padded black, so nothing the author framed
   // is ever cropped away; a caller passing the clip's own shape scaled up therefore gets a clean enlargement and no
   // bars. isCoverFit fills the frame instead, cropping the overflow centred - for a frame of a different shape, where
   // padding would bake black bars into the published clip.
   // Returns the new byte size, or -1. The stash is replaced in place: clone it first if anything else shares it.
   window.refitStashedVideoToFrame = async (token, targetWidth, targetHeight, isCoverFit) => {
     const file = window.__pendingVideoUploads[token];
     if (!file) {
       return -1;
     }

     // 4:2:0 chroma needs both edges even, the same constraint computeTranscodeTargetSize enforces on the other path.
     const roundedWidth = Math.round(targetWidth);
     const roundedHeight = Math.round(targetHeight);
     const frameWidth = Math.max(2, roundedWidth - (roundedWidth % 2));
     const frameHeight = Math.max(2, roundedHeight - (roundedHeight % 2));
     const refitFrame = { width: frameWidth, height: frameHeight, isCoverFit: isCoverFit === true };

     // A refit the user just asked for is a new job, not the cancelled one whose flag is still raised.
     isVideoJobCancelled = false;

     // No progress target: the cross-post layout that offers the refit has no video-job indicator to drive, and the
     // button it sits on carries the spinner instead.
     const reporter = createVideoCompressReporter(null, null);

     if (isWebCodecsTranscodeSupported()) {
       const webCodecsResult = await compressStashedVideoWithWebCodecs(file, token, 0, VIDEO_REFIT_QUALITY_CRF, reporter, refitFrame);
       if (webCodecsResult.status === 'ok') {
         return webCodecsResult.sizeBytes;
       }
       // Cancelled means cancelled; every other verdict is "these codecs could not do it", which ffmpeg may still.
       // 'not-smaller' cannot occur here - the refit path does not apply that rule.
       if (webCodecsResult.status === 'cancelled') {
         return -1;
       }
     }

     if (isVideoJobCancelled) {
       return -1;
     }

     const ffmpegSizeBytes = await compressStashedVideoWithFfmpeg(file, token, 0, VIDEO_REFIT_QUALITY_CRF, reporter, refitFrame);
     return ffmpegSizeBytes;
   };

   // ── Video orientation / aspect ──────────────────────────────────────────────────────────────────────────
   // A feed clip is shown in a box that follows its own frame instead of a fixed 16:9 one. A landscape clip stays
   // width-driven as before; a portrait clip gets the TikTok / Reels treatment — a tall box driven by a height cap,
   // centered, with the card's black gutters either side instead of pillarbox bars around a letterboxed strip.

   // EVERY portrait clip is shown in an exact 9:16 frame — the TikTok canvas — whatever it was shot at, so the feed
   // reads as one column of identically-shaped vertical videos instead of a ragged mix of 9:16, 4:5 and 3:4.
   // object-fit: cover absorbs whatever the source is taller or narrower than that. Landscape and square clips
   // keep their own frame; only vertical video is normalised.
   const VIDEO_ASPECT_PORTRAIT = 9 / 16;
   // Widest box allowed. Guards against a banner-shaped clip collapsing the card to a few pixels of height.
   const VIDEO_ASPECT_LANDSCAPE_MAXIMUM = 3;
   const VIDEO_ASPECT_CONTAINER_SELECTOR = '.video-player-container';
   const VIDEO_ASPECT_PORTRAIT_CLASS = 'tk-video-portrait';
   const VIDEO_ASPECT_VARIABLE = '--tk-video-aspect';
   const VIDEO_FLOAT_FRAME_CLASS = 'tk-video-float-frame';
   const VIDEO_FLOAT_FRAME_PORTRAIT_CLASS = 'tk-video-float-frame tk-video-float-frame-portrait';
   const MEDIA_CAROUSEL_WRAP_SELECTOR = '.media-carousel-wrap';
   const MEDIA_CAROUSEL_CLIP_ASPECT_VARIABLE = '--tk-media-clip-aspect';

   // Reads the natural frame size off whichever element carries it: the <video> once its metadata lands, or the
   // poster <img>, which is a still of the same frame and normally resolves first (it is what the card paints).
   const readMediaFrameSize = (element) => {
     if (!element) {
       return null;
     }
     if (element.videoWidth > 0 && element.videoHeight > 0) {
       return { width: element.videoWidth, height: element.videoHeight };
     }
     if (element.naturalWidth > 0 && element.naturalHeight > 0) {
       return { width: element.naturalWidth, height: element.naturalHeight };
     }
     return null;
   };

   // Wired from the markup (VideoPlayer.razor) on the poster's onload and on the video's onloadedmetadata. Both
   // describe the same clip, so whichever fires last just rewrites the same value — the video wins on a mismatch.
   window.tkApplyVideoAspect = (element) => {
     const frameSize = readMediaFrameSize(element);
     if (!frameSize) {
       return;
     }

     const container = element.closest(VIDEO_ASPECT_CONTAINER_SELECTOR);
     if (!container) {
       return;
     }

     const isPortrait = frameSize.height > frameSize.width;

     // Portrait snaps to the 9:16 canvas outright — that is what makes an already-uploaded 4:5 or 3:4 clip present
     // as a TikTok video without re-encoding a thing. Landscape keeps its own ratio but is still capped, so a
     // banner-shaped clip cannot collapse the card to a few pixels of height. object-fit takes the overflow.
     let aspect = frameSize.width / frameSize.height;
     if (isPortrait) {
       aspect = VIDEO_ASPECT_PORTRAIT;
     }
     if (aspect > VIDEO_ASPECT_LANDSCAPE_MAXIMUM) {
       aspect = VIDEO_ASPECT_LANDSCAPE_MAXIMUM;
     }

     container.style.setProperty(VIDEO_ASPECT_VARIABLE, aspect.toString());
     container.classList.toggle(VIDEO_ASPECT_PORTRAIT_CLASS, isPortrait);

     const carouselWrap = container.closest(MEDIA_CAROUSEL_WRAP_SELECTOR);
     if (carouselWrap) {
       window.tkSyncMediaCarouselAspect(carouselWrap);
     }
   };

   // A post's carousel (SocialFiPostExtraPictures.razor) takes the frame of the clip on show (App.css,
   // .media-carousel-wrap). Called when a clip's frame is measured, and from the wrap's onanimationstart on every
   // slide move: a clip moved back to from the clip beside it is still mounted, so no poster onload re-measures it.
   window.tkSyncMediaCarouselAspect = (carouselWrap) => {
     const activeClipContainer = carouselWrap.querySelector('.mud-carousel-item:not(.mud-carousel-item-exit) ' + VIDEO_ASPECT_CONTAINER_SELECTOR);
     if (!activeClipContainer) {
       return;
     }

     const clipAspect = activeClipContainer.style.getPropertyValue(VIDEO_ASPECT_VARIABLE);
     if (!clipAspect) {
       return;
     }

     carouselWrap.style.setProperty(MEDIA_CAROUSEL_CLIP_ASPECT_VARIABLE, clipAspect);
   };

   // The markup's poster onload and video onloadedmetadata call the function above, and either can fire before this
   // bundle has run - a cached poster on a page that parses quickly does - in which case the markup's guard skips the
   // call and the card would keep the default aspect. Apply it once here to every player whose media already has a
   // size; the function itself ignores an element that has none yet, and the video wins over its poster as above.
   document.querySelectorAll(VIDEO_ASPECT_CONTAINER_SELECTOR + " img.poster-image, " + VIDEO_ASPECT_CONTAINER_SELECTOR + " video")
     .forEach(window.tkApplyVideoAspect);

   // Class list for the floating mini-player frame. The frame is built in <body>, outside the player's container,
   // so its shape is resolved from the video element itself — the only thing that travels with it when it detaches.
   const composeVideoFloatFrameClass = (videoElement) => {
     const frameSize = readMediaFrameSize(videoElement);
     if (frameSize && frameSize.height > frameSize.width) {
       return VIDEO_FLOAT_FRAME_PORTRAIT_CLASS;
     }
     return VIDEO_FLOAT_FRAME_CLASS;
   };

   // In-page floating mini-player: native OS PiP needs a user gesture, and a scroll isn't one.
   window.initScrollPictureInPicture = (videoElement, closeLabel) => {
     if (!videoElement) {
       return;
     }
     if (videoElement._pipScrollWired) {
       return;
     }
     videoElement._pipScrollWired = true;

     // The wrapper holds the reserved inline space the video leaves behind while floating.
     const wrapper = videoElement.closest('.video-wrapper') || videoElement.parentElement;
     let floatFrame = null;

     const floatVideo = () => {
       if (floatFrame) {
         return;
       }
       // Only float a clip the user is watching, and never one already in the native OS PiP window.
       if (videoElement.paused || videoElement.ended) {
         return;
       }
       if (document.pictureInPictureElement === videoElement) {
         return;
       }

       floatFrame = document.createElement('div');
       floatFrame.className = composeVideoFloatFrameClass(videoElement);

       const closeButton = document.createElement('button');
       closeButton.type = 'button';
       closeButton.className = 'tk-video-float-frame-close';
       closeButton.setAttribute('aria-label', closeLabel || 'Close');
       closeButton.title = closeLabel || 'Close';
       closeButton.textContent = '✕';
       // Closing pauses and docks; floatVideo bails on a paused clip, so it cannot immediately re-float.
       closeButton.addEventListener('click', (e) => {
         e.preventDefault();
         e.stopPropagation();
         try { videoElement.pause(); } catch (err) { }
         dockVideo();
       });

       // Append to body, not the feed card: an ancestor transform/filter/overflow would break position:fixed.
       floatFrame.appendChild(videoElement);
       floatFrame.appendChild(closeButton);
       document.body.appendChild(floatFrame);
     };

     const dockVideo = () => {
       if (!floatFrame) {
         return;
       }
       // Back as the wrapper's first child so the inline sibling rules (PiP button / placeholder) line up again.
       if (wrapper) {
         wrapper.insertBefore(videoElement, wrapper.firstChild);
       }
       floatFrame.remove();
       floatFrame = null;
     };

     // Observe the WRAPPER, not the video: a floating video sits in the viewport and would dock/re-float forever.
     const anchor = wrapper || videoElement;

     // threshold 0: float only once the inline spot is fully scrolled away, dock the moment it reappears.
     const observer = new IntersectionObserver((entries) => {
       for (let i = 0; i < entries.length; i++) {
         const entry = entries[i];
         if (!entry.isIntersecting) {
           floatVideo();
         } else {
           dockVideo();
         }
       }
     }, { threshold: 0 });

     observer.observe(anchor);

     // Listened here, not just on our button, so PiP opened from the browser's own controls also marks the element.
     const onEnterPip = () => { videoElement.classList.add('tk-video-in-pip'); };
     const onLeavePip = () => { videoElement.classList.remove('tk-video-in-pip'); };
     videoElement.addEventListener('enterpictureinpicture', onEnterPip);
     videoElement.addEventListener('leavepictureinpicture', onLeavePip);

     const onPause = () => {
       if (videoElement.ended) {
         return;
       }
       dockVideo();
     };
     videoElement.addEventListener('pause', onPause);

     // Dock directly in JS when the clip ends: onPause above deliberately skips ended clips, so without this the
     // fixed float frame's removal would depend entirely on the OnVideoEndedAsync circuit round-trip — a busy or
     // dropped circuit left the frame orphaned over the page, swallowing clicks. The round-trip still restores
     // the poster when it lands.
     const onEnded = () => {
       dockVideo();
     };
     videoElement.addEventListener('ended', onEnded);

     videoElement._pipScrollCleanup = () => {
       observer.disconnect();
       // Dock before Blazor tears the component down, else the video is orphaned in <body> and keeps playing.
       dockVideo();
       videoElement.removeEventListener('pause', onPause);
       videoElement.removeEventListener('ended', onEnded);
       videoElement.removeEventListener('enterpictureinpicture', onEnterPip);
       videoElement.removeEventListener('leavepictureinpicture', onLeavePip);
       videoElement.classList.remove('tk-video-in-pip');
       videoElement._pipScrollWired = false;
       videoElement._pipScrollCleanup = null;
     };
   };

   window.disposeScrollPictureInPicture = (videoElement) => {
     if (videoElement && videoElement._pipScrollCleanup) {
       try { videoElement._pipScrollCleanup(); } catch (e) { }
     }
   };

   // Closes the native OS PiP window, returning the video to its inline spot. exitPictureInPicture() needs no
   // user gesture, so this is safe from a Blazor @onclick (the inline "playing in picture-in-picture"
   // placeholder).
   window.exitNativePictureInPicture = (videoElement) => {
     if (videoElement && document.pictureInPictureElement === videoElement) {
       document.exitPictureInPicture().catch(() => { });
     }
   };

   // Wires the "Open PiP" button to the native OS Picture-in-Picture window. requestPictureInPicture() requires
   // a transient user gesture, and in Blazor Server a C# @onclick round-trips through the server and loses that
   // activation — so the listener is attached here in pure JS and fires synchronously inside the real click.
   // The button is hidden where the standard PiP API is unavailable (e.g. iOS Safari). Idempotent per button.
   window.wireNativePipButton = (button, videoElement) => {
     if (!button || !videoElement) {
       return;
     }
     if (!document.pictureInPictureEnabled || videoElement.disablePictureInPicture) {
       button.style.display = 'none';
       return;
     }
     if (button._pipWired) {
       return;
     }
     button._pipWired = true;

     button.addEventListener('click', async (e) => {
       e.preventDefault();
       e.stopPropagation();
       try {
         if (document.pictureInPictureElement === videoElement) {
           await document.exitPictureInPicture();
         } else {
           await videoElement.requestPictureInPicture();
         }
       } catch (err) {
         // e.g. NotAllowedError — leave the video inline.
       }
     });
   };

   // Existence / decodability probe for a feed clip. A video removed from the CDN (or an undecodable file) must
   // show the host's "no longer available" placeholder instead of a dead play button. Blazor can't wire the
   // <video> element's non-bubbling `error` event (its @onerror never fires), and the real player element only
   // mounts on play, so this loads just the metadata through a throwaway detached <video>. Media elements load
   // cross-origin without CORS, so this works against the CDN host. It calls back into .NET ONLY on failure
   // (success stays silent) and releases the probe either way; guarded so it settles at most once.
   window.probeVideoExists = (url, dotNetRef, methodName) => {
     if (!url || !dotNetRef || !methodName) {
       return;
     }

     const probe = document.createElement('video');
     probe.preload = 'metadata';
     probe.muted = true;
     let settled = false;

     const release = () => {
       probe.removeAttribute('src');
       try {
         probe.load();
       } catch (e) {
       }
     };

     probe.addEventListener('loadedmetadata', () => {
       if (settled) {
         return;
       }
       settled = true;
       release();
     });

     probe.addEventListener('error', () => {
       if (settled) {
         return;
       }
       settled = true;
       release();
       try {
         dotNetRef.invokeMethodAsync(methodName);
       } catch (e) {
       }
     });

     probe.src = url;
     probe.load();
   };

   // Starts playback on a feed <video> element. Shows native controls, attaches a one-time error listener
   // (guarded so a re-render doesn't stack duplicates), then calls play() - unless deferPlayToCoordinator says the
   // clip is a feed autoplay start, which tkAutoplayFeed starts itself. Invoked from VideoPlayer.razor.
   window.playVideo = (videoElement, previewLimitSeconds, dotNetRef, muted, loop, deferPlayToCoordinator) => {
     // isPlayableMediaElement — declared above with the igp* helpers — covers null AND the truthy-but-not-an-element
     // case its comment describes, so it is the whole entry guard: an unpopulated ElementReference arrives as a plain
     // object, silently absorbs the property writes below, and only fails on .play(). Here that TypeError travelled
     // back into .NET, out of OnAfterRenderAsync and into the top-level ErrorBoundary, which replaced the whole routed
     // page with the 500 error page. VideoPlayer no longer calls in without a mounted element; this is what keeps any
     // future caller's mistake a dead player instead of a dead page.
     if (!isPlayableMediaElement(videoElement)) {
       console.error('playVideo: the reference did not resolve to a <video> element', videoElement);
       return;
     }

     videoElement.controls = true;

     // Apply muted/loop here (not only via the Blazor attribute) so muted autoplay is reliable — browsers block
     // unmuted programmatic playback, and the reflected `muted` attribute doesn't always stick through render.
     if (typeof muted === 'boolean') {
       videoElement.muted = muted;
     }
     if (typeof loop === 'boolean') {
       videoElement.loop = loop;
     }

     if (videoElement.dataset && !videoElement.dataset.listenerAdded) {
       videoElement.addEventListener('error', (e) => {
         console.error('Video error:', e);
       });

       // On end (non-looping clips only), tell Blazor to drop back to the poster so the finished clip shows its
       // cover image again instead of freezing on the last frame.
       videoElement.addEventListener('ended', () => {
         if (dotNetRef && !videoElement.loop) {
           dotNetRef.invokeMethodAsync('OnVideoEndedAsync');
         }
       });

       videoElement.dataset.listenerAdded = 'true';
     }

     // Guest preview gating: an unauthenticated viewer may watch only previewLimitSeconds before playback is
     // pinned. Once the limit is reached we pause the clip and call back into Blazor (OnPreviewLimitReachedAsync)
     // so it raises the join prompt. Wired once per element. A resume attempt past the limit pauses and re-prompts
     // (the dialog host guards against stacking), so the clip can never run past the preview window.
     const hasPreviewLimit = typeof previewLimitSeconds === 'number' && previewLimitSeconds > 0 && dotNetRef;
     if (hasPreviewLimit && videoElement.dataset && !videoElement.dataset.previewGateAdded) {
       videoElement.dataset.previewGateAdded = 'true';

       videoElement.addEventListener('timeupdate', () => {
         if (videoElement.currentTime < previewLimitSeconds) {
           return;
         }

         videoElement.pause();

         // Latch so the burst of timeupdate events around the pause raises the prompt only once; it's cleared on
         // the next play (below) so a resume attempt past the limit re-prompts.
         if (!videoElement.dataset.previewPrompted) {
           videoElement.dataset.previewPrompted = 'true';
           dotNetRef.invokeMethodAsync('OnPreviewLimitReachedAsync');
         }
       });

       videoElement.addEventListener('play', () => {
         delete videoElement.dataset.previewPrompted;
       });
     }

     // A feed autoplay start does not play here (TKR-12377). The caller hands the clip to tkAutoplayFeed next, and
     // its evaluate() plays only the clip most in view; starting every clip as it mounted had each of them buffer
     // its first few MB before the coordinator paused all but one. A start the viewer asked for plays at once.
     if (deferPlayToCoordinator === true) {
       return;
     }

     videoElement.play()
       .then(() => console.log('Video playing successfully'))
       .catch((e) => console.error('Error playing video:', e));
   };

   // ── Autoplay feed coordinator ───────────────────────────────────────────────────────────────────────────
   // Only ONE feed clip plays at a time: the one whose wrapper is most in view. Scrolled away, it floats into the
   // corner mini-player on a desktop and pauses on a mobile surface or in the app; the caller decides which, by
   // user agent, and passes it to register.
   window.tkAutoplayFeed = (function () {
     const items = [];                 // { video, wrapper, ratio, closeLabel, floatsWhenScrolledAway, floatFrame, userPaused, suppressPauseEvent, onPause, onPlay, onEnded }
     let activeVideo = null;
     let observer = null;
     let rafScheduled = false;

     const findItem = (video) => {
       for (let i = 0; i < items.length; i++) {
         if (items[i].video === video) {
           return items[i];
         }
       }
       return null;
     };

     const ensureObserver = () => {
       if (observer) {
         return;
       }
       // Several thresholds so the visibility ratio tracks smoothly as a clip scrolls through the viewport.
       const thresholds = [0, 0.25, 0.5, 0.6, 0.75, 1];
       observer = new IntersectionObserver((entries) => {
         for (let i = 0; i < entries.length; i++) {
           const entry = entries[i];
           for (let j = 0; j < items.length; j++) {
             if (items[j].wrapper !== entry.target) {
               continue;
             }

             const item = items[j];
             let ratio = 0;
             if (entry.isIntersecting) {
               ratio = entry.intersectionRatio;
             }
             item.ratio = ratio;

             if (ratio === 0 && item.userPaused) {
               item.userPaused = false;
             }
             break;
           }
         }
         schedule();
       }, { threshold: thresholds });
     };

     // Coalesce bursts of intersection callbacks into one evaluate() per frame.
     const schedule = () => {
       if (rafScheduled) {
         return;
       }
       rafScheduled = true;
       window.requestAnimationFrame(() => {
         rafScheduled = false;
         evaluate();
       });
     };

     const floatVideo = (item) => {
       if (item.floatFrame) {
         return;
       }
       if (item.video.paused || item.video.ended) {
         return;
       }
       if (document.pictureInPictureElement === item.video) {
         return;
       }

       const frame = document.createElement('div');
       frame.className = composeVideoFloatFrameClass(item.video);

       const closeButton = document.createElement('button');
       closeButton.type = 'button';
       closeButton.className = 'tk-video-float-frame-close';
       closeButton.setAttribute('aria-label', item.closeLabel || 'Close');
       closeButton.title = item.closeLabel || 'Close';
       closeButton.textContent = '✕';
       closeButton.addEventListener('click', (e) => {
         e.preventDefault();
         e.stopPropagation();
         try { item.video.pause(); } catch (err) { }
         dockVideo(item);
         if (activeVideo === item.video) {
           activeVideo = null;
         }
       });

       // Append to body, not the feed card: an ancestor transform/filter/overflow would break position:fixed.
       frame.appendChild(item.video);
       frame.appendChild(closeButton);
       document.body.appendChild(frame);
       item.floatFrame = frame;
     };

     const dockVideo = (item) => {
       if (!item.floatFrame) {
         return;
       }
       // Back as the wrapper's first child so the inline sibling rules (PiP button / placeholder) line up again.
       if (item.wrapper) {
         item.wrapper.insertBefore(item.video, item.wrapper.firstChild);
       }
       item.floatFrame.remove();
       item.floatFrame = null;
     };

     const playItem = (item) => {
       dockVideo(item);
       if (item.userPaused) {
         return;
       }
       // Only on a (re)start: muting is what autoplay policy needs for play() to be allowed on a paused clip.
       // Doing it unconditionally re-muted a clip the viewer had unmuted from the native controls, on every
       // evaluate() the scroll produced.
       if (item.video.paused) {
         item.video.muted = true;
       }
       const playPromise = item.video.play();
       if (playPromise && playPromise.catch) {
         playPromise.catch(() => { });
       }
     };

     const pauseItem = (item) => {
       try {
         if (!item.video.paused) {
           item.suppressPauseEvent = true;
           item.video.pause();
         }
       } catch (e) {
         item.suppressPauseEvent = false;
       }
       dockVideo(item);
     };

     const evaluate = () => {
       // Require a solid majority in view, else the active clip thrashes between two half-visible neighbours.
       let best = null;
       for (let i = 0; i < items.length; i++) {
         const item = items[i];
         if (item.ratio >= 0.6) {
           if (!best || item.ratio > best.ratio) {
             best = item;
           }
         }
       }

       if (best) {
         if (activeVideo !== best.video) {
           // Switch: pause + dock the previously active clip (removing its float), then play the new one.
           const previous = findItem(activeVideo);
           if (previous) {
             pauseItem(previous);
           }
           activeVideo = best.video;
         }
         playItem(best);

         // Belt-and-braces: pause any other registered clip that somehow ended up playing.
         for (let i = 0; i < items.length; i++) {
           if (items[i].video !== activeVideo && !items[i].video.paused) {
             pauseItem(items[i]);
           }
         }
         return;
       }

       // Mid-scroll between clips. On a desktop keep the active clip playing and float it until the next clip
       // takes over. On a mobile surface or in the app pause it instead (TKR-12377): a floated clip keeps streaming
       // the rest of its file for as long as the viewer scrolls - a staff clip can be 300 MB - and the viewer never
       // asked for this one to play. It stays the active clip, so scrolling back to it resumes where it stopped.
       const active = findItem(activeVideo);
       if (!active || active.video.paused) {
         return;
       }
       if (active.floatsWhenScrolledAway) {
         floatVideo(active);
         return;
       }
       pauseItem(active);
     };

     return {
       register: function (video, closeLabel, floatsWhenScrolledAway) {
         if (!video || findItem(video)) {
           return;
         }
         const wrapper = video.closest('.video-wrapper') || video.parentElement;
         const item = {
           video: video,
           wrapper: wrapper,
           ratio: 0,
           closeLabel: closeLabel,
           floatsWhenScrolledAway: floatsWhenScrolledAway === true,
           floatFrame: null,
           userPaused: false,
           suppressPauseEvent: false,
           onPause: null,
           onPlay: null,
           onEnded: null
         };

         item.onPause = () => {
           if (item.suppressPauseEvent) {
             item.suppressPauseEvent = false;
             return;
           }
           if (item.video.ended) {
             return;
           }

           item.userPaused = true;
           dockVideo(item);
           if (activeVideo === item.video) {
             activeVideo = null;
           }
           schedule();
         };

         item.onPlay = () => {
           item.userPaused = false;
         };

         // Dock directly in JS when the clip ends: onPause above deliberately skips ended clips, so without this
         // the fixed float frame's removal would depend entirely on the OnVideoEndedAsync circuit round-trip — a
         // busy or dropped circuit left the frame orphaned over the page, swallowing clicks. The round-trip still
         // restores the poster and unregisters the item when it lands.
         item.onEnded = () => {
           dockVideo(item);
           // The item stays registered until the round-trip unregisters it; without this flag the next evaluate()
           // could pick the ended clip as best and playItem() would restart it from zero. A real user replay fires
           // 'play', which clears the flag via onPlay.
           item.userPaused = true;
           if (activeVideo === item.video) {
             activeVideo = null;
           }
           // No schedule() here: unregister already schedules when the round-trip lands, and forcing an immediate
           // re-evaluate at end-time buys nothing.
         };

         video.addEventListener('pause', item.onPause);
         video.addEventListener('play', item.onPlay);
         video.addEventListener('ended', item.onEnded);

         items.push(item);
         ensureObserver();
         if (wrapper) {
           observer.observe(wrapper);
         }
         schedule();
       },
       unregister: function (video) {
         const item = findItem(video);
         if (!item) {
           return;
         }
         // Dock before Blazor tears the component down, else the video is orphaned in <body> and keeps playing.
         dockVideo(item);
         video.removeEventListener('pause', item.onPause);
         video.removeEventListener('play', item.onPlay);
         video.removeEventListener('ended', item.onEnded);
         if (observer && item.wrapper) {
           observer.unobserve(item.wrapper);
         }
         const index = items.indexOf(item);
         if (index >= 0) {
           items.splice(index, 1);
         }
         if (activeVideo === video) {
           activeVideo = null;
         }
         schedule();
       }
     };
   })();

   window.httpGet = async (url) => {
     const fetchCallback = async () => {
       const requestInit = {
         method: 'GET',
         headers: {
           'Is-Corproate-State': window.isCorporate() ? 'true' : 'false',
           'Is-Corporate-State': window.isCorporate() ? 'true' : 'false',
           'tokero-version': 'V5'
         },
         credentials: 'include',
       };
       const response = await fetch(url, requestInit);

       return response;
     };

     const httpWrapperResult = await window.httpWrapper(fetchCallback);
     return httpWrapperResult;
   }

   window.httpPost = async (url, input) => {
     const fetchCallback = async () => {
       const requestInit = {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Is-Corproate-State': window.isCorporate() ? 'true' : 'false',
           'Is-Corporate-State': window.isCorporate() ? 'true' : 'false',
           'tokero-version': 'V5'
         },
         credentials: 'include',
       };
       if (input) {
         requestInit.body = JSON.stringify(input);
       }

       const response = await fetch(url, requestInit);

       return response;
     };

     const httpWrapperResult = await window.httpWrapper(fetchCallback);
     return httpWrapperResult;
   }

   window.isCorporate = function() {
     const urlPath = window.location.pathname;
     const segments = urlPath.split('/').filter(Boolean);

     return segments.length >= 3 && segments[1] === 'corporate';
   }

   window.getUserAgent = () => {
     return window.navigator.userAgent;
   };

    window.copyToClipboard = async function (text, dotNetHelper, delayMS) {
       try {
           await navigator.clipboard.writeText(text);

           if (dotNetHelper && delayMS > 0) {
               setTimeout(() => {
                   dotNetHelper.invokeMethodAsync("OnCopyCompleted");
               }, delayMS);
           }
       } catch (err) {
           console.error("Clipboard copy failed", err);
       }
   };

   // The lightbox library loads on first use, not with the page (TKR-12377). fancybox.umd.min.js (27 KiB) and its
   // stylesheet used to come down on every circuit's first interactive render, lightbox or not - the signed-out
   // homepage has no lightbox element at all and still fetched both. Now initializeFancybox installs four document
   // listeners and nothing else: the first pointer, touch or focus that reaches a lightbox element starts the load,
   // so the library is normally bound by the time the click lands, and a click that arrives before it is bound is
   // held and replayed once the load has settled. One load per document, as before, kept on window.
   const lightboxTriggerSelector = ".lightbox, [data-fancybox], [data-fancybox-trigger]";

   // The lightbox element under an event target, or null. A text node - an event can be fired on one - has no closest().
   function findLightboxTrigger(eventTarget) {
     if (!(eventTarget instanceof Element)) {
       return null;
     }
     const lightboxTrigger = eventTarget.closest(lightboxTriggerSelector);
     return lightboxTrigger;
   }

   // Binds the lightbox once window.Fancybox exists. Split out of loadFancybox so the loader can call it after the
   // script has arrived without re-entering itself. Once per document: Fancybox delegates from document.body, so the
   // one binding serves every lightbox element rendered later, and _tokeroFancyboxBound is what the click listener
   // below steps aside on.
   function bindFancyboxLightbox() {
     // TKR-10418: load the lightbox stylesheet on demand, only when fancybox actually binds. This
     // keeps it off the critical path entirely and skips the download completely on any circuit
     // that never wires a lightbox. Injected once into <head>, where it persists for the whole
     // circuit and styles any lightbox opened later. Guarded by id so repeat calls are no-ops.
     if (!document.getElementById("fancybox-css")) {
       const fancyboxCss = document.createElement("link");
       fancyboxCss.id = "fancybox-css";
       fancyboxCss.rel = "stylesheet";
       fancyboxCss.href = "https://cdn.tokero.com/content/utils/lightbox/fancybox.min.css";
       document.head.appendChild(fancyboxCss);
     }
     // TKR-10603: users read the default plain magnifier as "search" — put a plus in the lens on the Toolbar
     // plugin defaults so both binding paths inherit it; guarded so a future bundle swap degrades to the old icon.
     if (Fancybox.Plugins?.Toolbar?.defaults?.items?.zoom) {
       Fancybox.Plugins.Toolbar.defaults.items.zoom.html =
         '<svg viewBox="0 0 24 24"><circle cx="10" cy="10" r="7"></circle><path d="M10 7v6M7 10h6"></path><path d="M16 16 L21 21"></path></svg>';
     }
     Fancybox.bind(".lightbox");
     window._tokeroFancyboxBound = true;
   }

   // Loads the script once per document and binds the lightbox when it arrives. Resolves once the lightbox is bound,
   // or once the load has settled without a usable library - a failed fetch, or a filtered network answering a block
   // page - and never stays pending or rejects: a held click is released on it either way. One load promise per
   // document, kept on window: a call while the load is in flight waits on it, and a call after it has settled gets
   // the same settled answer instead of attaching listeners to an element that will never fire again.
   function loadFancybox() {
     if (window.Fancybox) {
       if (!window._tokeroFancyboxBound) {
         bindFancyboxLightbox();
       }
       return Promise.resolve();
     }

     if (!window._tokeroFancyboxScriptLoad) {
       window._tokeroFancyboxScriptLoad = new Promise((resolve) => {
         const fancyboxScript = document.createElement("script");
         fancyboxScript.id = "fancybox-js";
         fancyboxScript.src = "https://cdn.tokero.com/content/utils/lightbox/fancybox.umd.min.js";
         fancyboxScript.addEventListener("load", resolve, { once: true });
         fancyboxScript.addEventListener("error", resolve, { once: true });
         document.head.appendChild(fancyboxScript);
       });
     }

     const fancyboxReady = window._tokeroFancyboxScriptLoad.then(() => {
       if (window.Fancybox && !window._tokeroFancyboxBound) {
         bindFancyboxLightbox();
       }
     });
     return fancyboxReady;
   }

   // Starts the load on the first sign that a lightbox is about to be used - a pointer over it, a touch on it, focus
   // on it - so the click that follows usually finds the library bound. Passive and in the capture phase: it costs
   // the event nothing and runs whether or not a handler on the element stops propagation. Nothing left to do once
   // the load has started.
   function prefetchFancyboxForLightbox(event) {
     if (window._tokeroFancyboxScriptLoad || window.Fancybox) {
       return;
     }
     const lightboxTrigger = findLightboxTrigger(event.target);
     if (!lightboxTrigger) {
       return;
     }
     loadFancybox();
   }

   // A click on a lightbox element before the lightbox is bound is held - prevented, so the browser does not follow
   // an <a> to the bare image meanwhile, and stopped, so the page's handlers see one click and not two - and replayed
   // on the element once the load has settled. Bound, Fancybox's own delegated listener opens the lightbox from the
   // replay as it would have from the click; not bound (the load failed), the replay runs the element's native
   // behaviour instead, which for an <a> is the image itself. The flag keeps this listener out of the replay's way
   // in that second case; in the first, _tokeroFancyboxBound already does. A modified click - another button, ctrl,
   // shift, meta - is left to the browser, as Fancybox leaves it.
   let isReplayingLightboxClick = false;

   function holdLightboxClickUntilBound(event) {
     if (window._tokeroFancyboxBound || isReplayingLightboxClick) {
       return;
     }
     if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) {
       return;
     }
     const lightboxTrigger = findLightboxTrigger(event.target);
     if (!lightboxTrigger) {
       return;
     }

     event.preventDefault();
     event.stopPropagation();
     loadFancybox().then(() => {
       isReplayingLightboxClick = true;
       try {
         lightboxTrigger.click();
       } finally {
         isReplayingLightboxClick = false;
       }
     });
   }

   // Installs the on-demand loader above, once per document, and settles at once. The layout awaits this on its first
   // interactive render right before it sets window.blazorReady, the readiness signal the Playwright suite clicks on;
   // nothing here waits on the network any more, and the suite's lightbox click goes through
   // holdLightboxClickUntilBound like anyone's first click.
   window.initializeFancybox = () => {
     if (!window._tokeroFancyboxOnDemandInstalled) {
       window._tokeroFancyboxOnDemandInstalled = true;
       document.addEventListener("pointerover", prefetchFancyboxForLightbox, { capture: true, passive: true });
       document.addEventListener("touchstart", prefetchFancyboxForLightbox, { capture: true, passive: true });
       document.addEventListener("focusin", prefetchFancyboxForLightbox, { capture: true, passive: true });
       document.addEventListener("click", holdLightboxClickUntilBound, { capture: true });
     }
     return Promise.resolve();
   };

   window.setCookie = function (name, value, days = 30) {
      let expires = "";
      if (days) {
          let date = new Date();
          date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
          expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + encodeURIComponent(JSON.stringify(value)) + "; path=/" + expires;
    };

    window.getCookie = function (name) {
      let nameEQ = name + "=";
      try {
        let ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i].trim();
            if (c.indexOf(nameEQ) === 0) {
                 try {
                   return decodeURIComponent(c.substring(nameEQ.length, c.length));
                 } catch (e) {
                     console.error("Cookie value could not be decoded", e);
                     return null;
                 }
            }
        }
      }
      catch(e){
        return null
      }
      return null;
    };

    // Writes the value verbatim, unlike setCookie above which JSON.stringify's it — that one round-trips
    // only through a JSON.parse the getCookie below does not do, so a plain string written with it comes
    // back wrapped in quotes. Needed for cookies shared with the server, where the value has to be the exact
    // bytes ASP.NET Core wrote: the pending discount code (TKR-10902) is written by
    // PendingDiscountCodeMiddleware on a campaign-link hit and here when an anonymous visitor types a code,
    // and a quoted copy would simply not resolve. Attributes mirror the middleware's CookieOptions so the
    // two writers produce ONE cookie rather than a pair that differ only by path/SameSite.
    window.setRawCookie = function (name, value, days) {
        let expires = "";
        if (days) {
            let date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + encodeURIComponent(value) + "; path=/" + expires + "; SameSite=Lax; Secure";
    };

    window.checkCookie = function (name) {
        return document.cookie.split(';').some((c) => c.trim().startsWith(name + "="));
    };

    window.deleteCookie = function (name) {
        document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    };

      // The inline color-scheme has to move with the class. App.razor stamps it on <html> at SSR so the browser
      // knows which palette the document paints itself in before anything else loads, and an inline declaration
      // outranks the .socialfi-theme-* rules in App.css — so a switch that only swapped the class would leave the
      // browser on the previous palette: UA canvas, scrollbars and native form controls stuck on the old theme,
      // and a page still declared light getting force-darkened by a dark-mode WebView's auto-darkening.
      window.applySocialFiTheme = function (isDark) {
      var html = document.documentElement;
      if (isDark) {
        html.classList.add('socialfi-theme-dark');
        html.classList.remove('socialfi-theme-light');
        html.style.setProperty('color-scheme', 'dark');
      } else {
        html.classList.add('socialfi-theme-light');
        html.classList.remove('socialfi-theme-dark');
        html.style.setProperty('color-scheme', 'light');
      }
    };

    // The folded desktop menu: one class on <html> that App.css reads, and a cookie the server reads verbatim so the
    // next page renders folded from the first byte (SocialFiSidebarCookieReader); setRawCookie is the writer for
    // exactly that kind of cookie.
    window.toggleSocialFiSidebarCollapsed = function () {
      var html = document.documentElement;
      var isCollapsed = !html.classList.contains('socialfi-sidebar-collapsed');
      html.classList.toggle('socialfi-sidebar-collapsed', isCollapsed);
      var collapsedValue = '0';
      if (isCollapsed) {
        collapsedValue = '1';
      }
      window.setRawCookie('socialfi_sidebar_collapsed', collapsedValue, 365);
      var toggle = document.getElementById('socialfi-sidebar-toggle');
      if (toggle) {
        toggle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
      }
    };

    // Toggles device-theme following (TKR-11206) across an in-circuit navigation — a Blazor route change between a
    // signed-out entry surface and a page with a palette of its own, which the pre-paint script in App.razor's head
    // cannot see. ON re-reads the media query and re-arms the listener; OFF detaches it, and the caller then pushes
    // the resolved palette through applySocialFiTheme above. Idempotent: always detached before being re-attached.
    window.setSocialFiDeviceThemeFollowing = function (isFollowing) {
      try {
        var darkQuery = window._tokeroDeviceThemeQuery;
        if (!darkQuery) {
          darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
          window._tokeroDeviceThemeQuery = darkQuery;
        }

        if (window._tokeroDeviceThemeListener) {
          darkQuery.removeEventListener('change', window._tokeroDeviceThemeListener);
        }

        if (!isFollowing) {
          return;
        }

        window._tokeroDeviceThemeListener = function (e) { window.applySocialFiTheme(e.matches); };
        darkQuery.addEventListener('change', window._tokeroDeviceThemeListener);
        window.applySocialFiTheme(darkQuery.matches);
      } catch (e) { /* no matchMedia — whichever palette is on the document stands */ }
    };

    window.hasHtmlClass = function (className) {
      return document.documentElement.classList.contains(className);
    };

    // "Highlight dev internals" (TKR-11456). Delegated from document rather than bound per control, and driven
    // by a data attribute rather than an inline onclick, for three reasons: the toggle renders in three places
    // (the topbar burger menu, the marketing footer, the SocialFi auth footer), a MudMenu popover mounts and
    // unmounts its copy on every open, and an inline handler would be a script-src violation under the
    // report-only CSP the app is measuring before enforcing.
    //
    // It is JS rather than an @onclick because the work is a cookie write, which C# cannot do after prerender
    // (no HttpContext on the circuit), and because this way the control works during prerender, on a static
    // render and after the circuit has dropped — the states a developer diagnosing a gate is most likely to be
    // looking at.
    //
    // The reload is not optional: DevInternalsMarker emits its marker element SERVER-side from the cookie, so
    // the markers for the gated blocks only exist in a document rendered after the cookie changed.
    // Wired at most once per document: a second listener would flip the cookie twice per click and the toggle
    // would look dead rather than broken.
    //
    // THIS BLOCK IS THE WHOLE HANDLER FOR THE SWITCH — DevInternalsHighlightToggle.razor renders markup and
    // nothing else, so deleting it here leaves a switch that renders, opens with the menu and does nothing at
    // all. TKR-11472 replaced it wholesale with the on-page-edit block below and it shipped dead for a day;
    // the two are neighbours by accident of position, not alternatives. Restored in TKR-11472 phase 2.
    if (!window._tokeroDevInternalsToggleWired) {
      window._tokeroDevInternalsToggleWired = true;

      document.addEventListener('click', function (clickEvent) {
        var eventTarget = clickEvent.target;
        if (!eventTarget || !eventTarget.closest) {
          return;
        }

        var toggleTrigger = eventTarget.closest('[data-tk-dev-internals-toggle]');
        if (!toggleTrigger) {
          return;
        }

        clickEvent.preventDefault();

        // Read exactly the way DevInternalsHighlightCookie reads it server-side — the whole value has to be
        // "1" — so the control and the server can never disagree about which state the click is leaving.
        // The name is the literal of CookiesConstants.Names.DevInternalsHighlight; keep the two in sync.
        var highlightCookieMatch = document.cookie.match(/(?:^|;\s*)tkr_dev_highlight=([^;]*)/);
        var isCurrentlyEnabled = false;
        if (highlightCookieMatch !== null && highlightCookieMatch[1] === '1') {
          isCurrentlyEnabled = true;
        }

        // setRawCookie writes no Domain attribute, which is what scopes this to the exact hostname: the mode is
        // switched on for one review env without following the developer to staging or production. 400 days is
        // CookiesConstants.MaxBrowserCookieLifetimeDays, repeated here because JS cannot read the constant.
        if (isCurrentlyEnabled) {
          window.deleteCookie('tkr_dev_highlight');
        } else {
          window.setRawCookie('tkr_dev_highlight', '1', 400);
        }

        window.location.reload();
      });
    }

    // "On-page edit double-click" (TKR-11472). Two calls, driven from OnPageEditable: put the stored value
    // into the editing surface, and read back what was typed.
    //
    // It is JS at all because contenteditable is the browser's own rich-text editor — the thing that makes
    // Ctrl+B, paste-with-formatting, drag-to-move and undo work without a library — and Blazor has no binding
    // for it. Nothing here decides anything: which block, whether this viewer may edit it, and what is finally
    // written are all decided in C#.
    //
    // THE SURFACE IS THE ONLY ELEMENT EITHER FUNCTION TOUCHES, and it is an element Blazor deliberately renders
    // with no children, ever. That is not a convention, it is the fix for a circuit-killing bug: the first
    // version replaced the innerHTML of the page's own block, Blazor's virtual copy of those nodes went stale,
    // and the next render that touched the subtree patched nodes that no longer existed — which throws inside
    // the renderer, drops the circuit, and leaves a page where nothing at all is clickable. Never point these
    // at anything but the surface, and never let Blazor render children into it.

    function resolveOnPageEditSurface(surfaceElementID) {
      var surfaceElement = document.getElementById(surfaceElementID);
      if (!surfaceElement) {
        return null;
      }
      return surfaceElement;
    }

    // editableHtml is the column as it sits in the database, minus any <style> / <script> blocks, which C# held
    // back — see OnPageEditService.SplitProtectedBlocks. It is NOT what the page rendered.
    window.tokeroOnPageEditFill = function (surfaceElementID, editableHtml, isHtml) {
      var surfaceElement = resolveOnPageEditSurface(surfaceElementID);
      if (!surfaceElement) {
        return;
      }

      if (isHtml) {
        surfaceElement.innerHTML = editableHtml;
      } else {
        surfaceElement.textContent = editableHtml;
      }

      surfaceElement.focus();
    };

    window.tokeroOnPageEditRead = function (surfaceElementID, isHtml) {
      var surfaceElement = resolveOnPageEditSurface(surfaceElementID);
      if (!surfaceElement) {
        return '';
      }

      if (isHtml) {
        return surfaceElement.innerHTML;
      }

      // innerText and not textContent: it collapses the whitespace the way the reader saw it, so a title typed
      // across a soft wrap is stored as one line rather than with the line break the DOM happens to hold.
      return surfaceElement.innerText;
    };

    // Embeddable event widget. Called from the RSVP popup (the full event page opened with
    // ?embedRsvp=true) after a successful RSVP, to notify the parent widget iframe so it can flip its card button.
    // window.opener is the iframe that opened this popup; both are on the Tokero origin, so we post to that
    // exact origin (never '*'). Silently no-ops when there is no opener (page opened directly, not as a popup).
    window.tokeroEmbedNotifyRsvpSuccess = function (slug, status) {
      try {
        if (!window.opener || window.opener.closed) {
          return;
        }
        window.opener.postMessage(
          { type: 'tokero:event-rsvp', slug: slug, status: status },
          window.location.origin);
      } catch (e) {
        /* Cross-origin/opener access can throw in odd embedding setups — the RSVP itself already succeeded. */
      }
    };

    // Live preview for the group brand accent: overrides the single brand CSS variable on <html> (inline
    // styles beat both :root and .socialfi-theme-dark). Empty value removes the override so the accent
    // falls back to the default --tk-brand-purple from App.css. Mirrors applySocialFiTheme above.
    // The "on" (legible text) and "soft" (tint) variables are NOT set here — App.css derives both off
    // --tk-brand-purple (color-mix for soft, an OKLCH auto-contrast pick for on), so the accent only ever
    // needs this one value. The .socialfi-branded marker tracks whether an accent is active: light mode
    // has no brand wiring of its own, so the .socialfi-theme-light.socialfi-branded rules in App.css key
    // off this class to route the primary surfaces (buttons, text-primary, links) to --tk-brand-purple.
    window.applySocialFiBrandColor = function (primary) {
      var html = document.documentElement;
      if (primary) {
        html.style.setProperty('--tk-brand-purple', primary);
        html.classList.add('socialfi-branded');
      } else {
        html.style.removeProperty('--tk-brand-purple');
        html.classList.remove('socialfi-branded');
      }
    };

    // Resolves the element an id should hand focus to. The id is usually on the text control itself, but several
    // MudBlazor field variants put it on the wrapper instead, hence the nested lookup; an id on anything else
    // focusable (a tabindex'd div) is returned as-is. Null means the id is not in the DOM at all.
    function resolveFocusTargetById(id) {
      var baseElement = document.getElementById(id);
      if (!baseElement) {
        return null;
      }

      if (baseElement instanceof HTMLInputElement || baseElement instanceof HTMLTextAreaElement) {
        return baseElement;
      }

      var nestedInputElement = baseElement.querySelector("textarea, input");
      if (nestedInputElement) {
        return nestedInputElement;
      }

      return baseElement;
    }

    // Returns whether the element was found and focused, so callers can tell a missing element apart from an
    // interop failure (which surfaces as a thrown JSException on the .NET side) instead of guessing.
    window.focusElementById = function (id) {
      var focusTarget = resolveFocusTargetById(id);
      if (!focusTarget) {
        return false;
      }

      focusTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      focusTarget.focus();
      return true;
    };

    // Focuses a post composer and rings its card with the fading highlight. Returns whether the input was actually
    // focused, so a caller that cares (the Blazor fallback below) can report a composer it could not find.
    //
    // Called from two places for the same tap, which is why it is a function and not inlined into the listener:
    // the native click listener wired below (synchronous, inside the gesture — the only path that can raise a
    // mobile keyboard) and, one SignalR round trip later, the button's Blazor handler as a fallback for taps that
    // beat the wiring. Both arriving is the normal fast path, so this has to be safe to run twice per tap.
    window.socialfiFocusComposerWithHighlight = function (composerCardId, composerInputId) {
      // Resolved per call rather than captured at wire-up time: a re-render can replace these nodes, and the
      // description field in particular is absent while the poll editor stands in for it.
      var didFocus = window.focusElementById(composerInputId);
      var composerCard = document.getElementById(composerCardId);
      if (!composerCard || !didFocus) {
        // No ring around a composer that was never focused — it would point the eye at the wrong thing.
        return didFocus;
      }

      // Already ringing, which means the native listener handled this same tap and the Blazor fallback is now
      // arriving behind it. Leaving the running animation alone is the point: re-adding the class would restart
      // it and read as a second flash a fifth of a second after the first.
      if (composerCard.classList.contains("tk-composer-highlight")) {
        return didFocus;
      }

      composerCard.classList.add("tk-composer-highlight");

      // Matches the animation length in App.css. The animation already ends on a transparent ring, so this is
      // only about not leaving a spent class on the element.
      window.setTimeout(function () {
        composerCard.classList.remove("tk-composer-highlight");
      }, 1800);

      return didFocus;
    };

    // Wires a button so that clicking it focuses a post composer, from inside the browser's own click handler.
    //
    // This exists instead of doing the work from the button's Blazor OnClick because both consumer apps render
    // Interactive Server: an @onclick is a SignalR round trip, so any interop call it makes reaches the browser
    // well after the transient user-activation window opened by the tap has closed. iOS Safari and Android Chrome
    // only raise the soft keyboard for a .focus() that runs inside the gesture task itself, so a native listener
    // is the only place the keyboard can come up. Reordering the C# does not help, and a desktop browser cannot
    // show the difference because desktop focus does not depend on the gesture at all.
    //
    // Returns false when the button or the composer card is missing, so the caller can log a genuinely broken
    // path rather than silently wiring nothing. The description field is deliberately NOT part of that check:
    // it is legitimately absent while the poll editor replaces it, so treating it as a wire-up failure would
    // report a working page as broken. Whether the focus lands is reported per click instead, by the return of
    // socialfiFocusComposerWithHighlight to the Blazor fallback.
    window.socialfiWireComposerFocusOnClick = function (triggerId, composerCardId, composerInputId) {
      var triggerElement = document.getElementById(triggerId);
      var composerCardElement = document.getElementById(composerCardId);
      if (!triggerElement || !composerCardElement) {
        return false;
      }

      // Blazor patches this button in place across re-renders, so the element (and this flag with it) survives;
      // the guard just keeps a repeated wire-up call from stacking duplicate listeners.
      if (triggerElement.dataset.composerFocusWired === "true") {
        return true;
      }

      triggerElement.addEventListener("click", function () {
        // Synchronous on purpose: awaiting anything here, or deferring into a timeout, ends the gesture task and
        // the keyboard stays down.
        window.socialfiFocusComposerWithHighlight(composerCardId, composerInputId);
      });

      triggerElement.dataset.composerFocusWired = "true";
      return true;
    };

    // Raises the mobile soft keyboard for a search box that is not in the DOM yet.
    //
    // Same constraint as socialfiWireComposerFocusOnClick above — iOS Safari and Android Chrome only raise the
    // keyboard for a .focus() that runs inside the tap's own gesture task — but a harder version of it. That one
    // can focus its target from the click listener because the composer is already on the page. The search box is
    // not: it lives in ProfileSearchDialog, which the topbar's magnifier opens through a Blazor @onclick, i.e. a
    // SignalR round trip. At the moment of the tap there is nothing to focus, and by the time the dialog has
    // rendered the gesture window has closed, so focusing the box then only moves the caret — it looks active and
    // the phone shows no keyboard until it is tapped a second time.
    //
    // So the tap focuses a stand-in. A 1px, fully transparent text input is created and focused synchronously
    // inside the gesture, which brings the keyboard straight up; it stays up across the round trip, and the moment
    // the dialog's own input lands in the DOM the focus is handed over to it. Moving focus from one text input to
    // another needs no fresh gesture, so the keyboard never drops — it just ends up attached to the real box,
    // which is where the typing goes.
    //
    // Opt in from the markup by putting data-tk-primes-keyboard-for="<input id>" on the button that opens the
    // dialog; see the mobile magnifier in TopbarMenuSocialFi.razor. Nothing here is needed on a desktop, where
    // focus does not depend on the gesture at all — the box's own AutoFocus covers that.
    window.socialfiSearchKeyboardPrimer = (function () {
      // How long the stand-in holds the keyboard open waiting for the dialog. A round trip plus the dialog's first
      // render is tens of milliseconds on a usable connection; past a few seconds of an unusable one, a keyboard
      // hovering over a dialog that never opened is worse than no keyboard, so the stand-in blurs itself instead.
      var handoverTimeoutMs = 4000;

      // The stand-in currently holding the keyboard, and the watcher waiting for the real box. Module state rather
      // than closure state because a second tap has to REPLACE both, not stack a second pair on top of them.
      var standInInput = null;
      var handoverObserver = null;
      var handoverGiveUpTimeoutId = 0;

      function stopWatchingForSearchInput() {
        if (handoverObserver) {
          handoverObserver.disconnect();
          handoverObserver = null;
        }

        if (handoverGiveUpTimeoutId) {
          window.clearTimeout(handoverGiveUpTimeoutId);
          handoverGiveUpTimeoutId = 0;
        }
      }

      function removeStandInInput() {
        if (!standInInput) {
          return;
        }

        var inputToRemove = standInInput;
        standInInput = null;

        if (inputToRemove.parentNode) {
          inputToRemove.parentNode.removeChild(inputToRemove);
        }
      }

      function createStandInInput() {
        var createdInput = document.createElement("input");
        // Plain text, matching the real search box rather than type="search": the keyboard raised here is the one
        // the user keeps, and a search input asks iOS for a different return key, which would visibly swap on the
        // handover.
        createdInput.type = "text";
        createdInput.setAttribute("autocomplete", "off");
        // Out of the tab order and out of the accessibility tree: this is a keyboard trick, not a form field, and
        // it is in the document for about one round trip.
        createdInput.setAttribute("tabindex", "-1");
        createdInput.setAttribute("aria-hidden", "true");
        // Focusable means actually rendered, so this has to be a real 1px box that is merely invisible —
        // display:none, visibility:hidden and a zero-size box are all skipped by the browsers' keyboard
        // heuristics, and so is anything moved off-screen far enough. position:fixed keeps it inside the viewport
        // so focusing it cannot scroll the feed away underneath the dialog that is about to cover it,
        // pointer-events:none keeps that 1px from swallowing a tap meant for what is behind it, and 16px is the
        // smallest font size iOS will focus without zooming the page in.
        createdInput.style.cssText = "position:fixed;top:8px;left:8px;width:1px;height:1px;padding:0;border:0;"
          + "opacity:0;background:transparent;font-size:16px;pointer-events:none;";
        document.body.appendChild(createdInput);
        return createdInput;
      }

      // Hands the keyboard from the stand-in to the real search box. Returns whether the box had rendered yet.
      function tryHandOverToSearchInput(searchInputId) {
        var searchInput = resolveFocusTargetById(searchInputId);
        if (!searchInput) {
          return false;
        }

        // Focused directly rather than through focusElementById, whose scrollIntoView is unwanted here: the box
        // is at the top of a dialog that just opened, so there is nothing to scroll to, and a smooth scroll while
        // the keyboard is animating in is exactly the jank iOS handles worst.
        //
        // The real box is focused FIRST and the stand-in dropped after: removing a focused element blurs it, and a
        // blur with nothing else focused is precisely what closes the keyboard.
        searchInput.focus();
        stopWatchingForSearchInput();
        removeStandInInput();
        return true;
      }

      function primeKeyboardFor(searchInputId) {
        // A previous tap whose dialog never arrived leaves a stand-in and a watcher behind for its timeout to
        // clear. A new tap supersedes both.
        stopWatchingForSearchInput();
        removeStandInInput();

        standInInput = createStandInInput();
        // Synchronous on purpose: awaiting anything here, or deferring into a timeout, ends the gesture task and
        // the keyboard stays down.
        standInInput.focus();

        var handedOver = tryHandOverToSearchInput(searchInputId);
        if (handedOver) {
          return;
        }

        // The dialog is a round trip away, so the box arrives as a DOM mutation rather than on any schedule.
        handoverObserver = new MutationObserver(function () {
          tryHandOverToSearchInput(searchInputId);
        });
        handoverObserver.observe(document.body, { childList: true, subtree: true });

        handoverGiveUpTimeoutId = window.setTimeout(function () {
          stopWatchingForSearchInput();

          if (standInInput) {
            standInInput.blur();
          }

          removeStandInInput();
        }, handoverTimeoutMs);
      }

      return {
        enable: function () {
          // The listener is delegated from the document and never removed, so wiring it twice would prime twice
          // per tap. Blazor re-invokes this from AfterRenderAsync far more often than the page is loaded.
          if (window.tkSearchKeyboardPrimingWired === true) {
            return;
          }
          window.tkSearchKeyboardPrimingWired = true;

          // Delegated from the document, in the capture phase. Delegated because the buttons that open the dialog
          // are conditionally rendered and can appear on a later render than the first, so there is no render pass
          // at which they are all present to be wired — and because the listener can then be registered before any
          // of them exists, which removes the window in which an eager tap lands before the wiring. Capture so it
          // runs inside the tap's own gesture task, ahead of Blazor's own document-level click listener.
          document.addEventListener("click", function (clickEvent) {
            var clickedElement = clickEvent.target;
            if (!clickedElement || typeof clickedElement.closest !== "function") {
              return;
            }

            var primingTrigger = clickedElement.closest("[data-tk-primes-keyboard-for]");
            if (!primingTrigger) {
              return;
            }

            var searchInputId = primingTrigger.getAttribute("data-tk-primes-keyboard-for");
            if (!searchInputId) {
              return;
            }

            primeKeyboardFor(searchInputId);
          }, true);
        }
      };
    })();

    // Closes the mobile soft keyboard when the user starts scrolling a search results list.
    //
    // The counterpart to socialfiSearchKeyboardPrimer above: that one raises the keyboard for the search box the
    // magnifier opens, this one gets it out of the way once the user is done typing and starts reading. On a phone
    // the keyboard covers roughly half the dialog, so the results the search just produced are scrolled behind it;
    // the reflex is to swipe the list, and every other app answers that swipe by dismissing the keyboard.
    //
    // It is a native listener rather than a Blazor @ontouchmove for the same reason as the primer: an @ontouchmove
    // is a SignalR round trip per touch event, which is both far too chatty for a scroll gesture and too late to
    // feel connected to it. Nothing here is needed on a desktop, and nothing here runs there — touch events are
    // the only trigger, so a mouse wheel or a scrollbar drag leaves focus exactly where it was.
    //
    // Enable it from the component that owns the box, passing the box's id: see ProfileSearchDialog.razor.
    window.socialfiSearchKeyboardScrollDismiss = (function () {
      // How far the finger must travel vertically before this counts as a scroll rather than a tap. A tap on a
      // result row carries a few pixels of jitter, and dismissing the keyboard on that would fight the tap it is
      // part of; a deliberate swipe passes this within the first frame or two, so the dismissal still reads as
      // instant.
      var dismissAfterVerticalDragPx = 12;

      // The box whose keyboard this dismisses, and the drag being measured. Module state because the listeners are
      // delegated from the document and wired once, while the dialog holding the box comes and goes.
      var watchedSearchInputId = "";
      var isDragBeingMeasured = false;
      var dragStartY = 0;

      function onTouchStart(touchEvent) {
        isDragBeingMeasured = false;

        if (!watchedSearchInputId) {
          return;
        }

        var firstTouch = touchEvent.touches[0];
        if (!firstTouch) {
          return;
        }

        // A drag that begins on a text field is a text selection, not a scroll — including one on the search box
        // itself, where dismissing the keyboard would take away the field the user is still working in.
        var touchedElement = touchEvent.target;
        if (touchedElement && typeof touchedElement.closest === "function") {
          var touchedTextField = touchedElement.closest("input, textarea, [contenteditable='true']");
          if (touchedTextField) {
            return;
          }
        }

        dragStartY = firstTouch.clientY;
        isDragBeingMeasured = true;
      }

      function onTouchMove(touchEvent) {
        if (!isDragBeingMeasured) {
          return;
        }

        var firstTouch = touchEvent.touches[0];
        if (!firstTouch) {
          return;
        }

        var verticalDragPx = Math.abs(firstTouch.clientY - dragStartY);
        if (verticalDragPx < dismissAfterVerticalDragPx) {
          return;
        }

        // One dismissal per drag: the rest of this gesture's touchmove events have nothing left to do, and
        // re-checking on each of them would only re-blur an already-blurred box.
        isDragBeingMeasured = false;

        // Resolved through the shared helper, not getElementById: MudTextField puts the Id we are given on its
        // wrapper, so the element that actually holds focus is the <input> nested inside it.
        var searchInput = resolveFocusTargetById(watchedSearchInputId);
        if (!searchInput) {
          return;
        }

        // Only ever blurs the box this was enabled for, and only while it actually holds focus. That is what makes
        // a permanent document-level listener harmless: with the dialog closed, or the caret anywhere else, this
        // does nothing at all.
        if (document.activeElement !== searchInput) {
          return;
        }

        searchInput.blur();
      }

      return {
        enable: function (searchInputId) {
          if (!searchInputId) {
            return;
          }

          watchedSearchInputId = searchInputId;

          // The listeners are delegated from the document and never removed, so wiring them twice would measure
          // every drag twice. Blazor re-invokes this from AfterRenderAsync far more often than the page is loaded.
          if (window.tkSearchScrollKeyboardDismissWired === true) {
            return;
          }
          window.tkSearchScrollKeyboardDismissWired = true;

          // Passive because this never calls preventDefault: the browser must keep scrolling the list at native
          // speed while the keyboard comes down. Capture so the measurement is not lost to a handler further down
          // that stops propagation.
          document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
          document.addEventListener("touchmove", onTouchMove, { passive: true, capture: true });
        }
      };
    })();

  window.socialfiCommentMentions = {
    getActiveQuery: function (id, triggerChar) {
      var minimumMentionQueryLength = 3;

      if (!triggerChar) {
        triggerChar = "@";
      }

      if (!id) {
        return null;
      }

      var baseElement = document.getElementById(id);
      if (!baseElement) {
        return null;
      }

      var inputElement = baseElement;
      if (!(inputElement instanceof HTMLInputElement) && !(inputElement instanceof HTMLTextAreaElement)) {
        var nestedInputElement = baseElement.querySelector("textarea, input");
        if (!nestedInputElement) {
          return null;
        }

        inputElement = nestedInputElement;
      }

      var currentValue = inputElement.value || "";
      var caretPosition = currentValue.length;
      if (typeof inputElement.selectionStart === "number") {
        caretPosition = inputElement.selectionStart;
      }

      var textBeforeCaret = currentValue.substring(0, caretPosition);
      var mentionStartIndex = textBeforeCaret.lastIndexOf(triggerChar);
      if (mentionStartIndex < 0) {
        return null;
      }

      var prefixBeforeMention = "";
      if (mentionStartIndex > 0) {
        prefixBeforeMention = textBeforeCaret.substring(mentionStartIndex - 1, mentionStartIndex);
      }

      if (/[A-Za-z0-9_]/.test(prefixBeforeMention)) {
        return null;
      }

      var mentionQuery = textBeforeCaret.substring(mentionStartIndex + 1);
      if (/[^A-Za-z0-9._-]/.test(mentionQuery)) {
        return null;
      }

      if (mentionQuery.length < minimumMentionQueryLength) {
        return null;
      }

      return {
        startIndex: mentionStartIndex,
        endIndex: caretPosition,
        query: mentionQuery,
        triggerChar: triggerChar
      };
    },

    setCaretPosition: function (id, position) {
      if (!id) {
        return;
      }

      var baseElement = document.getElementById(id);
      if (!baseElement) {
        return;
      }

      var inputElement = baseElement;
      if (!(inputElement instanceof HTMLInputElement) && !(inputElement instanceof HTMLTextAreaElement)) {
        var nestedInputElement = baseElement.querySelector("textarea, input");
        if (!nestedInputElement) {
          return;
        }

        inputElement = nestedInputElement;
      }

      inputElement.focus();

      if (typeof inputElement.setSelectionRange === "function") {
        inputElement.setSelectionRange(position, position);
      }
    }
  };

  // The post composer's paste / drop of a picture (SocialFiCreatePost.razor). Here rather than in a <script> inside
  // the component: that block only ran when the composer was part of the server-rendered document, so a composer that
  // first appeared through an interactive render - ticking a platform on the cross-post page, an in-circuit
  // navigation to the feed - called a function that did not exist, and the throw out of AfterRenderAsync replaced the
  // page with the error page (tokero-style 19.3.8).
  window.socialFiCreatePost = window.socialFiCreatePost || {};

  window.socialFiCreatePost.attachPasteImageListener = function(inputId, containerId, dotNetRef) {
    function readImageAndInvoke(blob) {
      var reader = new FileReader();
      reader.onload = function(evt) {
        dotNetRef.invokeMethodAsync('OnImagePastedAsync', evt.target.result);
      };
      reader.readAsDataURL(blob);
    }

    var container = document.getElementById(containerId);
    if (!container) return;

    // The paste handler sits on the container, which Blazor never re-creates, rather than on the description field,
    // which it does: the field is rendered only while no poll is open, so opening and closing a poll - and the reset
    // after a poll post - replaced it with a fresh element that carried no handler, and pasting a screenshot did
    // nothing until a reload. Paste bubbles, so the container still hears it; the target check keeps the handler to
    // pastes made inside the description field, as it was.
    if (container._pasteImageHandler) {
      container.removeEventListener('paste', container._pasteImageHandler);
    }
    container._pasteImageHandler = function(e) {
      var input = document.getElementById(inputId);
      if (!input || !input.contains(e.target)) return;
      var items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          var blob = items[i].getAsFile();
          if (blob) readImageAndInvoke(blob);
          return;
        }
      }
    };
    container.addEventListener('paste', container._pasteImageHandler);

    if (container._dragOverHandler) {
      container.removeEventListener('dragover', container._dragOverHandler);
      container.removeEventListener('dragleave', container._dragLeaveHandler);
      container.removeEventListener('drop', container._dropHandler);
    }

    container._dragOverHandler = function(e) {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      container.classList.add('drag-image-over');
    };

    container._dragLeaveHandler = function(e) {
      if (!container.contains(e.relatedTarget)) {
        container.classList.remove('drag-image-over');
      }
    };

    container._dropHandler = function(e) {
      container.classList.remove('drag-image-over');
      var files = e.dataTransfer && e.dataTransfer.files;
      if (!files || files.length === 0) return;
      for (var i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/')) {
          e.preventDefault();
          readImageAndInvoke(files[i]);
          return;
        }
      }
    };

    container.addEventListener('dragover', container._dragOverHandler);
    container.addEventListener('dragleave', container._dragLeaveHandler);
    container.addEventListener('drop', container._dropHandler);
  };

  // Keyboard docking for the comment composer (SocialCommentComposer.razor): while the field has focus, the
  // composer leaves the post card and pins itself, with its action row, above the soft keyboard - otherwise it is
  // scrolled away with the card or covered outright and people type blind.
  //
  // Phones only, gated twice because neither gate is sufficient alone:
  //   1. The caller - SocialCommentComposer attaches only when UserAgentHelper.IsMobileSurface says so. That keeps
  //      a desktop window dragged narrow out, which the viewport test alone would read as a keyboard.
  //   2. The keyboard - handleViewportChanged waits for the visual viewport to actually lose height. That keeps
  //      DEVICE EMULATION out: it fakes the user agent, pointer, screen size and pixel ratio, but not a keyboard.
  //
  // Hence no viewport-size or pointer-type test anywhere here or in the CSS: everywhere the two gates do not both
  // hold, the composer renders exactly as it did before this existed.
  window.socialfiComposerKeyboardDock = (function () {
    // focusedComposerElement is the composer the caret is in; dockedComposerElement is the one currently lifted
    // above the keyboard. Separate because focus alone does NOT dock - see the test in handleViewportChanged.
    var focusedComposerElement = null;
    var dockedComposerElement = null;
    var pendingUndockTimeoutId = 0;
    var areViewportListenersWired = false;

    // Tallest visual viewport seen at the current orientation, and the width it was seen at. Only a keyboard takes
    // a large bite out of the viewport, so "well below the tallest" answers "is the keyboard up" - and it is the
    // only form of that question device emulation cannot fake.
    var tallestViewportHeightInPixels = 0;
    var tallestViewportWidthInPixels = 0;

    // A viewport that loses more than this is showing a keyboard - clear of the 40-60px collapsing URL bar.
    var minimumKeyboardHeightInPixels = 120;

    // Grace period before the panel drops back into the card: a tap on a control blurs the text field before the
    // click is dispatched, and undocking in between would move the button out from under the finger.
    var undockGraceInMilliseconds = 160;

    function computeKeyboardInsetInPixels() {
      var viewport = window.visualViewport;
      if (!viewport) {
        return 0;
      }

      // Where the keyboard shrinks only the visual viewport (iOS Safari, Chrome's default) this is its height.
      // Where it shrinks the layout viewport too, innerHeight shrank with it, the difference is 0, and bottom:0
      // is already right.
      var keyboardInset = window.innerHeight - viewport.height - viewport.offsetTop;
      if (keyboardInset < 0) {
        keyboardInset = 0;
      }

      return keyboardInset;
    }

    function applyKeyboardInset() {
      if (!dockedComposerElement) {
        return;
      }

      var keyboardInset = computeKeyboardInsetInPixels();
      dockedComposerElement.style.setProperty("--tk-composer-keyboard-inset", keyboardInset + "px");
    }

    // While the panel is docked the page behind it must not scroll. The panel is pinned to the keyboard rather
    // than to the page, so a background that scrolls slides the post and the thread out from behind the comment
    // being written while the composer itself stays put - and on iOS the drag also carries Safari's own scroll
    // anchoring for the focused field, which lands the caret somewhere nobody asked for.
    //
    // Cancelling the touch gesture, rather than `overflow: hidden` on <html>: document-element overflow is the
    // one lock iOS Safari has never honoured consistently, and where it is honoured it clamps the scroll offset
    // and jumps the page as the class lands. It also has to stay a gesture-level lock because the composer's own
    // scrolling must survive - the field grows to 6rem and then scrolls, and the mention list scrolls - which
    // rules out `touch-action: none` on an ancestor, since the effective value is the intersection down the tree
    // and a descendant cannot opt back in. A touch that starts inside the composer is let through instead.
    //
    // Programmatic scrolling is deliberately left working: socialfiReplyComposer.focusInputById keeps calling
    // scrollIntoView for up to 1.2s after focus to bring the thread above the keyboard, and preventDefault on a
    // gesture does not touch it.
    var isBackgroundScrollLocked = false;

    function handleDocumentTouchMove(event) {
      // Cheap first: a leaked listener must never be able to freeze the page once nothing is docked.
      if (!dockedComposerElement) {
        return;
      }

      // Two fingers is a pinch, not a background scroll. Cancelling it would take zoom away from somebody who is
      // only trying to read the post they are answering.
      if (event.touches && event.touches.length > 1) {
        return;
      }

      var touchedElement = event.target;
      if (touchedElement && typeof touchedElement.closest === "function") {
        // Three things are not "the page behind the panel". The composer keeps its own scrolling, and a dialog or
        // a popover is a layer ABOVE the panel: the photo editor opens from the attach button, whose mousedown is
        // cancelled by handleComposerMouseDown to keep the caret - so the field never blurs, the panel stays
        // docked for as long as the dialog is up, and a lock that ignored it would leave the editor unscrollable.
        var exemptElement = touchedElement.closest(
          ".social-comment-composer, .mud-dialog-container, .mud-popover");
        if (exemptElement) {
          return;
        }
      }

      // A gesture already claimed by the browser (a scroll it started before this listener saw it) is not
      // cancelable, and calling preventDefault would only log an intervention warning.
      if (!event.cancelable) {
        return;
      }

      event.preventDefault();
    }

    function lockBackgroundScroll() {
      if (isBackgroundScrollLocked) {
        return;
      }

      // Non-passive on purpose: a passive listener is not allowed to cancel the scroll, which is the whole point.
      document.addEventListener("touchmove", handleDocumentTouchMove, { passive: false });
      isBackgroundScrollLocked = true;
    }

    function unlockBackgroundScroll() {
      if (!isBackgroundScrollLocked) {
        return;
      }

      document.removeEventListener("touchmove", handleDocumentTouchMove);
      isBackgroundScrollLocked = false;
    }

    // A rotation changes the width and legitimately shrinks the height, so the portrait maximum has to be
    // forgotten - kept, it would read landscape as a keyboard that never closes.
    function trackTallestViewport(viewport) {
      if (viewport.width !== tallestViewportWidthInPixels) {
        tallestViewportWidthInPixels = viewport.width;
        tallestViewportHeightInPixels = 0;
      }

      if (viewport.height > tallestViewportHeightInPixels) {
        tallestViewportHeightInPixels = viewport.height;
      }
    }

    function isKeyboardOpen(viewport) {
      var lostViewportHeight = tallestViewportHeightInPixels - viewport.height;
      if (lostViewportHeight > minimumKeyboardHeightInPixels) {
        return true;
      }

      return false;
    }

    function handleViewportChanged() {
      var viewport = window.visualViewport;
      if (!viewport) {
        return;
      }

      trackTallestViewport(viewport);

      var keyboardIsOpen = isKeyboardOpen(viewport);
      if (keyboardIsOpen && focusedComposerElement) {
        // Covers the first open and every later change alike (emoji panel, accessory bar, pinned-zoom scroll):
        // dock() re-applies the inset when handed the composer that is already docked.
        dock(focusedComposerElement);
        return;
      }

      // Keyboard gone. On Android the field keeps focus when it is dismissed with the back gesture, so focusout
      // never fires and this is the only signal that the panel belongs back in the card.
      if (!keyboardIsOpen && dockedComposerElement) {
        undock(dockedComposerElement);
      }
    }

    // Wired on the first attach and never removed: the listeners sit on window.visualViewport, not on anything
    // Blazor owns, and the handler is a no-op while no composer has focus. Attach time rather than focus time
    // because the tallest-viewport baseline has to be measured with no keyboard up.
    function wireViewportListeners() {
      if (areViewportListenersWired) {
        return;
      }

      var viewport = window.visualViewport;
      if (!viewport) {
        return;
      }

      trackTallestViewport(viewport);
      viewport.addEventListener("resize", handleViewportChanged);
      viewport.addEventListener("scroll", handleViewportChanged);
      areViewportListenersWired = true;
    }

    function dock(composerElement) {
      if (dockedComposerElement === composerElement) {
        applyKeyboardInset();
        return;
      }

      if (dockedComposerElement) {
        undock(dockedComposerElement);
      }

      // Hold the space the composer took inside the card, or the comment list jumps up the instant the shell goes
      // fixed and jumps back on undock. Measured before the class is added, so it is the in-card height.
      var reservedHeightInPixels = composerElement.offsetHeight;
      composerElement.style.height = reservedHeightInPixels + "px";
      composerElement.classList.add("social-comment-composer--docked");

      dockedComposerElement = composerElement;
      applyKeyboardInset();
      lockBackgroundScroll();
    }

    function undock(composerElement) {
      window.clearTimeout(pendingUndockTimeoutId);
      pendingUndockTimeoutId = 0;

      composerElement.classList.remove("social-comment-composer--docked");
      composerElement.style.removeProperty("height");
      composerElement.style.removeProperty("--tk-composer-keyboard-inset");

      if (dockedComposerElement === composerElement) {
        dockedComposerElement = null;
      }

      // Guarded on nothing being docked, and placed after the field is cleared above: dock() undocks the outgoing
      // composer before adopting the incoming one, and both happen in the same turn - so a swap releases and
      // re-arms with no gesture in between, while a real undock is the only thing that leaves the page free.
      if (!dockedComposerElement) {
        unlockBackgroundScroll();
      }
    }

    function scheduleUndock(composerElement) {
      window.clearTimeout(pendingUndockTimeoutId);
      pendingUndockTimeoutId = window.setTimeout(function () {
        // Focus moved within the composer (attach button, mention suggestion), so the person is still writing.
        var focusedElement = document.activeElement;
        if (focusedElement && composerElement.contains(focusedElement)) {
          return;
        }

        if (focusedComposerElement === composerElement) {
          focusedComposerElement = null;
        }

        undock(composerElement);
      }, undockGraceInMilliseconds);
    }

    function handleComposerFocusIn(event) {
      var composerElement = event.currentTarget;

      window.clearTimeout(pendingUndockTimeoutId);
      pendingUndockTimeoutId = 0;
      focusedComposerElement = composerElement;

      var viewport = window.visualViewport;
      if (!viewport) {
        return;
      }

      trackTallestViewport(viewport);

      // Nothing docks on focus by itself: on a phone the keyboard animates in and handleViewportChanged docks the
      // instant it has, and on a desktop it never arrives - which is the case this ordering exists to leave alone.
      // The exception is focus arriving while the keyboard is ALREADY up, where no further viewport change comes.
      var keyboardIsOpen = isKeyboardOpen(viewport);
      if (keyboardIsOpen) {
        dock(composerElement);
      }
    }

    function handleComposerFocusOut(event) {
      var composerElement = event.currentTarget;
      scheduleUndock(composerElement);
    }

    // Keeps the caret - and with it the keyboard - in place when anything but the text field is tapped inside the
    // docked panel; otherwise the field blurs on mousedown and the panel slides out from under the finger before
    // the click lands. Same trick as the mention list's @onmousedown:preventDefault, and just as free for buttons.
    function handleComposerMouseDown(event) {
      var composerElement = event.currentTarget;
      if (!composerElement.classList.contains("social-comment-composer--docked")) {
        return;
      }

      var tappedElement = event.target;
      if (!tappedElement || typeof tappedElement.closest !== "function") {
        return;
      }

      var textEntryElement = tappedElement.closest("input, textarea, [contenteditable=\"true\"]");
      if (textEntryElement) {
        return;
      }

      event.preventDefault();
    }

    return {
      // Returns whether the composer was found, so a broken wire-up can be logged rather than silently ignored.
      attach: function (composerElementId) {
        if (!composerElementId) {
          return false;
        }

        var composerElement = document.getElementById(composerElementId);
        if (!composerElement) {
          return false;
        }

        // Blazor patches this element in place across re-renders, so the listeners and this flag survive; the
        // guard only keeps a repeated attach from stacking duplicates.
        if (composerElement.dataset.keyboardDockWired === "true") {
          return true;
        }

        composerElement.addEventListener("focusin", handleComposerFocusIn);
        composerElement.addEventListener("focusout", handleComposerFocusOut);
        composerElement.addEventListener("mousedown", handleComposerMouseDown);
        composerElement.dataset.keyboardDockWired = "true";

        // Here rather than at first focus: the earliest point the viewport is known to be at its full, no-keyboard
        // height, which is the baseline every later "is the keyboard up" answer is compared against.
        wireViewportListeners();
        return true;
      },

      detach: function (composerElementId) {
        if (!composerElementId) {
          return;
        }

        var composerElement = document.getElementById(composerElementId);
        if (!composerElement) {
          return;
        }

        composerElement.removeEventListener("focusin", handleComposerFocusIn);
        composerElement.removeEventListener("focusout", handleComposerFocusOut);
        composerElement.removeEventListener("mousedown", handleComposerMouseDown);
        delete composerElement.dataset.keyboardDockWired;

        if (focusedComposerElement === composerElement) {
          focusedComposerElement = null;
        }

        // Also clears the inline height and the class, so a composer disposed while docked does not leave a fixed
        // panel behind on the next page.
        undock(composerElement);
      }
    };
  })();

    function arrayBufferToBase64(blob) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
          let base64String = reader.result.split(',')[1]; // Remove the Data URL prefix
          resolve(base64String);
      };
      reader.onerror = reject;
    });
  }

   async function downloadFileFromStream(fileName, contentStreamReference, mimeType = 'application/pdf') {
     const arrayBuffer = await contentStreamReference.arrayBuffer();
     const blob = new Blob([arrayBuffer]);


     if (isMobileApp() && window.ReactNativeWebView !== undefined) {
       const base64data = await arrayBufferToBase64(blob);

       //call like it is on react => handleDownloadDiploma
       window.ReactNativeWebView?.postMessage(
         JSON.stringify({
           type: 'downloadFile',
           data: base64data,
           fileName: fileName,
           mimeType,
         })
       );
     } else {

       const url = URL.createObjectURL(blob);
       const anchorElement = document.createElement('a');
       anchorElement.href = url;
       anchorElement.download = fileName ?? '';
       anchorElement.click();
       anchorElement.remove();
       URL.revokeObjectURL(url);
     }
   }

   window.tokeroUserAgents = {
     android: 'tokero-android-app',
     ios: 'tokero-ios-app',
     v36: 'LDVMobileApp',
     expo: 'tokero-expo-app',
     tokeroandroid: 'tokero-android',
     tokeroios: 'tokero-ios',
     app: 'tokero-app',
     blogwebview: 'tokero-blog'
   };

     window.isMobileBrowser = () => {
      const userAgent = navigator.userAgent;
      const mobileBrowser = userAgent.includes('Android') || userAgent.includes('Iphone') || userAgent.includes('iPhone');
      return mobileBrowser;
     }

      window.isIOSMobileApp = () => {
      const userAgent = navigator.userAgent;
      const IOSmobileApp = userAgent.includes('tokero-ios-app');
      return IOSmobileApp;
     }

     window.isMobileBrowserAndroid = () => {
      const userAgent = navigator.userAgent;
      const mobileBrowser = userAgent.includes('Android');
      return mobileBrowser;
     }

     window.isMobileBrowserIos = () => {
      const userAgent = navigator.userAgent;
      const mobileBrowser = userAgent.includes('Iphone') || userAgent.includes('iPhone') || userAgent.includes('Ipad') || userAgent.includes('iPad') || userAgent.includes('Ipod') || userAgent.includes('iPod');
      // iPadOS 13+ requests desktop-class sites by default and reports a Macintosh user agent with no
      // 'iPad' in it, so the string test above misses those. A real Mac reports maxTouchPoints 0; an
      // iPad reports 5. Without this an iPad reads as a desktop, which is how the OAuth buttons ended
      // up calling window.open inside an ASWebAuthenticationSession sheet that silently drops popups.
      const desktopClassIpad = userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1;
      return mobileBrowser || desktopClassIpad;
     }

     /* Stamps the visitor's platform on <html> as a single tk-os-* class, so CSS can show exactly the install
        affordance they can act on — a store badge on the phone that will install the app, a QR on the machine
        that cannot install it but can be scanned from one. Used by CommunityAppsLanding's store row and by the
        CommunityAppInstallQrCode component.

        Here rather than in a per-page <script> for two reasons. It is stamped on the <html> element, which
        survives every Blazor enhanced navigation, so one pass at document load answers the question for the
        whole session — whereas a script inside a routed page is NOT executed when that page arrives through
        enhanced navigation, and the class would simply never appear. And it is decided in the browser rather
        than on the server because the pages that read it are served from the anonymous output cache: one
        response is reused for every visitor, so a User-Agent branch on the server would let the first visitor
        of the TTL window decide what every later one sees.

        Runs at load, immediately after the helpers above are defined — this is a plain blocking script, so the
        class is in place before anything that reads it can run. Detection goes through those shared helpers
        rather than a private copy of the same regexes: they already carry the cases that catch people out,
        notably that iPadOS 13+ asks for desktop-class sites and reports a "Macintosh" UA with no "iPad" in it,
        distinguishable only by having touch points. */
     (function stampPlatformOnDocumentElement() {
       let platformClass = 'tk-os-desktop';
       try {
         /* Android first: an Android user agent can also carry "like Mac OS X" wording, and no device is both. */
         if (window.isMobileBrowserAndroid()) {
           platformClass = 'tk-os-android';
         } else if (window.isMobileBrowserIos()) {
           platformClass = 'tk-os-ios';
         } else if (window.isMobileBrowser()) {
           platformClass = 'tk-os-mobile';
         }
       } catch (platformDetectionError) {
         /* Fail towards the arrangement that works everywhere: both store badges are ordinary links a reader
            can pick between, while a QR drawn on a phone would be useless. Never leave them with no way in. */
         platformClass = 'tk-os-mobile';
       }
       document.documentElement.classList.add(platformClass);
     })();

    window.isMobileApp = () => {
     const userAgent = navigator.userAgent;

     const knownUA = Object.values(window.tokeroUserAgents || {});
     const hasKnownMobileAppUserAgent = knownUA.some(ua => userAgent.includes(ua));

     if (hasKnownMobileAppUserAgent) {
       return true;
     }

     // if there is no custom user agent, is not mobile app
     return false;
   };

   window.setMobileStatusBarColor = (color, textStyle) => {
     if (!window.ReactNativeWebView || !window.isMobileApp || !window.isMobileApp()) return;
     if (!color) return;
     window.ReactNativeWebView.postMessage(JSON.stringify({
       type: 'setStatusBarColor',
       color: color,
       textStyle: textStyle === 'light' ? 'light' : 'dark'
     }));
   };

   window.resetMobileStatusBar = () => {
     if (!window.ReactNativeWebView || !window.isMobileApp || !window.isMobileApp()) return;
     window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'resetStatusBar' }));
   };

   // Switches the shell's swipe-down refresh gesture off while a drag surface is open (the photo cropper).
   // On Android the WebView sits inside a SwipeRefreshLayout, which decides in onInterceptTouchEvent —
   // natively, before the WebView ever sees the move — whether a downward drag is a refresh, and its test is
   // "can the child scroll up?". A full-screen overlay sits on a document still at scroll top, so every drag
   // inside the cropper read as a pull-to-refresh and reloaded the page mid-edit. Nothing the page itself can
   // do prevents that, which is why the native side exposes this switch. iOS ignores the message: its
   // RefreshView only triggers on a genuine scroll-view overscroll.
   //
   // Anything other than an explicit false releases the caller's hold — losing a refresh gesture is
   // recoverable, leaving it dead for the rest of the session is not.
   //
   // The native switch is one flag, so each caller names itself and the gesture comes back only once no owner
   // still holds it off: the phone drawer closing on the DMs page must not re-enable it under the message list.
   // The set lives with the document, as the shell's own re-enable on a full page load does.
   const pullToRefreshDisabledOwners = new Set();
   window.setMobileWebViewPullToRefresh = (enabled, owner) => {
     if (!window.ReactNativeWebView || !window.isMobileApp || !window.isMobileApp()) return;
     if (enabled === false) {
       pullToRefreshDisabledOwners.add(owner);
     } else {
       pullToRefreshDisabledOwners.delete(owner);
     }

     window.ReactNativeWebView.postMessage(JSON.stringify({
       type: 'setPullToRefresh',
       enabled: pullToRefreshDisabledOwners.size === 0
     }));
   };

   // Every bottom sheet holds the gesture off while it is on screen, with no code in the sheet: a sheet is a fixed
   // overlay on a document still at scroll top, so on Android a drag inside it reads as a pull-to-refresh. The
   // three sheet surfaces (.tk-sheet, the .tk-dialog-sheet MudDialogs, the .tk-panel-sheet overlays) are in the
   // DOM exactly while open or sliding down, so their presence is the switch, and a sheet disposed by a
   // navigation away releases it by leaving the DOM. Mobile app only - nothing else has the gesture.
   const sheetSelector = '.tk-sheet, .mud-dialog.tk-dialog-sheet, .tk-panel-sheet';
   let isSheetHoldingPullToRefresh = false;
   let isSheetCheckScheduled = false;
   const syncPullToRefreshWithSheets = () => {
     isSheetCheckScheduled = false;
     const hasSheet = document.querySelector(sheetSelector) !== null;
     if (hasSheet === isSheetHoldingPullToRefresh) {
       return;
     }

     isSheetHoldingPullToRefresh = hasSheet;
     window.setMobileWebViewPullToRefresh(!hasSheet, 'bottom-sheet');
   };
   const scheduleSheetCheck = () => {
     if (isSheetCheckScheduled) {
       return;
     }

     // One check per frame however many nodes a render touched: Blazor applies a render as many mutations.
     isSheetCheckScheduled = true;
     window.requestAnimationFrame(syncPullToRefreshWithSheets);
   };
   if (document.body && window.isMobileApp()) {
     new MutationObserver(scheduleSheetCheck).observe(document.body, { childList: true, subtree: true });
     syncPullToRefreshWithSheets();
   }

   // Auth messages for the native shell. Both live here rather than inline on a page: three pages send one of
   // them (Logout, the Exchange AccountDashboard duplicate-account flow, and the login stuck-session watchdog),
   // and while they were page-local AccountDashboard's call hit an undefined function. `path` is where the shell
   // should take the WebView next; callers with no better destination omit it and get the generic mobile app's
   // own login screen, which is the only path the shell used to be given.
   window.postWebViewAuthMessage = function (messageType, path) {
     if (!window.ReactNativeWebView || !window.ReactNativeWebView.postMessage) {
       return;
     }

     let targetPath = path;
     if (!targetPath) {
       targetPath = '/login';
     }

     const message = {
       type: messageType,
       isAuthenticated: false,
       path: targetPath,
       hasMobileScreen: false
     };

     window.ReactNativeWebView.postMessage(JSON.stringify(message));
   };

   // The web session has just been signed out; the shell must drop its own auth state to match.
   //
   // Android injects the bridge after page load. Wait for its callable receiver before handing navigation to the
   // shell, which clears its persisted credentials and WebView cookies before displaying the signed-out screen.
   window.sendLogoutMessageToWebView = async function (path) {
     const bridgeWaitLimitMs = 10000;
     const bridgeWaitStepMs = 100;
     let bridgeWaitedMs = 0;
     while (bridgeWaitedMs < bridgeWaitLimitMs) {
       if (typeof window.ReactNativeWebView?.postMessage === 'function') {
         window.postWebViewAuthMessage('logout', path);
         return true;
       }
       await new Promise(resolve => setTimeout(resolve, bridgeWaitStepMs));
       bridgeWaitedMs += bridgeWaitStepMs;
     }

     console.error('Native logout bridge did not become available.');
     return false;
   };

   // Keep the POST, native notification and navigation in the browser: changing the cookie can outlive the
   // circuit that initiated logout. The latch also stops circuit recovery from reloading during this sequence.
   window.completeLocalLogout = async function (postLogoutPath, isNativeMobileAppRequest, webViewPostLogoutPath) {
     if (window.tokeroLogoutInProgress) {
       return;
     }
     window.tokeroLogoutInProgress = true;

     const abortController = new AbortController();
     const requestTimeout = setTimeout(function () { abortController.abort(); }, 15000);
     let nativeOwnsNavigation = false;
     try {
       const response = await fetch('/api/auth/logout/', {
         method: 'POST',
         credentials: 'same-origin',
         signal: abortController.signal
       });
       if (!response.ok) {
         console.error('Local logout failed with HTTP status', response.status);
         return;
       }

       const signedOut = await response.json();
       if (signedOut !== true) {
         console.error('Local logout did not confirm sign-out.');
         return;
       }

       clearTimeout(requestTimeout);
       // A sibling tab or a closed channel must not prevent the native shell from clearing its own session.
       try {
         window.broadcastAuth();
       } catch (error) {
         console.error('Logout auth broadcast failed.', error);
       }

       if (isNativeMobileAppRequest) {
         nativeOwnsNavigation = await window.sendLogoutMessageToWebView(webViewPostLogoutPath);
       }
     } catch (error) {
       console.error('Local logout failed.', error);
     } finally {
       clearTimeout(requestTimeout);
       if (!nativeOwnsNavigation) {
         window.location.replace(postLogoutPath);
       }
     }
   };

   // Same channel, different type: the login page's watchdog reports a session that never became usable, so the
   // shell restarts its sign-in instead of leaving the user on a spinner.
   window.sendSessionStuckMessageToWebView = function (path) {
     window.postWebViewAuthMessage('sessionStuck', path);
   };


   window.initiateInBrowserCountdown = (dotNetHelper, timeoutMs, onSecondPassedName, onCountdownEndName) =>
   {
     const intervalLengthMs = 1000; // 1 second
     let remainingSeconds = Math.ceil(timeoutMs / intervalLengthMs);

     const tickingInterval = setInterval(() =>
     {
       remainingSeconds -= 1;

       if (onSecondPassedName)
       {
         dotNetHelper.invokeMethodAsync(onSecondPassedName, remainingSeconds);
       }

       if (remainingSeconds <= 0)
       {
         if (onCountdownEndName)
         {
           dotNetHelper.invokeMethodAsync(onCountdownEndName);
         }

         clearInterval(tickingInterval);
       }
     }, intervalLengthMs)
   }

   window.dispatchBlazorLoaded = function () {
     const event = new CustomEvent("blazorLoaded");
     window.dispatchEvent(event);
   };

   // ApplicationLayoutWrapper's first interactive render hands its setup steps to this function in one list - each a
   // { functionName, stepArguments } pair, run in order - rather than calling each window.* helper on its own. Every
   // interop call is a round trip between the browser and the server, and the page is not ready - window.blazorReady
   // is not set - until the last of them has returned, so helpers called one at a time cost one round trip each for a
   // few milliseconds of work. The layout owns the list, so what runs here and what it falls back to calling one at a
   // time are the same helpers.
   //
   // Each step stays independent: a helper this page does not define is skipped, and one that throws or rejects is
   // skipped the same way, so no step can cancel the ones after it - onAfterRender, which sets window.blazorReady,
   // included. A step must settle, though: one whose promise never settles holds every step after it.
   window.runLayoutInteropSteps = async function (steps) {
     for (const step of steps) {
       const stepFunction = window[step.functionName];
       if (typeof stepFunction !== 'function') {
         continue;
       }
       try {
         await stepFunction(...step.stepArguments);
       } catch (stepError) {
         // Skipped, as a missing helper is: the steps after it still run.
       }
     }
   };

   window.isSafari = () =>
   {
     const userAgent = navigator.userAgent;
     const isChrome = userAgent.includes('Chrome') || userAgent.includes('CriOS');
     const isSafari = !isChrome && userAgent.includes('Safari');

     if(isSafari)
     {
       return true;
     }

     return false;
   }

   window.getCurrentUrl = function() {
       if (typeof window.location === "undefined" || window.location === null || typeof window.location.href === "undefined" || window.location.href === null) {
           return "";
       }
       return window.location.href;
   };

   window.getWindowName = function() {
       if (typeof window.name === "undefined" || window.name === null) {
           return "";
       }
       return window.name;
   };

     window.getPageTitle = function() {
       if (typeof document.title === "undefined" || document.title === null) {
           return "";
       }
       return document.title;
   };

   window.fromBase64 = (data) =>
   {
     const decodedData = atob(data);
     const chunks8 = new Uint8Array(decodedData.length);

     for (let i = 0; i < chunks8.length; i += 1) {
       chunks8[i] = decodedData.charCodeAt(i);
     }

     /**
      * What this does is it takes every two 8-bit chunks and at each index of the 16-bit array
      * it stores `256 * chunks8[oddIndex] + chunks8[evenIndex]`
      */
     const chunks16 = new Uint16Array(chunks8.buffer);

     return String.fromCharCode(...chunks16);
   }

    window.toBase64 = (data) =>
   {
     const chunks16 = new Uint16Array(data.length);

     for (let i = 0; i < chunks16.length; i += 1) {
       chunks16[i] = data.charCodeAt(i);
     }

     /**
      * What this does is it takes each 16-bit chunk and performs the following operations:
      * 1. At the even index, it stores `chunk16 % 256`
      * 2. At the odd index, it stores `Math.floor(chunk16 / 256)`
      */
     const chunks8 = new Uint8Array(chunks16.buffer);
     const safeData = String.fromCharCode(...chunks8);

    return btoa(safeData);
   }

   window.tryLoadFromLocalStorage = (key) => {
     const serializedValue = window.localStorage.getItem(key);
       if (!serializedValue) {
         return undefined;
       }
     const decodedData = fromBase64(serializedValue);
     return JSON.parse(decodedData);
    }

   window.trySaveToLocalStorage = (key, value) => {
     const serializedValue = JSON.stringify(value);
     const encodedData = toBase64(serializedValue);
     window.localStorage.setItem(key, encodedData);
    }

   window.authChannel = window.authChannel || new BroadcastChannel('auth-channel');


   window.broadcastAuth = function() {
       window.authChannel.postMessage({});
   }

   window.subscribeToAuthChannel = function(dotnetRef) {
       window.authChannel.onmessage = function () {
           dotnetRef.invokeMethodAsync('OnAuthBroadcast');
       }
   }

   // Called once per full page load by the layout. A server-side SSO auth-state change (login / account switch /
   // logout) sets this flag cookie on its redirect because it never reaches the Blazor components that call
   // broadcastAuth. The landing tab clears the flag and broadcasts so sibling tabs on THIS origin force-redirect
   // to the new auth state at once. Cookie name mirrors CookiesConstants.Names.AuthStateChangedBroadcast.
   window.consumeAuthBroadcastFlag = function() {
       const cookieName = 'tk_auth_broadcast';
       const hasFlag = document.cookie.split('; ').some(entry => entry.indexOf(cookieName + '=') === 0);
       if (!hasFlag) {
           return;
       }
       document.cookie = cookieName + '=; Max-Age=0; Path=/';
       if (typeof window.broadcastAuth === 'function') {
           window.broadcastAuth();
       }
   }

   window.balanceChannel = window.balanceChannel || new BroadcastChannel('balance-channel');

   window.broadcastBalance = function(coinId) {
       window.balanceChannel.postMessage({coinId : Number(coinId)});
   }

   window.subscribeToBalanceChannel = function(dotnetRef) {
        window.balanceChannel.onmessage = function (event) {
          const coinId = event.data?.coinId || null;
            dotnetRef.invokeMethodAsync('OnBalanceChange', coinId);
        }
    }

   window.corporateChannel = window.corporateChannel || new BroadcastChannel('corporate-channel');

   window.broadcastCorporate = function(legalUserId) {
       window.corporateChannel.postMessage({ legalUserId: legalUserId });
   }

   window.subscribeToCorporateChannel = function(dotnetRef) {
       window.corporateChannel.onmessage = function (event) {
           const legalUserId = event.data?.legalUserId || null;
           dotnetRef.invokeMethodAsync('OnCorporateBroadcast', legalUserId);
       }
   }

   window.scrollToGridRowByClassId = function (itemId, objectTypeName, duration=3500) {
       try {
         let row = undefined;
         if (objectTypeName) {
           row = document.querySelector(`.grid-row-${objectTypeName}-id-${itemId}`);
         } else {
           const regex = new RegExp(`^grid-row-(\\w+)-id-${itemId}$`);
           const matchingRows = Array.from(document.querySelectorAll('[class]')).filter(el =>
             Array.from(el.classList).some(className => regex.test(className))
           );

           if (matchingRows && matchingRows.length > 0) {
             row = matchingRows[0];
           }
         }

         if (row) {
             row.scrollIntoView({ behavior: 'smooth', block: 'center' });
             row.classList.add('highlight-row');
               setTimeout(() => {
                   row.classList.remove('highlight-row');
               }, duration);

               return true;
         }
       } catch (error) {
           console.error("Error scrolling to grid row by class ID:", error);
       }

       return false;
   };

   window.scrollToGridRowId = function (itemId, objectTypeName, duration=3500) {
       try {

         let row = undefined;
         if (objectTypeName) {
           row = document.getElementById(`grid-row-${objectTypeName}-id-${itemId}`);
         } else {
           const regex = new RegExp(`^grid-row-(\\w+)-id-${itemId}$`);
           row = Array.from(document.querySelectorAll('[id]')).find(el =>
             regex.test(el.id)
           );
         }

         if (row) {
             row.scrollIntoView({ behavior: 'smooth', block: 'center' });
             row.classList.add('highlight-row');
               setTimeout(() => {
                   row.classList.remove('highlight-row');
               }, duration);

               return true;
         }
       } catch (error) {
           console.error("Error scrolling to grid row by ID:", error);
       }
       return false;
   };

   window.scrollPageToTop = function () {
       window.scrollTo(0, 0);
       if (document.scrollingElement) { document.scrollingElement.scrollTop = 0; }
       if (document.documentElement) { document.documentElement.scrollTop = 0; }
       if (document.body) { document.body.scrollTop = 0; }
       // Some MudBlazor layouts make an inner div the scroll container — scroll any
       // element that is currently scrolled past the top.
       var scrollables = document.querySelectorAll('main, .mud-main-content, .layout, .blazor-element');
       for (var i = 0; i < scrollables.length; i++) {
         if (scrollables[i].scrollTop > 0) {
           scrollables[i].scrollTop = 0;
         }
       }
   };

   //Pin the current scroll position for a short window so a programmatic re-render cannot bump the page
   window.tkPreserveScroll = function (durationMs) {
       try {
           var limit = 600;
           if (typeof durationMs === 'number' && durationMs > 0) {
               limit = durationMs;
           }

           var targets = [];
           targets.push({ el: window, x: window.scrollX, y: window.scrollY });

           var candidates = document.querySelectorAll('main, .mud-main-content, .layout, .blazor-element, .socialfi-content, .socialfi-canvas, html, body');
           for (var i = 0; i < candidates.length; i++) {
               var candidate = candidates[i];
               targets.push({ el: candidate, x: candidate.scrollLeft, y: candidate.scrollTop });
           }

           var startTs = null;
           var step = function (ts) {
               if (startTs === null) {
                   startTs = ts;
               }

               for (var j = 0; j < targets.length; j++) {
                   var target = targets[j];
                   if (target.el === window) {
                       if (Math.abs(window.scrollY - target.y) > 1 || Math.abs(window.scrollX - target.x) > 1) {
                           window.scrollTo(target.x, target.y);
                       }
                   }
                   else {
                       if (Math.abs(target.el.scrollTop - target.y) > 1) {
                           target.el.scrollTop = target.y;
                       }
                       if (Math.abs(target.el.scrollLeft - target.x) > 1) {
                           target.el.scrollLeft = target.x;
                       }
                   }
               }

               if (ts - startTs < limit) {
                   window.requestAnimationFrame(step);
               }
           };
           window.requestAnimationFrame(step);
       }
       catch (e) {
           // Best-effort only: scroll preservation must never throw and break the caller.
       }
   };

   window.scrollToGridRowById = function (itemId, objectTypeName, duration=3500) {
       let result = scrollToGridRowId(itemId, objectTypeName, duration);
       if (!result) {
         result = scrollToGridRowByClassId(itemId, objectTypeName, duration);
       }

       return result;
   };

     window.getWindowDimensions = () => {
       return {
         width: window.innerWidth,
         height: window.innerHeight
       }
     };

     window.getScreenDimensions = () => {
       return {
         width: window.screen.width,
         height: window.screen.height
       }
     };

     /* window.open for a connect popup that settles only once the popup is closed, so the connection switch that
        opened it stays on for as long as it is open (OpenCenteredWindow, isAwaitingClose). A connect that succeeds
        navigates this page from inside the popup just before closing it; beforeunload sees that navigation and the
        promise is left unsettled, so the switch does not drop to off while the page reloads connected. A popup the
        browser blocked settles at once. */
     window.openWindowUntilClosed = (url, windowFeatures) => {
       const popup = window.open(url, '_blank', windowFeatures);
       if (!popup) {
         return Promise.resolve();
       }

       let isPageLeaving = false;
       const markPageLeaving = () => { isPageLeaving = true; };
       window.addEventListener('beforeunload', markPageLeaving, { once: true });

       return new Promise((resolve) => {
         const closedPoll = setInterval(() => {
           if (!popup.closed) {
             return;
           }

           clearInterval(closedPoll);
           /* The listener belongs to this popup: removed with it, or every popup opened and cancelled leaves one on
              window for the life of the document, which enhanced navigation keeps alive. */
           window.removeEventListener('beforeunload', markPageLeaving);
           if (!isPageLeaving) {
             resolve();
             return;
           }

           /* A real unload never lets this fire. A beforeunload that did not end in one - a cancelled navigation -
              would otherwise leave the promise, and the switch waiting on it, pending for good. */
           setTimeout(resolve, 5000);
         }, 500);
       });
     };

     /* One listener for the whole circuit. Every caller attaches this from AfterRenderAsync(firstRender), so
        without the swap each mount stacked another listener holding that mount's DotNetObjectReference: an OAuth
        error then fired all of them, and the ones whose component was already gone rejected with "no tracked
        object with id". The newest caller is the surface whose popup is open, so it is the one that keeps it. */
     window.oAuthErrorListener = function (dotnetRef) {
       if (window._oAuthErrorHandler) {
         window.removeEventListener('message', window._oAuthErrorHandler);
       }

       window._oAuthErrorHandler = (event) => {
         if (event.origin !== window.location.origin) {
             return;
         }

         if (event.data?.type === 'oauth-error' && typeof event.data.message === 'string') {
             const showErrorPromise = dotnetRef.invokeMethodAsync('ShowError', event.data.message);
             if (showErrorPromise && showErrorPromise.catch) {
               /* The circuit or the component can be gone by now; there is no one left to show the message to. */
               showErrorPromise.catch(() => { });
             }
         }
       };

       window.addEventListener('message', window._oAuthErrorHandler);
     };

     // NOTE: the SSO popup opener lives in the shared SsoLoginButton component (Tokero.Shared.Blazor),
     // kept self-contained there so the button has no load-order dependency on this file.

     window.blockMinusEverywhere = (elementId) => {
     const el = document.getElementById(elementId);
     if (!el) return;

     el.addEventListener('input', (e) => {
       const original = el.value;
       const sanitized = original.replace(/-/g, '');
       if (original !== sanitized) {
         el.value = sanitized;
         el.selectionStart = el.selectionEnd = sanitized.length;
         el.dispatchEvent(new Event('input', { bubbles: true }));
       }
     });
   };

    // Focus behaviour for the right-aligned numeric fields (.right-aligned-input) of the portfolio add/edit asset
    // dialog and its bond / private-investment fragments. Two things happen when one is tapped:
    //
    //   * a field still holding the default the dialog opened it on is emptied, so the first digit typed replaces
    //     that default instead of being appended to it. Which value counts as the default is declared per field
    //     via data-tk-default-value, because it is NOT always zero — the amount field opens on 1 — and because a
    //     field that has no default at all (an optional decimal? that opens empty) must never be emptied;
    //   * a field holding any other value gets the caret dropped after its last digit instead of in front of the
    //     first one. Right-aligned text leaves the blank half of the box on the LEFT, and tapping that blank put
    //     the caret at position 0, so typing prefixed the existing number.
    //
    // Same behaviour as the exchange buy/sell amount fields (HandleFocus/HandleBlur in ExchangeBuy.razor and
    // ExchangeSell.razor), done once here rather than as a pair of Blazor @onfocus/@onblur handlers on each of the
    // dialog's numeric fields, for two reasons. The caret half is not expressible in C# at all: MudNumericField
    // renders <input type="number">, whose selection API throws InvalidStateError, so the only way to move the
    // cursor is the DOM's own rule that assigning .value on a focused input parks the cursor at the end of the
    // text. And several of those fields bind a non-nullable decimal/int, which has no way to represent "empty" —
    // the exchange trick of assigning null works there only because its amounts are decimal?.
    //
    // Nothing is reported back to Blazor, deliberately. The cleared text lives in the DOM only while the field is
    // focused and untouched, and blurring without typing puts it back, so Blazor's render tree is never out of
    // step with what is on screen — the mirror of the exchange fields' HandleBlur restoring 0. As soon as the user
    // types, the browser's own input event drives MudNumericField's binding as usual and this stops interfering.
    window.enableRightAlignedNumericFieldFocusBehavior = function () {
      // The listeners are delegated from the document and never removed, so wiring them twice would run every
      // handler twice. Blazor re-renders (and re-invokes this from AfterRenderAsync) far more often than the page
      // is loaded.
      if (window.tkRightAlignedNumericFieldFocusWired === true) {
        return;
      }
      window.tkRightAlignedNumericFieldFocusWired = true;

      // Capture phase, so this runs before the browser moves focus. A pointer press on a field that is NOT yet the
      // active element is the press that will focus it, and that is the only press whose caret position gets
      // overridden below — a later click inside an already-focused field is the user deliberately picking a spot to
      // edit, and must be left alone.
      document.addEventListener("mousedown", function (pressEvent) {
        markPointerGestureThatWillFocus(pressEvent.target);
      }, true);

      document.addEventListener("touchstart", function (pressEvent) {
        markPointerGestureThatWillFocus(pressEvent.target);
      }, true);

      // Delegated from the document rather than bound per element: the fields are conditionally rendered per asset
      // category and live in three different components, so there is no single render pass at which they all
      // exist to be wired.
      document.addEventListener("focusin", function (focusEvent) {
        var inputElement = resolveRightAlignedNumericInput(focusEvent.target);
        if (!inputElement) {
          return;
        }

        var originalText = inputElement.value || "";
        if (originalText === "") {
          // An empty optional field. Nothing to clear, and the caret already sits at the right edge because the
          // text is right-aligned.
          return;
        }

        // Compared numerically rather than as text, so a default of "1" still matches a field that MudNumericField
        // rendered as "1.00" under a Format. A field with no data-tk-default-value yields NaN here, and NaN never
        // equals anything — including itself — so it falls through to the caret move, which is what it wants.
        var declaredDefault = resolveDeclaredNumericDefault(inputElement);
        if (parseFloat(originalText) === declaredDefault) {
          inputElement.dataset.tkClearedDefault = originalText;
          inputElement.value = "";
          return;
        }

        // Deferred to the next task, NOT done here. Focus is granted inside the pointer press's default action and
        // the caret is positioned from the press coordinates immediately afterwards, so a caret moved during
        // focusin is overwritten by the click that caused the focus — which is exactly how this shipped broken the
        // first time. Keyboard and programmatic focus have no such press, and this timeout is the only thing that
        // serves them.
        scheduleCaretMoveToEnd(inputElement);
      });

      // Fires after mouseup / touchend, so every caret placement the browser does for this gesture is already
      // behind us. Belt-and-braces alongside the focusin timeout above: the two orderings differ between engines
      // and between a plain click and a click that drags a fraction of a pixel, and parking the caret at the end
      // twice is indistinguishable from doing it once.
      document.addEventListener("click", function (clickEvent) {
        var inputElement = resolveRightAlignedNumericInput(clickEvent.target);
        if (!inputElement) {
          return;
        }

        if (inputElement.dataset.tkFocusingPointerGesture !== "1") {
          return;
        }

        delete inputElement.dataset.tkFocusingPointerGesture;
        scheduleCaretMoveToEnd(inputElement);
      });

      document.addEventListener("input", function (inputEvent) {
        var inputElement = resolveRightAlignedNumericInput(inputEvent.target);
        if (!inputElement) {
          return;
        }

        // The user has taken over. From here MudNumericField's own binding owns the text, so the restore below
        // must not put the old default back over whatever was typed, and the caret must stay where they are typing.
        delete inputElement.dataset.tkClearedDefault;
        delete inputElement.dataset.tkFocusingPointerGesture;
      });

      // Registered in the capture phase so it runs before Blazor's own document-level change listener. Chrome and
      // Firefox fire change only when the USER edited the field, so a field we cleared and nobody typed into fires
      // nothing and this never runs; WebKit has historically compared the value at focus against the value at
      // blur, which would hand MudNumericField an empty string for a field that was only tapped. Putting the
      // default back first means such a change carries the value Blazor already holds and lands as a no-op. It has
      // to happen here rather than in focusout below, because change fires before blur.
      document.addEventListener("change", function (changeEvent) {
        var inputElement = resolveRightAlignedNumericInput(changeEvent.target);
        if (!inputElement) {
          return;
        }

        restoreClearedNumericFieldDefault(inputElement);
      }, true);

      document.addEventListener("focusout", function (blurEvent) {
        var inputElement = resolveRightAlignedNumericInput(blurEvent.target);
        if (!inputElement) {
          return;
        }

        // A press that never produced a click on this field (dragged off it, cancelled) leaves the marker behind
        // otherwise, and the next click would be treated as the focusing one.
        delete inputElement.dataset.tkFocusingPointerGesture;
        restoreClearedNumericFieldDefault(inputElement);
      });
    };

    // The value the field opened on, as declared by the Razor markup, or NaN when it declares none — which is the
    // case for every optional decimal? field (they open empty) and for every field in an EDIT, where the number on
    // screen is the user's saved value and emptying it on a tap would look like losing it.
    //
    // closest() rather than a direct dataset read because it matches the element itself first and then walks up:
    // MudBlazor splats an unmatched attribute onto the <input> it renders, which is where this lands today, and the
    // walk keeps the behaviour working if a Mud upgrade moves user attributes to the outer control instead of
    // silently switching it off.
    function resolveDeclaredNumericDefault(inputElement) {
      var declaringElement = inputElement.closest("[data-tk-default-value]");
      if (!declaringElement) {
        return NaN;
      }

      return parseFloat(declaringElement.dataset.tkDefaultValue);
    }

    // Flags the pointer press that is about to focus a right-aligned numeric field, so the click it turns into can
    // tell itself apart from a click inside a field that already had focus.
    function markPointerGestureThatWillFocus(eventTarget) {
      var inputElement = resolveRightAlignedNumericInput(eventTarget);
      if (!inputElement) {
        return;
      }

      if (document.activeElement === inputElement) {
        return;
      }

      inputElement.dataset.tkFocusingPointerGesture = "1";
    }

    // Parks the caret after the last character, on the next task rather than now — see the focusin handler for why
    // doing it synchronously loses to the browser's own caret placement.
    function scheduleCaretMoveToEnd(inputElement) {
      window.setTimeout(function () {
        moveNumericFieldCaretToEnd(inputElement);
      }, 0);
    }

    function moveNumericFieldCaretToEnd(inputElement) {
      // Focus may have moved on, or Blazor may have replaced the node, in the task between scheduling and running.
      if (document.activeElement !== inputElement) {
        return;
      }

      var currentText = inputElement.value || "";
      if (currentText === "") {
        return;
      }

      // Assigning .value on a focused input parks the cursor at the end of the text; it has to actually change for
      // the browser to do that, hence the round trip through the empty string. Neither assignment raises an input
      // event, so Blazor sees none of this and the value it renders is unchanged.
      inputElement.value = "";
      inputElement.value = currentText;

      // And then the selection API on top, for any engine that does not move the cursor on assignment. It does not
      // apply to type="number" and throws InvalidStateError there, which is why the result is discarded rather
      // than relied on — whichever of the two works, the caret ends up in the same place.
      try {
        inputElement.setSelectionRange(currentText.length, currentText.length);
      } catch (selectionNotSupported) {
        // type="number" in Chrome and Firefox. The assignment above is what does the work in those engines.
      }
    }

    // Puts back the default that focusin hid, for a field the user never typed into, so the DOM goes back to
    // matching the value Blazor still holds — none of the clearing was ever reported to it.
    function restoreClearedNumericFieldDefault(inputElement) {
      var clearedText = inputElement.dataset.tkClearedDefault;
      if (clearedText === undefined) {
        return;
      }

      delete inputElement.dataset.tkClearedDefault;

      // Only restore into a field that is still empty. Blazor can legitimately have written a new value here while
      // the field was focused (changing the dialog's base coin reconverts the price fields), and that value has to
      // win over the default we hid.
      if (inputElement.value === "") {
        inputElement.value = clearedText;
      }
    }

    // The marker class sits on MudBlazor's outer .mud-input-control wrapper (that is also how the text-align rule
    // in App.css is written), so the input itself has to be matched by walking up to it.
    function resolveRightAlignedNumericInput(eventTarget) {
      if (!(eventTarget instanceof HTMLInputElement)) {
        return null;
      }

      // A field the user cannot type into must keep showing its value. The dialog's auto-priced current-value and
      // the bond yield are rendered Disabled as well, which already stops focusin, but readOnly on its own does
      // not — and emptying a display-only field on tap would read as the value having been lost.
      if (eventTarget.readOnly || eventTarget.disabled) {
        return null;
      }

      var markedControl = eventTarget.closest(".right-aligned-input");
      if (!markedControl) {
        return null;
      }

      return eventTarget;
    }

    window.updateQueryParam = (key, value, replace = true) => {
          const url = new URL(window.location.href);

         if (value === null || value === undefined || value === "") {
             // Remove the key if value is empty
             url.searchParams.delete(key);
         } else {
             // Update or add the key
             url.searchParams.set(key, value);
         }

         // Only prefix "?" when params remain, so stripping the last one yields a clean path (no dangling "?").
         const queryString = url.searchParams.toString();
         let newUrl = url.pathname + url.hash;
         if (queryString) {
             newUrl = url.pathname + "?" + queryString + url.hash;
         }

          if (replace) {
              history.replaceState(null, '', newUrl);
          } else {
              history.pushState(null, '', newUrl);
          }
      };

    window.scrollToElementByIdWithOffset = async function (elementId, topOffset = 0, maxAttempts = 10, delayMs = 100) {
      let attemptsLeft = maxAttempts;
      let previousTargetTop = -1;

      while (attemptsLeft > 0) {
        const element = document.getElementById(elementId);
        if (!element) {
          await new Promise(function (resolve) { window.setTimeout(resolve, delayMs); });
          attemptsLeft--;
          continue;
        }

        const elementRect = element.getBoundingClientRect();
        let targetTop = window.pageYOffset + elementRect.top - topOffset;
        if (targetTop < 0) {
          targetTop = 0;
        }

        window.scrollTo({
          top: targetTop,
          behavior: "smooth"
        });

        if (Math.abs(targetTop - previousTargetTop) <= 2) {
          return true;
        }

        previousTargetTop = targetTop;
        await new Promise(function (resolve) { window.setTimeout(resolve, delayMs); });
        attemptsLeft--;
      }

      return false;
    };

    // True on devices with a real hover-capable pointer (mouse/trackpad); false on touch-only devices.
    window.deviceSupportsHover = function () {
      return window.matchMedia('(hover: hover)').matches;
    };

    window.scrollToUrlFragment = function () {
      if (!location.hash || location.hash.length <= 1) {
        return;
      }
      var targetId = location.hash.substring(1);
      window.scrollToElementByIdWithOffset(targetId, 80, 20, 150);
    };

        // crop script start
    window._croppers = window._croppers || {};

   window.initializePanZoomCropper = function (
     canvasId,
     _,
     fileInputId,
     aspectRatio = 1,
     initialSize = 260
   ) {
     if (window._croppers[canvasId]) {
       window.disposeCropper(canvasId, fileInputId);
     }

     const canvas = document.getElementById(canvasId);
     const input = document.getElementById(fileInputId);
     if (!canvas || !input) {
       console.error("initializePanZoomCropper: missing elements", { canvasId, fileInputId });
       return;
     }

     const ctx = canvas.getContext("2d");

     const viewport = Math.min(window.innerWidth, 420);
     const size = Math.min(Math.max(initialSize, viewport - 32), 420);

       const dpr = window.devicePixelRatio || 1;

  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = size + "px";
  canvas.style.height = size + "px";

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
     canvas.style.touchAction = "none";

     const state = {
       canvas,
       ctx,
       input,
       image: new Image(),
       scale: 1,
       minScale: 1,
       maxScale: 4,
       offsetX: 0,
       offsetY: 0,
       lastX: 0,
       lastY: 0,
       isDragging: false,
       pinchStartDistance: 0,
       pinchStartScale: 1,
     };

     // After the declaration, not before it: `state` is a const in this same scope, so the assignment used to sit
     // in its temporal dead zone and threw before a single listener was attached.
     state.cssSize = size;

     function draw() {
       ctx.clearRect(0, 0, canvas.width, canvas.height);
       if (!state.image.complete || !state.image.naturalWidth) return;

       ctx.save();
       ctx.translate(canvas.width / 2 + state.offsetX, canvas.height / 2 + state.offsetY);
       ctx.scale(state.scale, state.scale);
       ctx.drawImage(state.image, -state.image.width / 2, -state.image.height / 2);
       ctx.restore();
     }

     function onFileChange() {
       const file = input.files?.[0];
       if (!file) return;

       const reader = new FileReader();
       reader.onload = (e) => {
         state.image.onload = () => {
           const scaleX = canvas.width / state.image.width;
           const scaleY = canvas.height / state.image.height;
           state.scale = Math.max(scaleX, scaleY);
           state.minScale = state.scale;
           state.offsetX = 0;
           state.offsetY = 0;
           draw();
           canvas.classList.remove("d-none");
         };
         state.image.src = e.target.result;
       };
       reader.readAsDataURL(file);
     }

     function distance(a, b) {
       return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
     }

     function onTouchStart(e) {
       if (e.touches.length === 1) {
         state.isDragging = true;
         state.lastX = e.touches[0].clientX;
         state.lastY = e.touches[0].clientY;
       }
       if (e.touches.length === 2) {
         state.pinchStartDistance = distance(e.touches[0], e.touches[1]);
         state.pinchStartScale = state.scale;
       }
     }

     function onTouchMove(e) {
       e.preventDefault();

       if (e.touches.length === 1 && state.isDragging) {
         const dx = e.touches[0].clientX - state.lastX;
         const dy = e.touches[0].clientY - state.lastY;
         state.offsetX += dx;
         state.offsetY += dy;
         state.lastX = e.touches[0].clientX;
         state.lastY = e.touches[0].clientY;
         draw();
       }

       if (e.touches.length === 2) {
         const d = distance(e.touches[0], e.touches[1]);
         const scale = state.pinchStartScale * (d / state.pinchStartDistance);
         state.scale = Math.min(state.maxScale, Math.max(state.minScale, scale));
         draw();
       }
     }

     function onTouchEnd() {
       state.isDragging = false;
       state.pinchStartDistance = 0;
     }

     input.addEventListener("change", onFileChange);
     canvas.addEventListener("touchstart", onTouchStart, { passive: false });
     canvas.addEventListener("touchmove", onTouchMove, { passive: false });
     canvas.addEventListener("touchend", onTouchEnd);

     window._croppers[canvasId] = {
       state,
       handlers: { onFileChange, onTouchStart, onTouchMove, onTouchEnd },
       draw
     };
   };

   /**
    * REAL CROPPER (crop box + move + resize).
    * Signature: initializeCropperFromUpload(CanvasId, dotNetRef, InputId, AspectRatio, CropWidth, EdgePadding).
    * dotNetRef is a per-instance Blazor handle (DotNetObjectReference<PhotoEditorDialog>) — we call
    * dotNetRef.invokeMethodAsync('OnCropImageLoaded') when the chosen image finishes decoding. The
    * old global window.onCropImageLoaded + static [JSInvokable] dispatch raced across circuits and
    * routed the callback to whichever dialog last assigned the static Instance slot.
    * edgePadding (default 64) is the total horizontal gap left around the canvas when the viewport is the
    * limiting factor. The canvas is written out below as a fixed px width and never shrinks to its container,
    * so pass at least the host dialog's chrome — see PostMediaDialog.CropperEdgePaddingPixels.
    */
   window.initializeCropperFromUpload = function (
     canvasId,
     dotNetRef,
     fileInputId,
     aspectRatio = 1,
     initialWidth = 200,
     edgePadding = 64
   ) {
     window._croppers = window._croppers || {};

     // cleanup previous instance
     if (window._croppers[canvasId]) {
       window.disposeCropper(canvasId, fileInputId);
     }

     const cropCanvas = document.getElementById(canvasId);
     const fileInput = document.getElementById(fileInputId);

     if (!cropCanvas || !fileInput) {
       console.error("initializeCropperFromUpload: missing elements", { canvasId, fileInputId });
       return;
     }

     const cropCtx = cropCanvas.getContext("2d");
     const resizeHandleSize = 28;
     const resizeHandleHitSize = 36;

     // reset input so selecting same file triggers change
     try { fileInput.value = ""; } catch { }

     const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
     const MAX_CANVAS = Math.max(240, viewportWidth - edgePadding); // keep padding (edgePadding=0 => full width)
     const CANVAS_SIZE = Math.min(Math.max(initialWidth * 2, 260), MAX_CANVAS);

      const state = {
      cropCanvas,
      cropCtx,
      fileInput,
      dotNetRef,
      aspectRatio: aspectRatio ? parseFloat(aspectRatio) : null,
      initialWidth,

       cropX: 0,
       cropY: 0,
       cropWidth: initialWidth,
       cropHeight: aspectRatio ? initialWidth / aspectRatio : initialWidth,

       isResizing: false,
       isMoving: false,
       dragOffsetX: 0,
       dragOffsetY: 0,

       image: new Image(),

       // where the image is drawn in canvas coords
       imageDrawX: 0,
       imageDrawY: 0,
       imageDrawWidth: 0,
       imageDrawHeight: 0,

       activePointerId: null,
       resizeHandleSize,
       resizeHandleHitSize
     };

     cropCanvas.style.touchAction = "none";
       const dpr = window.devicePixelRatio || 1;

   // real pixel size
   cropCanvas.width = CANVAS_SIZE * dpr;
   cropCanvas.height = CANVAS_SIZE * dpr;

   // visual size (CSS pixels)
   cropCanvas.style.width = CANVAS_SIZE + "px";
   cropCanvas.style.height = CANVAS_SIZE + "px";

   // scale drawing to match DPI
   cropCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
   state.cropCssSize = CANVAS_SIZE;

     function draw() {
       const s = state;
       const ctx = s.cropCtx;
       const size = s.cropCssSize;

       ctx.clearRect(0, 0, size, size);

       // background
       ctx.fillStyle = "#f4f4f5";
       ctx.fillRect(0, 0, size, size);

       // image
       if (s.image && s.image.complete && s.image.naturalWidth) {
         ctx.drawImage(s.image, s.imageDrawX, s.imageDrawY, s.imageDrawWidth, s.imageDrawHeight);
       }

       // overlay — four bands around the crop box. Everything in this draw() routine works in
       // CSS coordinates (size = cropCssSize) because the context is pre-scaled by setTransform(dpr).
       // The previous version subtracted the crop box with an evenodd two-rect path whose OUTER rect
       // used s.cropCanvas.width/height (device pixels = CANVAS_SIZE * dpr). On a 1:1 display those
       // numbers equal `size`, so it looked correct; but on Edge at Windows display scaling (dpr 1.25/
       // 1.5) the outer rect was dpr-times oversized inside the already-scaled context and the evenodd
       // hole no longer registered, washing the whole image with the dark overlay. Painting explicit
       // bands in CSS coordinates avoids both the device/CSS mismatch and any evenodd cross-browser quirk.
       ctx.save();
       ctx.fillStyle = "rgba(0,0,0,0.45)";
       const cropRight = s.cropX + s.cropWidth;
       const cropBottom = s.cropY + s.cropHeight;

       ctx.beginPath();

       // top
       ctx.fillRect(0, 0, size, s.cropY);
       // bottom
       ctx.fillRect(0, cropBottom, size, size - cropBottom);
       // left
       ctx.fillRect(0, s.cropY, s.cropX, s.cropHeight);
       // right
       ctx.fillRect(cropRight, s.cropY, size - cropRight, s.cropHeight);
       ctx.restore();

       // border
       ctx.save();
       ctx.setLineDash([6, 4]);
       ctx.strokeStyle = "white";
       ctx.lineWidth = 2;
       ctx.strokeRect(s.cropX, s.cropY, s.cropWidth, s.cropHeight);
       ctx.restore();

       // handle
       const handleX = s.cropX + s.cropWidth - s.resizeHandleSize;
       const handleY = s.cropY + s.cropHeight - s.resizeHandleSize;
       ctx.fillStyle = "white";
       ctx.strokeStyle = "rgba(0,0,0,0.35)";
       ctx.lineWidth = 1;
       ctx.fillRect(handleX, handleY, s.resizeHandleSize, s.resizeHandleSize);
       ctx.strokeRect(handleX, handleY, s.resizeHandleSize, s.resizeHandleSize);

       // diagonal grip lines
       ctx.strokeStyle = "rgba(0,0,0,0.55)";
       ctx.beginPath();
       ctx.moveTo(handleX + s.resizeHandleSize - 5, handleY + 5);
       ctx.lineTo(handleX + 5, handleY + s.resizeHandleSize - 5);
       ctx.moveTo(handleX + s.resizeHandleSize - 9, handleY + 5);
       ctx.lineTo(handleX + 5, handleY + s.resizeHandleSize - 9);
       ctx.stroke();
     }

       function getCanvasPos(evt) {
    const rect = state.cropCanvas.getBoundingClientRect();
    return {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top
    };
  }

     // Bounded on both axes. With only the lower bound this matched the whole quadrant below and right of the
     // handle's top-left corner, so a press in the letterbox band outside the crop box started a resize and
     // dragged the box over padding the output must never include. The slop is what the hit size already adds
     // inward, mirrored outward, so the handle stays as easy to grab on touch as it was.
     function isInResizeHandle(x, y) {
       const s = state;
       const handleRight = s.cropX + s.cropWidth;
       const handleBottom = s.cropY + s.cropHeight;
       const outwardSlop = s.resizeHandleHitSize - s.resizeHandleSize;
       return (
         x >= handleRight - s.resizeHandleHitSize &&
         x <= handleRight + outwardSlop &&
         y >= handleBottom - s.resizeHandleHitSize &&
         y <= handleBottom + outwardSlop
       );
     }

     function isInsideCropBox(x, y) {
       const s = state;
       return (
         x >= s.cropX &&
         x <= s.cropX + s.cropWidth &&
         y >= s.cropY &&
         y <= s.cropY + s.cropHeight
       );
     }

     function updateCursor(x, y) {
       if (isInResizeHandle(x, y)) {
         cropCanvas.style.cursor = "pointer";
         return;
       }
       if (isInsideCropBox(x, y)) {
         cropCanvas.style.cursor = state.isMoving ? "grabbing" : "grab";
         return;
       }
       cropCanvas.style.cursor = "default";
     }

     function onFileChange() {
       const file = state.fileInput.files?.[0];
       if (!file) return;

       const reader = new FileReader();
       reader.onload = (e) => {
         state.image = new Image();
         state.image.onload = () => {
           const imgRatio = state.image.width / state.image.height;
           const canvasSize = state.cropCssSize;
           // fit INSIDE canvas, keep proportions
             if (imgRatio > 1) {
    state.imageDrawWidth = canvasSize;
    state.imageDrawHeight = canvasSize / imgRatio;
    state.imageDrawX = 0;
    state.imageDrawY = (canvasSize - state.imageDrawHeight) / 2;
  } else {
    state.imageDrawHeight = canvasSize;
    state.imageDrawWidth = canvasSize * imgRatio;
    state.imageDrawX = (canvasSize - state.imageDrawWidth) / 2;
    state.imageDrawY = 0;
  }

           // Default crop = fit to the full image, not a smaller centered chip. Users no
           // longer need to click "Fit" after uploading; if they want to tighten the crop
           // they can drag-resize from the default. When an aspectRatio is set (e.g. 1.0
           // for profile photos) the crop snaps to the largest box of that ratio that
           // still fits inside the image so the output never picks up letterbox padding.
           if (state.aspectRatio) {
             let fitW = state.imageDrawWidth;
             let fitH = fitW / state.aspectRatio;
             if (fitH > state.imageDrawHeight) {
               fitH = state.imageDrawHeight;
               fitW = fitH * state.aspectRatio;
             }
             state.cropWidth = fitW;
             state.cropHeight = fitH;
             state.cropX = state.imageDrawX + (state.imageDrawWidth - fitW) / 2;
             state.cropY = state.imageDrawY + (state.imageDrawHeight - fitH) / 2;
           } else {
             state.cropWidth = state.imageDrawWidth;
             state.cropHeight = state.imageDrawHeight;
             state.cropX = state.imageDrawX;
             state.cropY = state.imageDrawY;
           }

           draw();
           state.cropCanvas.classList.remove("d-none");

           if (state.dotNetRef) {
             state.dotNetRef.invokeMethodAsync("OnCropImageLoaded")
               .catch((err) => console.error("OnCropImageLoaded failed", err));
           }
         };
         state.image.src = e.target.result;
       };
       reader.readAsDataURL(file);
     }

     function onPointerDown(e) {
       if (e.button !== undefined && e.button !== 0) return;

       const { x, y } = getCanvasPos(e);

       if (isInResizeHandle(x, y)) {
         state.isResizing = true;
         cropCanvas.style.cursor = "pointer";
       } else if (isInsideCropBox(x, y)) {
         state.isMoving = true;
         state.dragOffsetX = x - state.cropX;
         state.dragOffsetY = y - state.cropY;
         cropCanvas.style.cursor = "grabbing";
       } else {
         updateCursor(x, y);
         return;
       }

       e.preventDefault();
       state.activePointerId = e.pointerId;
       try { cropCanvas.setPointerCapture(e.pointerId); } catch { }
     }

       function onPointerMove(e) {
    const { x, y } = getCanvasPos(e);
    const size = state.cropCssSize; // ✅ CSS pixels only

    if (state.activePointerId === null) {
      updateCursor(x, y);
      return;
    }
    if (state.activePointerId !== e.pointerId) return;

    // --------------------
    // RESIZE
    // --------------------
    if (state.isResizing) {
      let w = Math.max(60, x - state.cropX);
      let h = state.aspectRatio ? w / state.aspectRatio : Math.max(60, y - state.cropY);;

      // clamp to canvas bounds
      if (state.cropX + w > size) {
        w = size - state.cropX;
        if(state.aspectRatio)
        {
          h = w / state.aspectRatio;
        }
      }
      if (state.cropY + h > size) {
        h = size - state.cropY;
        if(state.aspectRatio)
        {
          w = h * state.aspectRatio;
        }
      }

      state.cropWidth = w;
      state.cropHeight = h;

      draw();
    }

    // --------------------
    // MOVE
    // --------------------
    if (state.isMoving) {
      state.cropX = Math.max(
        0,
        Math.min(x - state.dragOffsetX, size - state.cropWidth)
      );

      state.cropY = Math.max(
        0,
        Math.min(y - state.dragOffsetY, size - state.cropHeight)
      );

      draw();
    }

    updateCursor(x, y);
  }


     function endPointer(e) {
       if (state.activePointerId === e.pointerId) {
         state.isResizing = false;
         state.isMoving = false;
         state.activePointerId = null;
         updateCursor(state.cropX + state.cropWidth, state.cropY + state.cropHeight);
         try { cropCanvas.releasePointerCapture(e.pointerId); } catch { }
       }
     }

     function onPointerLeave() {
       cropCanvas.style.cursor = "default";
     }

     fileInput.addEventListener("change", onFileChange);
     cropCanvas.addEventListener("pointerdown", onPointerDown);
     cropCanvas.addEventListener("pointermove", onPointerMove, { passive: false });
     cropCanvas.addEventListener("pointerup", endPointer);
     cropCanvas.addEventListener("pointercancel", endPointer);
     cropCanvas.addEventListener("pointerleave", onPointerLeave);

     window._croppers[canvasId] = {
       state,
       handlers: { onFileChange, onPointerDown, onPointerMove, endPointer, onPointerLeave },
       draw
     };
   };

    window.loadImageIntoCropper = function (canvasId, dataUrl) {
      const cropper = window._croppers?.[canvasId];
      if (!cropper) return;

      const s = cropper.state;

      s.image = new Image();
      s.image.onload = () => {
        const canvasSize = s.cropCssSize;

        const imgRatio = s.image.width / s.image.height;

        if (imgRatio > 1) {
          s.imageDrawWidth = canvasSize;
          s.imageDrawHeight = canvasSize / imgRatio;
          s.imageDrawX = 0;
          s.imageDrawY = (canvasSize - s.imageDrawHeight) / 2;
        } else {
          s.imageDrawHeight = canvasSize;
          s.imageDrawWidth = canvasSize * imgRatio;
          s.imageDrawX = (canvasSize - s.imageDrawWidth) / 2;
          s.imageDrawY = 0;
        }

        // Default crop = fit the full image (no letterbox), matching the same fit-on-load
        // semantics applied in the internal file-pick branch above. Users no longer need
        // to click "Fit" after uploading; the aspect-ratio constraint (when set) snaps the
        // crop to the largest in-image box of that ratio so the saved output never picks
        // up white padding around the picture.
        if (s.aspectRatio) {
          let fitW = s.imageDrawWidth;
          let fitH = fitW / s.aspectRatio;
          if (fitH > s.imageDrawHeight) {
            fitH = s.imageDrawHeight;
            fitW = fitH * s.aspectRatio;
          }
          s.cropWidth = fitW;
          s.cropHeight = fitH;
          s.cropX = s.imageDrawX + (s.imageDrawWidth - fitW) / 2;
          s.cropY = s.imageDrawY + (s.imageDrawHeight - fitH) / 2;
        } else {
          s.cropWidth = s.imageDrawWidth;
          s.cropHeight = s.imageDrawHeight;
          s.cropX = s.imageDrawX;
          s.cropY = s.imageDrawY;
        }

        cropper.draw();

        if (s.dotNetRef) {
          s.dotNetRef.invokeMethodAsync("OnCropImageLoaded")
            .catch((err) => console.error("OnCropImageLoaded failed", err));
        }
      };

      s.image.src = dataUrl;
    };

        window.getCroppedImageData = function (canvasId) {
    const cropper = window._croppers?.[canvasId];
    if (!cropper) return null;

    const s = cropper.state;
    if (!s.image || !s.image.complete || !s.image.naturalWidth) return null;

    const cssScaleX = s.image.naturalWidth / s.imageDrawWidth;
    const cssScaleY = s.image.naturalHeight / s.imageDrawHeight;

    const sx = (s.cropX - s.imageDrawX) * cssScaleX;
    const sy = (s.cropY - s.imageDrawY) * cssScaleY;
    const sw = s.cropWidth * cssScaleX;
    const sh = s.cropHeight * cssScaleY;

    const MAX_SIZE = 1600;
    let outW = Math.round(sw);
    let outH = Math.round(sh);

    if (outW > MAX_SIZE || outH > MAX_SIZE) {
      const ratio = outW / outH;
      if (outW > outH) {
        outW = MAX_SIZE;
        outH = Math.round(MAX_SIZE / ratio);
      } else {
        outH = MAX_SIZE;
        outW = Math.round(MAX_SIZE * ratio);
      }
    }

    const out = document.createElement("canvas");
    out.width = outW;
    out.height = outH;

    const ctx = out.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outW, outH);

    ctx.drawImage(
      s.image,
      sx, sy, sw, sh,
      0, 0, outW, outH
    );

    return out.toDataURL("image/jpeg", 0.92).split(",")[1];
  };


  // Builds the blurred companion of a post image. Takes bare base64 JPEG (no data: prefix) and returns the same,
  // matching getCroppedImageData above so the caller can hand either straight to the uploader.
  //
  // DESTRUCTIVE ON PURPOSE — this is not a CSS blur. The blurred file is uploaded to the CDN under a public URL, so
  // anyone can fetch it: it has to be a picture from which the original cannot be recovered, not the original with a
  // filter draped over it. A canvas blur alone would not be that. Applying a small-radius blur is a convolution and
  // is partially invertible (deconvolution / sharpening recovers a lot of it), so the pixels themselves must go.
  //
  // Hence the downscale first: the image is drawn into a canvas a few pixels wide, which throws away almost every
  // high-frequency detail for good, and only then scaled back up with a blur to smooth the resulting blocks into
  // something that reads as a frosted preview rather than a broken thumbnail. Information that is not in the small
  // canvas cannot come back out of the large one.
  //
  // Returns null on any failure — a decode error, a tainted canvas, an empty input. The caller treats null as "no
  // blurred companion" and carries on with the normal upload rather than losing the post.
  window.blurImageBase64 = function (base64Jpeg, downscaleWidth, blurPixels) {
    return new Promise((resolve) => {
      if (!base64Jpeg) {
        resolve(null);
        return;
      }

      // Defaults live here rather than at the call site so every caller gets the same strength. 16px wide leaves
      // colour and rough composition and nothing else — no faces, no text, no logos — which is the point of a
      // teaser. The blur radius default is not a constant: see below.
      let targetSmallWidth = 16;
      if (downscaleWidth > 0) {
        targetSmallWidth = downscaleWidth;
      }

      const image = new Image();

      image.onload = function () {
        try {
          const fullWidth = image.naturalWidth;
          const fullHeight = image.naturalHeight;
          if (!fullWidth || !fullHeight) {
            resolve(null);
            return;
          }

          // Blur radius is PROPORTIONAL to the output, not a fixed pixel count. The filter works in the destination
          // canvas's coordinate space, so one constant cannot serve both a 400px phone upload and a 4000px camera
          // one: the same 8px reads as heavy on the first and as a faint haze on the second, which is how a picture
          // could come out looking barely touched. 3.5% of the width holds the appearance steady at any size, with a
          // floor so tiny images still get a real blur. An explicit blurPixels argument still overrides it.
          const proportionalBlurRadius = Math.round(fullWidth * 0.035);
          let blurRadius = Math.max(12, proportionalBlurRadius);
          if (blurPixels > 0) {
            blurRadius = blurPixels;
          }

          // Step 1 — throw the detail away. Aspect ratio is kept so the upscale does not distort, and the height is
          // floored at 1px for very wide banners.
          const smallWidth = Math.max(1, Math.min(targetSmallWidth, fullWidth));
          const smallHeight = Math.max(1, Math.round(smallWidth * (fullHeight / fullWidth)));

          const smallCanvas = document.createElement("canvas");
          smallCanvas.width = smallWidth;
          smallCanvas.height = smallHeight;

          const smallCtx = smallCanvas.getContext("2d");
          smallCtx.imageSmoothingEnabled = true;
          smallCtx.imageSmoothingQuality = "high";
          smallCtx.drawImage(image, 0, 0, smallWidth, smallHeight);

          // Step 2 — back up to the original size. Same dimensions as the real picture so the two files are
          // interchangeable in any layout that already reserves space for one of them.
          const out = document.createElement("canvas");
          out.width = fullWidth;
          out.height = fullHeight;

          const ctx = out.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";

          // Opaque white underneath, like getCroppedImageData: JPEG has no alpha, and without this a transparent
          // source would flatten to black.
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, fullWidth, fullHeight);

          // The blur is applied while drawing the SMALL canvas up, not to the full-size image. Blurring at the edges
          // of a canvas samples outside it and leaves a pale border; scaling up first means the blur only has to
          // soften block boundaries, so a modest radius does the job and the frame stays clean.
          ctx.filter = "blur(" + blurRadius + "px)";
          ctx.drawImage(smallCanvas, 0, 0, smallWidth, smallHeight, 0, 0, fullWidth, fullHeight);
          ctx.filter = "none";

          resolve(out.toDataURL("image/jpeg", 0.8).split(",")[1]);
        } catch (e) {
          console.error("blurImageBase64 failed", e);
          resolve(null);
        }
      };

      image.onerror = function () {
        console.error("blurImageBase64 could not decode the source image");
        resolve(null);
      };

      image.src = "data:image/jpeg;base64," + base64Jpeg;
    });
  };


   window.disposeCropper = function (canvasId, inputId) {
     const cropper = window._croppers?.[canvasId];
     if (!cropper) return;

     const { state, handlers } = cropper;
     const canvas = state?.cropCanvas || state?.canvas;
     const input = state?.fileInput || state?.input || document.getElementById(inputId);

     try { input && (input.value = ""); } catch { }

     if (input && handlers?.onFileChange) input.removeEventListener("change", handlers.onFileChange);

     if (canvas && handlers?.onPointerDown) canvas.removeEventListener("pointerdown", handlers.onPointerDown);
     if (canvas && handlers?.onPointerMove) canvas.removeEventListener("pointermove", handlers.onPointerMove);
     if (canvas && handlers?.endPointer) {
       canvas.removeEventListener("pointerup", handlers.endPointer);
       canvas.removeEventListener("pointercancel", handlers.endPointer);
     }
     if (canvas && handlers?.onPointerLeave) canvas.removeEventListener("pointerleave", handlers.onPointerLeave);

     try {
       const ctx = canvas?.getContext?.("2d");
       if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
     } catch { }

     delete window._croppers[canvasId];
   };
   // crop script end


   window.infiniteScrollIsElementValid = function (element) {
       return element instanceof Element;
   };

   window.infiniteScrollInitialize = function (lastItemIndicator, componentInstance) {
       if (!(lastItemIndicator instanceof Element)) {
           return null;
       }
       const options = {
           root: window.infiniteScrollFindClosestScrollContainer(lastItemIndicator),
           rootMargin: '0px 0px 2400px 0px',
           threshold: 0,
       };

       // Once the controller is disposed (tab switch / navigation) the observer must never invoke
       // back into .NET: a JS->.NET call that lands while the circuit is tearing down throws a
       // NullReferenceException server-side in RemoteJSRuntime.EndInvokeDotNet. Track a disposed flag,
       // bail early when set, and swallow the rejection the same way the YouTube/timer callbacks do.
       let disposed = false;

       const observer = new IntersectionObserver((entries) => {
           if (disposed) {
               return;
           }
           for (const entry of entries) {
               if (entry.isIntersecting) {
                   observer.unobserve(lastItemIndicator);
                   componentInstance.invokeMethodAsync("LoadMoreItems").catch(() => {});
               }
           }
       }, options);

       observer.observe(lastItemIndicator);

       return {
           dispose: () => {
               disposed = true;
               window.infiniteScrollDispose(observer);
           },
           onNewItems: () => {
               if (disposed) {
                   return;
               }
               observer.unobserve(lastItemIndicator);
               observer.observe(lastItemIndicator);
           }
       };
   };

   window.socialfiSeenPostsInitialize = function (componentInstance) {
       const observedPostIds = new Set();
       // See infiniteScrollInitialize: never invoke .NET after dispose, and swallow the rejection so a
       // mark-as-seen landing during teardown can't NRE in RemoteJSRuntime.EndInvokeDotNet.
       let disposed = false;
       const observer = new IntersectionObserver((entries) => {
           if (disposed) {
               return;
           }
           for (const entry of entries) {
               if (!entry.isIntersecting) {
                   continue;
               }

               const seenPostId = entry.target.dataset.seenPostId;
               observer.unobserve(entry.target);

               if (!seenPostId) {
                   continue;
               }

               componentInstance.invokeMethodAsync("MarkPostAsSeen", Number(seenPostId)).catch(() => {});
           }
       }, {
           threshold: 0.5
       });

       return {
           dispose: () => {
               disposed = true;
               window.infiniteScrollDispose(observer);
           },
           observePostById: (elementId, postId) => {
               if (disposed || observedPostIds.has(postId)) {
                   return;
               }

               const postElement = document.getElementById(elementId);
               if (!(postElement instanceof Element)) {
                   return;
               }

               postElement.dataset.seenPostId = postId;
               observedPostIds.add(postId);
               observer.observe(postElement);
           }
       };
   };

  // Fit-to-image (not fit-to-canvas). The canvas is larger than the drawn image whenever
  // the picture is not square — the rest is empty letterbox. Snapping the crop to the
  // canvas would put those empty bars into the saved output. Snap to
  // imageDrawX/Y/Width/Height instead so the output is always pixels-only, respecting
  // aspectRatio when set.
  window.fitCropToCanvas = function (canvasId) {
    const cropper = window._croppers?.[canvasId];
    if (!cropper) return;

    const s = cropper.state;

    if (s.aspectRatio) {
      let cropW = s.imageDrawWidth;
      let cropH = cropW / s.aspectRatio;
      if (cropH > s.imageDrawHeight) {
        cropH = s.imageDrawHeight;
        cropW = cropH * s.aspectRatio;
      }
      s.cropWidth = cropW;
      s.cropHeight = cropH;
      s.cropX = s.imageDrawX + (s.imageDrawWidth - cropW) / 2;
      s.cropY = s.imageDrawY + (s.imageDrawHeight - cropH) / 2;
    } else {
      s.cropWidth = s.imageDrawWidth;
      s.cropHeight = s.imageDrawHeight;
      s.cropX = s.imageDrawX;
      s.cropY = s.imageDrawY;
    }

    cropper.draw();
  };

   window.infiniteScrollDispose = function (observer) {
       observer.disconnect();
   };

   window.infiniteScrollFindClosestScrollContainer = function (element) {
       while (element) {
          if (!(element instanceof Element)) {
             return null; // defensive against generic 500 shown sometimes
         }
           const style = getComputedStyle(element);
           if (style.overflowY !== 'visible') {
               return element;
           }
           element = element.parentElement;
       }
       return null;
   };

         (function () {

     // Update UTC timestamps rendered by Blazor to local display text.
     function pad2(v) {
       var value = String(v);
       if (value.length < 2)
       {
         value = "0" + value;
       }
       return value;
     }

     function normalizeFormat(format) {
       var trimmed = "";
       if (format) {
         var candidate = format.trim();
         if (candidate) {
           trimmed = candidate;
         }
       }

       if (!trimmed) {
         return "dd/MM/yyyy HH:mm";
       }

       return trimmed;
     }

     function normalizeLocale(locale) {
       if (locale && locale.trim()) {
         return locale.trim();
       }

       // No data-locale on the element — let the browser pick, which is still closer to the reader than
       // hardcoding English would be.
       return undefined;
     }

     // Intl.DateTimeFormat construction is expensive and updateAllTimes re-runs on every Blazor mutation, so
     // formatters are built once per (locale, kind) pair. Values may be null when Intl rejects the locale;
     // hasOwnProperty is what distinguishes "cached a null" from "not cached yet".
     var intlFormatterCache = {};

     function resolveIntlFormatter(locale, cacheKey, options) {
       var fullCacheKey = String(locale) + "|" + cacheKey;
       if (Object.prototype.hasOwnProperty.call(intlFormatterCache, fullCacheKey)) {
         return intlFormatterCache[fullCacheKey];
       }

       var formatter = null;
       try {
         formatter = new Intl.DateTimeFormat(locale, options);
       } catch (error) {
         formatter = null;
       }

       intlFormatterCache[fullCacheKey] = formatter;
       return formatter;
     }

     // Fallbacks for when Intl is unavailable or rejects the locale. English only, deliberately — they are the
     // last resort, not the normal path.
     var fallbackMonthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
     var fallbackMonthNamesLong = ["January", "February", "March", "April", "May", "June",
                                   "July", "August", "September", "October", "November", "December"];
     // 0 = Sunday, 1 = Monday, ... 6 = Saturday — matches JS Date.getDay()
     var fallbackWeekdayNamesShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
     var fallbackWeekdayNamesLong = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

     function resolveMonthName(date, locale, width) {
       var formatter = resolveIntlFormatter(locale, "month-" + width, { month: width });
       if (formatter) {
         return formatter.format(date);
       }

       if (width === "long") {
         return fallbackMonthNamesLong[date.getMonth()];
       }

       return fallbackMonthNamesShort[date.getMonth()];
     }

     function resolveWeekdayName(date, locale, width) {
       var formatter = resolveIntlFormatter(locale, "weekday-" + width, { weekday: width });
       if (formatter) {
         return formatter.format(date);
       }

       if (width === "long") {
         return fallbackWeekdayNamesLong[date.getDay()];
       }

       return fallbackWeekdayNamesShort[date.getDay()];
     }

     function resolveHours12(date) {
       var hours = date.getHours() % 12;
       if (hours === 0) {
         return 12;
       }

       return hours;
     }

     function resolveDayPeriod(date, locale) {
       var formatter = resolveIntlFormatter(locale, "dayperiod", { hour: "numeric", hour12: true });
       if (formatter && typeof formatter.formatToParts === "function") {
         var parts = formatter.formatToParts(date);
         for (var i = 0; i < parts.length; i++) {
           if (parts[i].type === "dayPeriod") {
             return parts[i].value;
           }
         }
       }

       if (date.getHours() < 12) {
         return "AM";
       }

       return "PM";
     }

     function formatToken(date, token, locale) {
       switch (token) {
         case "yyyy": return String(date.getFullYear());
         case "yy": return pad2(date.getFullYear() % 100);
         case "MMMM": return resolveMonthName(date, locale, "long");
         case "MMM": return resolveMonthName(date, locale, "short");
         case "MM": return pad2(date.getMonth() + 1);
         case "M": return String(date.getMonth() + 1);
         case "dddd": return resolveWeekdayName(date, locale, "long");
         case "ddd": return resolveWeekdayName(date, locale, "short");
         case "dd": return pad2(date.getDate());
         case "d": return String(date.getDate());
         case "HH": return pad2(date.getHours());
         case "H": return String(date.getHours());
         case "hh": return pad2(resolveHours12(date));
         case "h": return String(resolveHours12(date));
         case "mm": return pad2(date.getMinutes());
         case "m": return String(date.getMinutes());
         case "ss": return pad2(date.getSeconds());
         case "s": return String(date.getSeconds());
         case "tt": return resolveDayPeriod(date, locale);
         case "t": return resolveDayPeriod(date, locale).charAt(0);
       }

       // A .NET-style quoted literal ('at'), emitted without the quotes.
       return token.replace(/'/g, "");
     }

     // Longest token first, matched in ONE sweep. The previous implementation ran a chain of .replace() passes,
     // which has two failure modes this avoids: a token with no pass of its own survived into the output as a
     // literal (Format="MMM d, yyyy" rendered "Aug d, 2026"), and a substituted value containing token letters
     // could be re-substituted by a later pass — a hazard that gets much more likely now that month and weekday
     // names are localized and no longer a known-safe English set.
     var formatTokenPattern = /'[^']*'|yyyy|yy|MMMM|MMM|MM|M|dddd|ddd|dd|d|HH|H|hh|h|mm|m|ss|s|tt|t/g;

     function formatLocal(date, format, locale) {
       var normalizedFormat = normalizeFormat(format);
       var normalizedLocale = normalizeLocale(locale);

       var formattedValue = normalizedFormat.replace(formatTokenPattern, function (token) {
         var tokenValue = formatToken(date, token, normalizedLocale);
         return tokenValue;
       });

       return formattedValue;
     }

     function getUtcValue(el) {
       var utcValue = el.getAttribute("data-utc");
       if (utcValue && utcValue.trim()) {
         return utcValue;
       }

       return null;
     }

     function parseUtc(utcValue) {
       var parsedDate = new Date(utcValue);
       if (isNaN(parsedDate.getTime())) {
         return null;
       }

       return parsedDate;
     }

     function markReady(el) {
       el.setAttribute("data-utc-ready", "true");
       el.removeAttribute("hidden");
       var container = null;
       if (el && typeof el.closest === "function") {
         container = el.closest("[data-utc-container]");
       }
       if (container) {
         container.setAttribute("data-utc-ready", "true");
         container.setAttribute("aria-busy", "false");
       }
     }

     function updateAllTimes() {
       if (window._utcTimeUpdating === true) {
         return;
       }

       window._utcTimeUpdating = true;
       var nodes = document.querySelectorAll("time[data-utc]");
       try {
         for (var i = 0; i < nodes.length; i++) {
           var el = nodes[i];
           try {
             var utcValue = getUtcValue(el);
             if (!utcValue) {
               continue;
             }

             var parsedDate = parseUtc(utcValue);
             if (!parsedDate) {
               continue;
             }

             var fmt = el.getAttribute("data-format");
             var elementLocale = el.getAttribute("data-locale");
             var displayValue = formatLocal(parsedDate, fmt, elementLocale);
             el.textContent = displayValue;
             markReady(el);
           } catch (error) {
             console.error("Failed to format utc time element", error);
           }
         }
       } finally {
         window._utcTimeUpdating = false;
       }
     }

     function handleMutations(mutations) {
       if (window._utcTimeUpdating === true) {
         return;
       }

       var shouldUpdate = false;
       for (var i = 0; i < mutations.length; i++) {
         var mutation = mutations[i];
         if (mutation.addedNodes && mutation.addedNodes.length > 0) {
           for (var nodeIndex = 0; nodeIndex < mutation.addedNodes.length; nodeIndex++) {
             var node = mutation.addedNodes[nodeIndex];
             if (node && node.nodeType === 1) {
               if (node.matches && node.matches("time[data-utc]")) {
                 shouldUpdate = true;
                 break;
               }
               if (node.querySelector && node.querySelector("time[data-utc]")) {
                 shouldUpdate = true;
                 break;
               }
             }
           }
         }
         if (shouldUpdate) {
           break;
         }
       }

       if (shouldUpdate) {
         updateAllTimes();
       }
     }

     function initializeUtcObserver() {
       if (window._utcTimeObserver) {
         return;
       }

       if (!document.body) {
         return;
       }

       // Watch for Blazor re-renders or late-added time elements.
       var observer = new MutationObserver(handleMutations);
       observer.observe(document.body, { childList: true, subtree: true });
       window._utcTimeObserver = observer;
     }

     function onBlazorLoaded() {
       updateAllTimes();
     }

     window.updateUtcTimeElements = updateAllTimes;
     window.addEventListener("blazorLoaded", onBlazorLoaded);

     if (document.readyState === "loading") {
       document.addEventListener("DOMContentLoaded", function () {
         updateAllTimes();
         initializeUtcObserver();
       });
     } else {
       updateAllTimes();
       initializeUtcObserver();
     }

   })();

  // Scroll-reveal for the marketing pages. The `tkca-reveal-ready` flag below is what ARMS the pages' own
  // `opacity: 0` rules — without it their [data-reveal] blocks are simply visible — so this function and
  // those rules are two halves of one mechanism and must cover exactly the same elements.
  //
  // The selector is therefore deliberately NOT a per-page allow-list. It used to be
  // '.tkca [data-reveal], .tkic [data-reveal], .tkgc [data-reveal]', and .tkms (the membership page) was
  // added to the CSS side without being added here. The failure that produced is worth spelling out, because
  // an allow-list will produce it again for the next page: with no match this returned early, so nothing was
  // ever observed and nothing ever got `is-revealed`. On a cold load of /membership/ that was invisible —
  // the flag was absent too, so the hiding rule never applied and the page looked correct by accident. But
  // the flag goes on <html>, is never removed, and SURVIVES enhanced navigation: arriving at /membership/
  // from any .tkca/.tkic/.tkgc page left it armed with no revealer behind it, and the hero, the tier grid
  // and six other blocks rendered at opacity 0 — present in the DOM, permanently invisible.
  //
  // Matching every [data-reveal] costs nothing on pages that have none, and a page carrying the markup
  // without the CSS just gains a class no rule reads.
  window.initTkcaScrollReveal = function () {
    try {
      var items = document.querySelectorAll('[data-reveal]');
      if (!items.length) {
        return;
      }

      document.documentElement.classList.add('tkca-reveal-ready');

      var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced || !('IntersectionObserver' in window)) {
        for (var k = 0; k < items.length; k++) {
          items[k].classList.add('is-revealed');
        }
        return;
      }

      var observer = new IntersectionObserver(function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            entries[i].target.classList.add('is-revealed');
            obs.unobserve(entries[i].target);
          }
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

      for (var j = 0; j < items.length; j++) {
        if (items[j].dataset.revealObserved === 'true') {
          continue;
        }
        items[j].dataset.revealObserved = 'true';
        observer.observe(items[j]);
      }
    } catch (err) {
      console.error('initTkcaScrollReveal failed', err);
    }
  };

  window.hasOverflow = el =>
     el instanceof Element && el.scrollHeight > el.clientHeight;

  // Tap-to-expand for ExpandableText: the clamped body becomes a second target for "See More". Deliberately
  // insensitive - a scroll starting on a post must never expand it - so a pointer counts as a tap only when it
  // barely moved, was released quickly, selected nothing and missed every link. Expands only.
  window.initExpandableTextTap = function (textElement, toggleId) {
    try {
      if (!(textElement instanceof Element)) {
        return;
      }

      if (textElement.dataset.tapToExpandWired === 'true') {
        return;
      }
      textElement.dataset.tapToExpandWired = 'true';

      // Movement past this many CSS pixels means the finger was dragging the page, not tapping the text.
      var maxTapMovementPx = 8;
      // A press held longer than this is a long-press - text selection or a context menu - and not a tap.
      var maxTapDurationMs = 500;
      // Everything here handles its own click: links, mentions, hashtags, buttons, the See More label.
      var interactiveSelector = 'a, button, label, input, select, textarea, ' +
        '[role="button"], [role="link"], .mud-button-root';

      var pendingTap = null;

      textElement.addEventListener('pointerdown', function (event) {
        pendingTap = null;
        if (event.button !== 0) {
          return;
        }
        pendingTap = { x: event.clientX, y: event.clientY, startedAt: Date.now() };
      });

      textElement.addEventListener('pointermove', function (event) {
        if (pendingTap === null) {
          return;
        }
        var movedX = Math.abs(event.clientX - pendingTap.x);
        var movedY = Math.abs(event.clientY - pendingTap.y);
        if (movedX > maxTapMovementPx || movedY > maxTapMovementPx) {
          pendingTap = null;
        }
      });

      textElement.addEventListener('pointercancel', function () {
          pendingTap = null;
      });

      textElement.addEventListener('pointerup', function (event) {
        var tapStart = pendingTap;
        pendingTap = null;
        if (tapStart === null) {
          return;
        }

        var movedX = Math.abs(event.clientX - tapStart.x);
        var movedY = Math.abs(event.clientY - tapStart.y);
        if (movedX > maxTapMovementPx || movedY > maxTapMovementPx) {
          return;
        }

        var heldForMs = Date.now() - tapStart.startedAt;
        if (heldForMs > maxTapDurationMs) {
          return;
        }

        var interactiveTarget = null;
        if (event.target && event.target.closest) {
          interactiveTarget = event.target.closest(interactiveSelector);
        }
        if (interactiveTarget !== null) {
          return;
        }

        // A drag that selected words inside this text is a selection gesture, not a tap.
        var selection = window.getSelection();
        var selectedInsideText = false;
        if (selection !== null && selection.isCollapsed === false && selection.anchorNode !== null) {
          selectedInsideText = textElement.contains(selection.anchorNode);
        }
        if (selectedInsideText) {
          return;
        }

        var toggle = document.getElementById(toggleId);
        if (toggle === null || toggle.checked === true) {
          return;
        }
        toggle.checked = true;
      });
    } catch (err) {
      console.error('initExpandableTextTap failed', err);
    }
  };

  window.consoleMessage = function (message) {
    console.log(message);
  };

  window.loadExternalScript = function (src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  window.loadTradingViewScriptIfNeeded = function () {
    return new Promise(function (resolve, reject) {
      if (window.TradingView) {
        resolve();
        return;
      }

      var existingScriptElement = document.querySelector('script[data-tokero-tradingview-script="true"]');
      if (!existingScriptElement) {
        existingScriptElement = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      }
      if (existingScriptElement) {
        existingScriptElement.setAttribute("data-tokero-tradingview-script", "true");

        if (existingScriptElement.dataset.loaded === "true" || window.TradingView) {
          resolve();
          return;
        }

        existingScriptElement.addEventListener("load", function () {
          if (typeof window.onTradingViewLoad === "function") {
            window.onTradingViewLoad();
          }
          resolve();
        }, { once: true });

        existingScriptElement.addEventListener("error", function () {
          reject(new Error("Failed to load TradingView script."));
        }, { once: true });

        return;
      }

      var existingPreloadElement = document.querySelector('link[data-tokero-tradingview-preload="true"]');
      if (!existingPreloadElement) {
        var preloadElement = document.createElement("link");
        preloadElement.rel = "preload";
        preloadElement.href = "https://s3.tradingview.com/tv.js";
        preloadElement.as = "script";
        preloadElement.setAttribute("data-tokero-tradingview-preload", "true");
        document.head.appendChild(preloadElement);
      }

      var scriptElement = document.createElement("script");
      scriptElement.src = "https://s3.tradingview.com/tv.js";
      scriptElement.defer = true;
      scriptElement.setAttribute("data-tokero-tradingview-script", "true");

      scriptElement.addEventListener("load", function () {
        scriptElement.dataset.loaded = "true";
        if (typeof window.onTradingViewLoad === "function") {
          window.onTradingViewLoad();
        }
        resolve();
      }, { once: true });

      scriptElement.addEventListener("error", function () {
        reject(new Error("Failed to load TradingView script."));
      }, { once: true });

      document.body.appendChild(scriptElement);
    });
  };

/* TKR-10855: hands the visitor's cookie decision, and the analytics buffer the page has been holding since it
   rendered, to the server so it can persist the identifiers and send the page view it withheld.

   Runs only on Accept. A visitor who never answers the banner never reaches this, so nothing about them is ever
   stored or transmitted. Failures are swallowed on purpose: analytics must never be able to break the banner or
   block the click that dismisses it. */
window.tokeroSubmitCookiePreferences = async function (acceptedCookieTypes) {
  try {
    var pageBuffer = null;
    var bufferElement = document.getElementById('tk-session-buffer');
    if (bufferElement && bufferElement.textContent) {
      pageBuffer = JSON.parse(bufferElement.textContent);
      /* The server leaves the referrer out of the buffer: the page can be a cached copy served to other visitors. */
      pageBuffer.referrer = document.referrer;
    }

    var payload = {
      acceptedCookieTypes: acceptedCookieTypes || [],
      pageBuffer: pageBuffer
    };

    /* keepalive so the send survives the visitor immediately navigating away from the page they accepted on. */
    await fetch('/api/session/preferences/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      keepalive: true
    });
  } catch (err) {
    /* Intentionally silent - see above. */
  }
};

/* TKR-10910: reports a completed webinar registration as a first-party conversion POST.

   The registration form submits inside the Blazor circuit, which has no HttpContext, so the server-side
   handler cannot dispatch the analytics event itself - the browser posts the fact to our own origin instead,
   where consent, IP and user agent are real. The server re-anchors everything to the registration row it
   already wrote, so this call carries only the two ids and the one-time token the circuit issued for that row,
   which proves the report comes from the browser that registered. Failures are swallowed on purpose: reporting
   must never be able to break the signup. */
window.tokeroReportWebinarRegistration = async function (marketingEventId, registrationId, reportToken) {
  try {
    var pageBuffer = null;
    var bufferElement = document.getElementById('tk-session-buffer');
    if (bufferElement && bufferElement.textContent) {
      pageBuffer = JSON.parse(bufferElement.textContent);
      /* Not in the server's buffer - see tokeroSubmitCookiePreferences. */
      pageBuffer.referrer = document.referrer;
    }

    var payload = {
      marketingEventID: marketingEventId,
      registrationID: registrationId,
      reportToken: reportToken,
      pageBuffer: pageBuffer
    };

    /* keepalive so the send survives the thank-you redirect that follows a successful registration. */
    await fetch('/api/session/webinar-registration/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      keepalive: true
    });
  } catch (err) {
    /* Intentionally silent - see above. */
  }
};

/* TKR-10855: first-party engagement beacon.

   Replicates what gtag.js does for time-on-page, because it is the one signal a server cannot produce: only the
   browser knows whether its tab is visible and focused. A counter accumulates ONLY while the page is both, and
   each report sends the delta since the last one - never a running total - which is the shape GA4 sums into
   "average engagement time".

   It posts to our own origin. Filter lists block vendor hosts, not first-party fetches, so this keeps working in
   exactly the browsers that killed the tag we removed.

   Nothing runs until the page has consent: startEngagementTracking is only called once cookies are accepted. */
window.tokeroEngagementTracker = (function () {
  var accumulatedMs = 0;          /* engaged time not yet reported */
  var lastResumedAt = null;       /* when the current engaged stretch began, null while paused */
  var trackedUrl = null;
  var trackedBufferText = null;   /* the buffer as it was at the last document render - see evaluateNavigation */
  var flushTimerId = null;
  var navigationTimerId = null;
  var historyHooksInstalled = false;
  var started = false;

  var REPORT_INTERVAL_MS = 15000; /* backstop only: navigation is detected from history events, not this tick */

  /* TKR-11105: how long to wait after a navigation signal before judging what kind of navigation it was.

     Routes is rendered InteractiveServer, so the router lives in the circuit and moving between pages performs no
     HTTP request at all - the server never sees it and ServerSideAnalyticsMiddleware never counts it. The browser
     has to say so. But a navigation that DID reach the server must not be reported, or it counts twice, and the
     history event can fire before the new document has swapped in. Settling first is what makes the two
     distinguishable.

     Long enough to outlast a same-origin enhanced-navigation fetch, because on a back/forward gesture the URL
     changes BEFORE the new document is patched in - judging too early there would report a page view the
     middleware also reported. The cost of the wait is that two navigations inside one window collapse to the
     later one; a page passed through in under a second is the right thing to lose if something must be. */
  var NAVIGATION_SETTLE_MS = 800;

  function isEngaged() {
    if (document.visibilityState !== 'visible') {
      return false;
    }
    return document.hasFocus();
  }

  function accumulate() {
    if (lastResumedAt !== null) {
      accumulatedMs += Date.now() - lastResumedAt;
      lastResumedAt = null;
    }
  }

  function resume() {
    if (lastResumedAt === null && isEngaged()) {
      lastResumedAt = Date.now();
    }
  }

  function readPageBuffer() {
    var bufferElement = document.getElementById('tk-session-buffer');
    if (!bufferElement || !bufferElement.textContent) {
      return null;
    }
    try {
      var pageBuffer = JSON.parse(bufferElement.textContent);
      /* Not in the server's buffer - see tokeroSubmitCookiePreferences. */
      pageBuffer.referrer = document.referrer;
      return pageBuffer;
    } catch (err) {
      return null;
    }
  }

  /* useBeacon on the unload path: a normal fetch is cancelled when the page goes away, which is precisely the
     moment the largest and most valuable delta is pending. */
  function flush(useBeacon) {
    accumulate();

    if (accumulatedMs < 1000) {
      return;                     /* sub-second noise is not worth a request */
    }

    var pageBuffer = readPageBuffer();
    if (!pageBuffer) {
      accumulatedMs = 0;
      return;
    }

    var payload = JSON.stringify({
      engagementTimeMilliseconds: Math.round(accumulatedMs),
      pageBuffer: pageBuffer
    });
    accumulatedMs = 0;            /* cleared before the send: a failed report must not double-count on the next one */

    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon('/api/session/activity/', new Blob([payload], { type: 'application/json' }));
        return;
      }

      fetch('/api/session/activity/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        credentials: 'same-origin',
        keepalive: true
      });
    } catch (err) {
      /* Measurement must never surface to the visitor. */
    }
  }

  function onVisibilityChange() {
    if (isEngaged()) {
      resume();
      return;
    }
    flush(true);                  /* hiding the tab is an unload-shaped moment */
  }

  function onIntervalTick() {
    /* A navigation is already waiting to be judged - let it settle rather than forcing an early verdict, which
       is exactly the premature call NAVIGATION_SETTLE_MS exists to avoid. */
    if (navigationTimerId !== null) {
      return;
    }

    /* Backstop for a navigation that produced no history event, or one whose signal was missed. Same decision
       path as the event-driven route, so the two can never disagree about what counts as a navigation. */
    if (window.location.href !== trackedUrl) {
      evaluateNavigation();
      return;
    }

    /* flush() accumulates the stretch itself and leaves the clock stopped, so the restart has to come AFTER it -
       the ordering evaluateNavigation and onEnhancedLoad already use. Resuming first meant the stretch reported
       here was the ~0 ms since that resume, and every window after the first one measured nothing. */
    flush(false);
    resume();
  }

  function readPageBufferText() {
    var bufferElement = document.getElementById('tk-session-buffer');
    if (!bufferElement || !bufferElement.textContent) {
      return null;
    }
    return bufferElement.textContent;
  }

  /* Coalesces bursts: Blazor can emit several history calls for one navigation, and only the settled result
     matters. */
  function onNavigationSignal() {
    if (navigationTimerId !== null) {
      window.clearTimeout(navigationTimerId);
    }
    navigationTimerId = window.setTimeout(evaluateNavigation, NAVIGATION_SETTLE_MS);
  }

  function evaluateNavigation() {
    if (navigationTimerId !== null) {
      window.clearTimeout(navigationTimerId);
      navigationTimerId = null;
    }

    var currentUrl = window.location.href;
    if (currentUrl === trackedUrl) {
      return;
    }

    var previousUrl = trackedUrl;

    /* Engaged time belongs to the page it was spent on, so it is flushed against the outgoing page before the
       new URL is adopted. This is the behaviour the interval tick already had. */
    flush(false);
    trackedUrl = currentUrl;
    resume();                     /* the new page starts accruing now, not at the next tick */

    var bufferText = readPageBufferText();
    if (bufferText === null) {
      return;
    }

    /* Every document render mints a fresh event id, so a buffer that is byte-identical to the one this circuit
       started with proves no document was rendered - the router moved inside the circuit and the middleware
       never saw it. Comparing the whole buffer rather than one field keeps this immune to how the serializer
       happens to case its property names.

       This comparison IS the double-count guard: a navigation the server did handle arrives with a new buffer,
       and we adopt it and stay quiet, because the middleware has already reported that page view itself. */
    if (bufferText !== trackedBufferText) {
      trackedBufferText = bufferText;
      return;
    }

    reportPageView(currentUrl, previousUrl, bufferText);
  }

  function reportPageView(pageUrl, referrer, bufferText) {
    var pageBuffer = null;
    try {
      pageBuffer = JSON.parse(bufferText);
      /* Not in the server's buffer - see tokeroSubmitCookiePreferences. */
      pageBuffer.referrer = document.referrer;
    } catch (err) {
      return;
    }

    var payload = JSON.stringify({
      pageUrl: pageUrl,
      referrer: referrer,
      pageBuffer: pageBuffer
    });

    try {
      /* keepalive so the report survives the visitor immediately navigating onward again. */
      fetch('/api/session/pageview/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        credentials: 'same-origin',
        keepalive: true
      });
    } catch (err) {
      /* Measurement must never surface to the visitor. */
    }
  }

  /* An enhanced navigation IS a document request, so the middleware reported that page view itself and this must
     not report it again. Blazor raises enhancedload once the new document has been patched in, which is the
     definitive "the server handled this one" signal - it cancels any pending judgement and adopts the new state
     silently. Belt and braces next to the buffer comparison, and the thing that closes the back/forward race
     NAVIGATION_SETTLE_MS can only narrow. */
  function onEnhancedLoad() {
    if (navigationTimerId !== null) {
      window.clearTimeout(navigationTimerId);
      navigationTimerId = null;
    }

    if (window.location.href !== trackedUrl) {
      flush(false);
      trackedUrl = window.location.href;
      resume();
    }

    trackedBufferText = readPageBufferText();
  }

  /* Blazor routes in-circuit through history.pushState, and there is no event for that - patching is the only
     way to learn about a navigation at the moment it happens rather than up to REPORT_INTERVAL_MS later. The
     originals are always called first and their return value passed straight through, so this stays invisible to
     anything else using the History API. */
  function installHistoryHooks() {
    if (historyHooksInstalled) {
      return;
    }
    historyHooksInstalled = true;

    var originalPushState = window.history.pushState;
    var originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      var pushResult = originalPushState.apply(this, arguments);
      onNavigationSignal();
      return pushResult;
    };

    window.history.replaceState = function () {
      var replaceResult = originalReplaceState.apply(this, arguments);
      onNavigationSignal();
      return replaceResult;
    };
  }

  return {
    start: function () {
      if (started) {
        return;
      }
      started = true;
      trackedUrl = window.location.href;
      /* The buffer as it stands right now is the one the middleware already counted a page view for. Recording it
         here is what makes the FIRST in-circuit navigation detectable. */
      trackedBufferText = readPageBufferText();
      resume();

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', resume);
      window.addEventListener('blur', function () { flush(false); });
      /* pagehide as well as visibilitychange: iOS Safari is unreliable on the latter for real navigations. */
      window.addEventListener('pagehide', function () { flush(true); });
      window.addEventListener('popstate', onNavigationSignal);
      document.addEventListener('enhancedload', onEnhancedLoad);
      installHistoryHooks();

      flushTimerId = window.setInterval(onIntervalTick, REPORT_INTERVAL_MS);
    }
  };
})();

/* Called from the page when consent is already on record, and from the consent banner the moment it is granted. */
window.tokeroStartEngagementTracking = function () {
  try {
    window.tokeroEngagementTracker.start();
  } catch (err) {
    /* Never break the page for a measurement feature. */
  }
};

/* Called from App.razor on load. Reads the visitor's own cookieControlPrefs the way the banner check in App.razor's
   head does, and CookieConsentReader.HasAnalyticsConsent on the server: a JSON array that names "analytics". The
   document itself can be a cached copy rendered for another visitor, so it cannot carry this decision. */
window.tokeroStartEngagementTrackingWhenConsented = function () {
  try {
    var match = document.cookie.match(/(?:^|;\s*)cookieControlPrefs=([^;]+)/);
    if (!match || !match[1]) {
      return;
    }

    var acceptedCookieTypes = JSON.parse(decodeURIComponent(match[1]));
    if (!Array.isArray(acceptedCookieTypes)) {
      return;
    }

    if (acceptedCookieTypes.indexOf('analytics') === -1) {
      return;
    }

    window.tokeroStartEngagementTracking();
  } catch (err) {
    /* A malformed cookie is no consent, and a measurement feature never breaks the page. */
  }
};

/* Versioned request/reply channel to the MAUI shell. Desktop and old app builds resolve absence as a normal
   result, so a StoreKit-only control never waits forever for a receiver that cannot answer. */
window.TokeroMauiBridgeClient = window.TokeroMauiBridgeClient || (function () {
  var protocolVersion = 1;
  var pending = {};
  var isSubscribed = false;

  function canRequest() {
    var hasSender = window.ReactNativeWebView && window.ReactNativeWebView.postMessage;
    var supportedVersion = (window.__mauiBridgeVersion || 0) >= protocolVersion;
    return !!hasSender && supportedVersion;
  }

  function acknowledge(requestID) {
    if (!canRequest()) {
      return;
    }

    var envelope = {
      v: protocolVersion,
      id: requestID,
      type: 'bridge.ack'
    };
    var envelopeJson = JSON.stringify(envelope);
    window.ReactNativeWebView.postMessage(envelopeJson);
  }

  function settle(envelope) {
    if (!envelope || !envelope.id) {
      return;
    }

    var request = pending[envelope.id];
    if (!request) {
      return;
    }

    delete pending[envelope.id];
    window.clearTimeout(request.timeoutHandle);
    acknowledge(envelope.id);
    request.resolve({
      ok: envelope.ok === true,
      code: envelope.code || 'INTERNAL',
      payload: envelope.payload || {}
    });
  }

  function subscribe() {
    if (isSubscribed) {
      return;
    }

    isSubscribed = true;
    window.addEventListener('maui:message', function (event) {
      settle(event.detail);
    });

    if (window.__mauiSubscribe) {
      window.__mauiSubscribe();
    }
  }

  function ready(timeoutMs) {
    if (canRequest()) {
      subscribe();
      return Promise.resolve(true);
    }

    return new Promise(function (resolve) {
      var wasSettled = false;

      function finish(result) {
        if (wasSettled) {
          return;
        }

        wasSettled = true;
        window.removeEventListener('maui:bridgeready', onReady);
        if (result) {
          subscribe();
        }
        resolve(result);
      }

      function onReady() {
        finish(canRequest());
      }

      window.addEventListener('maui:bridgeready', onReady);
      window.setTimeout(function () {
        finish(canRequest());
      }, timeoutMs || 500);
    });
  }

  function request(type, payload, timeoutMs) {
    if (!canRequest()) {
      return Promise.resolve({ ok: false, code: 'BRIDGE_ABSENT', payload: {} });
    }

    subscribe();
    var requestID = 'r-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    return new Promise(function (resolve) {
      var timeoutHandle = window.setTimeout(function () {
        delete pending[requestID];
        resolve({ ok: false, code: 'TIMEOUT', payload: {} });
      }, timeoutMs || 30000);

      pending[requestID] = {
        resolve: resolve,
        timeoutHandle: timeoutHandle
      };

      var envelope = {
        v: protocolVersion,
        id: requestID,
        type: type,
        payload: payload || {}
      };
      var envelopeJson = JSON.stringify(envelope);
      window.ReactNativeWebView.postMessage(envelopeJson);
    });
  }

  return {
    canRequest: canRequest,
    ready: ready,
    request: request
  };
})();

window.mauiBridgeReady = function () {
  return window.TokeroMauiBridgeClient.ready(500);
};

window.mauiBridgeRequest = function (type, payload, timeoutMs) {
  return window.TokeroMauiBridgeClient.request(type, payload, timeoutMs);
};

/* Content the OS share sheet handed to the MAUI shell, claimed by the page that can compose a post out of it
   (TKR-11477).

   A PULL rather than a push, which is the whole reason this is three lines instead of a delivery mechanism.
   TokeroMauiBridgeClient.settle() drops any envelope that does not correlate to a request the page itself sent,
   so an unsolicited native push would need new delivery semantics on a channel that currently carries purchases.
   Asking for the payload when the composer is ready also removes the race the push would have created: the
   shell can receive a share long before this document exists, and a page that asks cannot ask too early.

   One-shot by contract: the shell clears its inbox as it answers, so a reload does not re-attach the same
   pictures to a post the member has already started editing. Returns null off the app, on a build older than
   the bridge, and when nothing was shared - every caller treats all three the same way, which is to do nothing. */
window.tokeroTakeMauiSharedContent = function () {
  var bridgeClient = window.TokeroMauiBridgeClient;
  if (!bridgeClient || !bridgeClient.canRequest()) {
    return Promise.resolve(null);
  }

  return bridgeClient.request('share.take', {}, 10000).then(function (result) {
    if (!result || result.ok !== true) {
      return null;
    }

    var payload = result.payload || {};
    var sharedText = payload.text || '';
    var sharedUrl = payload.url || '';
    var sharedImages = payload.images || [];
    var hasAnything = sharedText.length > 0 || sharedUrl.length > 0 || sharedImages.length > 0;
    if (!hasAnything) {
      return null;
    }

    return { text: sharedText, url: sharedUrl, images: sharedImages };
  }).catch(function () {
    /* Never let a share hand-off break the page it was supposed to fill. */
    return null;
  });
};

// SsoLoginButton's popup mode (the admin login). Here rather than in the component's own <script> block, which never
// ran when the button first rendered interactively (tokero-style 19.3.8). The URLs and the success message type are
// read off the button's data-* attributes: Razor HTML-encodes an attribute value and getAttribute decodes it, so a URL
// holding an apostrophe or a backslash arrives intact instead of breaking a quoted JS literal in the onclick.
if (!window.tokeroSsoOpenPopup) {
  window.tokeroSsoOpenPopup = function (button) {
    var ssoUrl = button.getAttribute('data-sso-url');
    var destinationUrl = button.getAttribute('data-destination-url');
    var successMessageType = button.getAttribute('data-success-message-type');

    var width = 480;
    var height = 640;
    var left = Math.max(0, Math.floor((window.screen.width - width) / 2));
    var top = Math.max(0, Math.floor((window.screen.height - height) / 2));
    var popup = window.open(ssoUrl, 'tokero-sso-login',
      'width=' + width + ',height=' + height + ',top=' + top + ',left=' + left);

    if (!popup) {
      window.location.href = ssoUrl;
      return;
    }

    var onMessage = function (event) {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data && event.data.type === successMessageType) {
        window.removeEventListener('message', onMessage);
        try { popup.close(); } catch (ignored) { }
        if (destinationUrl) {
          window.location.href = destinationUrl;
        } else {
          window.location.reload();
        }
      }
    };

    window.addEventListener('message', onMessage);
  };
}

// The Statements page's "statement for all coins" link. Here rather than in that page's own <script> block, which never
// ran after an in-app navigation to the page, so the click threw "Could not find 'scrollToElement'" out of the
// handler (tokero-style 19.3.8).
window.scrollToElement = function (element) {
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

// LottieComponent's animation helpers, called through JS interop once the component has rendered. They live here,
// in the bundle every page loads, rather than in the component's own inline <script>: the browser runs an inline
// script only when it arrives in the server-rendered document, so a component that first appeared after a link
// followed inside the circuit found them undefined and drew an empty box.
function initializeLottieAnimation(containerId, animationPath, autoplay = true, autoResize = false) {
  if (!window.lottie || typeof window.lottie.loadAnimation !== "function") {
    // One script tag for the whole page: a feature page starts a dozen animations at once, and each used to add its
    // own copy of the library before the first had arrived. The first call adds it; the rest wait on the same load.
    if (!window.tokeroLottieLoad) {
      window.tokeroLottieLoad = new Promise(function (resolve, reject) {
        const tag = document.createElement('script');
        tag.src = "https://cdn.tokero.com/content/scripts/lottie.min.js";
        tag.onload = resolve;
        tag.onerror = function () {
          // A failed load is retried by the next animation that starts, instead of every later one waiting on it.
          window.tokeroLottieLoad = null;
          reject(new Error('lottie.min.js failed to load'));
        };
        const firstScriptTag = document.getElementsByTagName('script')[0];

        try {
          if (firstScriptTag.parentNode) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
          } else {
            firstScriptTag.insertBefore(tag, firstScriptTag);
          }
        } catch {
          firstScriptTag.insertBefore(tag, firstScriptTag);
        }
      });
    }

    window.tokeroLottieLoad
      .then(function () {
        createLottieAnimation(containerId, animationPath, autoplay);
      })
      .catch(function (loadError) {
        console.error(loadError);
      });
  } else {
    createLottieAnimation(containerId, animationPath, autoplay);
  }

  if (autoResize) {
    // The listener outlives the element it watches. An Academy lesson swap destroys this component and the
    // replacement registers under a brand-new GUID container id, so an anonymous listener piles up one dead
    // closure per lesson and logs a "not found" error for each of them on every resize or orientation
    // change — the page is never reloaded between lessons, so they accumulate for the whole session. Drop
    // the listener the first time it finds its own container gone, the way SharedScripts keys its YouTube
    // player resize handlers so destroyPlayer can unregister them.
    const resizeHandler = function () {
      if (!document.getElementById(containerId)) {
        window.removeEventListener('resize', resizeHandler);
        return;
      }

      updateIframeWidth(containerId);
    };

    window.addEventListener('resize', resizeHandler);
  }
}

function createLottieAnimation(containerId, animationPath, autoplay = true) {
    const container = document.getElementById(containerId);
    if (container) {
        // A visitor who asked the system for less motion gets the animation standing on its last frame, where it
        // lands once played, instead of a loop; hovering still plays it, because that motion is one they started.
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const animation = lottie.loadAnimation({
            container: container, // the DOM element
            renderer: 'svg',
            loop: true,
            autoplay: autoplay && !prefersReducedMotion,
            path: animationPath, // the path to the animation JSON file
        });

        if (autoplay && prefersReducedMotion) {
          animation.addEventListener('DOMLoaded', () => {
            animation.goToAndStop(animation.totalFrames - 1, true);
          });
        }

        container.addEventListener('mouseenter', () => {
          animation.play();
        });

        container.addEventListener('mouseleave', () => {
          animation.stop();
        });
    }
}

// !!! this rule is too loose and as LottieComponent is used appwide it's affecting side menu
// to be refactored
function updateIframeWidth(containerId) {
  const iframeContainer = document.getElementById(containerId);

  if (iframeContainer) {
    const width = iframeContainer.offsetWidth;
    if (window.innerWidth <= 576) {
      iframeContainer.style.width = '250px';
    }
  } else {
    console.error(`Element with id "${containerId}" not found.`);
  }
}
